// shared.jsx
// Cross-screen primitives: global toast queue + small modal shell.
// Loaded BEFORE landing.jsx / builder.jsx in index.html.

const { useState: useStateS, useEffect: useEffectS, useRef: useRefS } = React;

// ── global toast bus ─────────────────────────────────────────────────────────
// Components call `window.toast("message", { tone: "default" | "warn" })`.
// Tone "default" reads as a friendly muted note; "warn" uses the accent color.
const __toastListeners = new Set();
window.toast = (message, opts = {}) => {
  const event = {
    id: Date.now() + Math.random(),
    message,
    tone: opts.tone || "default",
    action: opts.action || null, // { label, onClick }
    duration: opts.duration ?? 3200,
  };
  __toastListeners.forEach((fn) => fn(event));
};

// Cap the visible stack so a chatty page (e.g. a network blip
// fanning out across cards) doesn't push toasts off-screen. When
// the cap is hit, the oldest item is force-dismissed before the
// new one is appended.
const TOAST_MAX = 4;

function Toaster() {
  const [items, setItems] = useStateS([]);
  const dismiss = (id) => setItems((arr) => arr.filter((x) => x.id !== id));

  useEffectS(() => {
    const onToast = (evt) => {
      setItems((arr) => {
        const next = arr.length >= TOAST_MAX ? arr.slice(arr.length - TOAST_MAX + 1) : arr;
        // Clear the displaced toast's auto-dismiss timer if any.
        for (const dropped of arr.slice(0, arr.length - next.length)) {
          if (dropped.__t) clearTimeout(dropped.__t);
        }
        return [...next, evt];
      });
      const t = setTimeout(() => dismiss(evt.id), evt.duration);
      evt.__t = t;
    };
    __toastListeners.add(onToast);
    return () => __toastListeners.delete(onToast);
  }, []);

  const toneStyle = (tone) =>
    tone === "warn" ? toastStyles.warn :
    tone === "success" ? toastStyles.success :
    {};
  const dotStyle = (tone) =>
    tone === "warn" ? toastStyles.dot :
    tone === "success" ? toastStyles.dotSuccess :
    null;

  // P210 — aria-atomic="false" + aria-relevant="additions" so screen
  // readers announce ONLY new toasts as they appear. Pre-fix
  // aria-atomic="true" re-read the entire stack on every change
  // (added, removed, mutated), which got noisy fast — adding a
  // success toast on top of two old ones re-announced all three.
  // Each toast is also a status-region so warn-tone reads like a
  // notification rather than a dialog.
  return (
    <div
      style={toastStyles.stack}
      aria-live="polite"
      aria-atomic="false"
      aria-relevant="additions"
    >
      {items.map((t) => {
        const dot = dotStyle(t.tone);
        // P606 — warn-tone toasts get role="alert" instead of role="status"
        // so SR users are interrupted by critical notifications (failed
        // save, expired session, validation error) instead of having
        // them queue politely behind the current announcement. Pre-fix
        // every toast — success / info / warn — used role="status",
        // which is polite by definition. WAI-ARIA spec: alert implies
        // aria-live="assertive" + aria-atomic="true" implicitly. Success
        // and info toasts (the common case) keep role="status" so they
        // don't bulldoze into whatever the user is reading.
        const role = t.tone === "warn" ? "alert" : "status";
        return (
          <div
            key={t.id}
            style={{ ...toastStyles.item, ...toneStyle(t.tone) }}
            role={role}
          >
            {/* P424 — the tone dot is decorative (warn/success indicator);
                aria-hidden so SR doesn't synthesize "image" or read its
                color. The toast text below is the actual message. */}
            {dot && <span style={dot} aria-hidden="true" />}
            <span>{t.message}</span>
            {t.action && (
              <button
               type="button"
                style={toastStyles.action}
                onClick={() => {
                  if (t.__t) clearTimeout(t.__t);
                  try { t.action.onClick(); } finally { dismiss(t.id); }
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

const toastStyles = {
  stack: {
    position: "fixed",
    left: "50%",
    bottom: 28,
    transform: "translateX(-50%)",
    zIndex: 2147483645,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    pointerEvents: "none",
  },
  item: {
    background: "var(--ink)",
    color: "var(--paper)",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    padding: "10px 16px",
    borderRadius: 999,
    boxShadow: "0 16px 40px -12px rgba(0,0,0,0.35)",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    animation: "toast-in 240ms ease-out both",
    pointerEvents: "auto",
    // P578 — Pre-fix `whiteSpace: nowrap` + no max-width meant a
    // long error message (e.g. "Could not save: status 500 —
    // upstream returned malformed body") overflowed the viewport
    // horizontally on mobile (320px-wide screens). Now caps at
    // 90vw with normal whitespace; the pill still rounds gracefully
    // with a wider radius when content wraps. The action button
    // and dot remain on the same flex row; long text wraps below.
    whiteSpace: "normal",
    maxWidth: "min(90vw, 480px)",
    textAlign: "left",
    lineHeight: 1.4,
  },
  warn: {
    background: "var(--accent)",
    color: "var(--accent-fg)",
  },
  success: {
    background: "oklch(0.46 0.14 145)",
    color: "var(--paper)",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "var(--accent-fg)",
  },
  dotSuccess: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "color-mix(in oklab, oklch(0.78 0.16 145) 100%, transparent)",
  },
  action: {
    marginLeft: 6,
    padding: "4px 10px",
    background: "color-mix(in oklab, currentColor 14%, transparent)",
    color: "inherit",
    borderRadius: 999,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 500,
  },
};

// ── ModalShell ──────────────────────────────────────────────────────────────
// Lightweight overlay used by later steps (Publish, Share, etc.). Step 1
// only ships the primitive — no consumers yet.
// Selectors for "focusable in a modal context". `[data-focus-skip]` lets
// a caller opt a specific element out (e.g. an icon-only decorative button).
const _FOCUSABLE_SEL = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "textarea:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].map((s) => `${s}:not([data-focus-skip])`).join(",");

// Bump on every ModalShell mount so multiple stacked modals (rare,
// but plan-cap-prompt-on-create can race with project-settings) get
// distinct ids — aria-labelledby has to point at the right h2.
let _modalIdCounter = 0;
function ModalShell({ open, onClose, title, kicker, children, width = 520 }) {
  // P431 — Close-button aria-label localized via window.useT (called
  // unconditionally to satisfy Rules of Hooks; pre-fix the early
  // `if (!open) return null` was below the ref/effect calls already).
  const { t } = window.useT();
  const shellRef = useRefS(null);
  // P248 — pre-fix this was `useRefS(`modal-title-${++_modalIdCounter}`).current`.
  // The ref kept its first-mount value (so the id stayed stable across
  // re-renders, which is what aria-labelledby needs), but the
  // ++_modalIdCounter expression was re-evaluated on EVERY render of
  // every ModalShell — useRef takes its argument every render but
  // discards it after mount. The counter therefore climbed unboundedly
  // on a busy page (e.g. each setState in the publish-modal flow
  // bumped it), and a future debug console reading the counter would
  // see a misleadingly large number. useState with a lazy initializer
  // runs the function exactly once on mount, like the rest of the
  // codebase's lazy-init pattern.
  const [titleId] = useStateS(() => `modal-title-${++_modalIdCounter}`);

  useEffectS(() => {
    if (!open) return;
    const previousFocus = document.activeElement;

    const onKey = (e) => {
      if (e.key === "Escape") { onClose?.(); return; }
      if (e.key !== "Tab") return;
      // Trap focus inside the modal. Find every focusable descendant
      // and cycle: Shift+Tab from the first wraps to the last; Tab
      // from the last wraps to the first.
      const root = shellRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll(_FOCUSABLE_SEL);
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    // Initial focus: first focusable inside the modal, or the modal
    // itself as a fallback. Run after one frame so React has finished
    // mounting the children.
    const t = setTimeout(() => {
      const root = shellRef.current;
      if (!root) return;
      const first = root.querySelector(_FOCUSABLE_SEL);
      (first || root).focus?.();
    }, 0);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      clearTimeout(t);
      // Restore focus to whatever was focused before the modal opened
      // — the trigger button on dashboards / settings, almost always.
      if (previousFocus && typeof previousFocus.focus === "function") {
        try { previousFocus.focus(); } catch { /* tolerate detached node */ }
      }
    };
  }, [open, onClose]);

  if (!open) return null;
  // P529 — Pre-fix the scrim used `onClick={onClose}` to close the
  // modal when the user clicked outside it. But click events fire on
  // the deepest common ancestor of mousedown + mouseup targets, so
  // when a user starts a text-selection drag INSIDE an input and
  // overshoots the modal edge before releasing (very common when
  // selecting all text in a long input or textarea), the click event
  // bubbles to the scrim and the modal closes — losing their
  // in-progress edit. Now we track mousedown: only close if both the
  // mousedown AND the mouseup landed on the scrim itself, i.e. the
  // user actually intended to dismiss. Drags that start inside the
  // dialog never trigger close, regardless of where they end.
  const onScrimMouseDown = (e) => {
    e.currentTarget.dataset.dismissArmed = e.target === e.currentTarget ? "1" : "";
  };
  const onScrimClick = (e) => {
    if (e.currentTarget.dataset.dismissArmed === "1" && e.target === e.currentTarget) {
      onClose?.();
    }
    e.currentTarget.dataset.dismissArmed = "";
  };
  return (
    <div style={modalStyles.scrim} onMouseDown={onScrimMouseDown} onClick={onScrimClick}>
      <div
        ref={shellRef}
        style={{ ...modalStyles.shell, width }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header style={modalStyles.head}>
          <div>
            {kicker && <div style={modalStyles.kicker}>{kicker}</div>}
            <h2 style={modalStyles.title} id={titleId}>{title}</h2>
          </div>
          <button type="button" style={modalStyles.x} onClick={onClose} aria-label={t("modal.close")}>✕</button>
        </header>
        <div style={modalStyles.body}>{children}</div>
      </div>
    </div>
  );
}

const modalStyles = {
  scrim: {
    position: "fixed",
    inset: 0,
    background: "color-mix(in oklab, var(--ink) 35%, transparent)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    zIndex: 2147483640,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    animation: "modal-scrim 200ms ease-out both",
  },
  shell: {
    background: "var(--paper)",
    border: "1px solid var(--rule)",
    borderRadius: 16,
    boxShadow: "0 40px 100px -30px rgba(0,0,0,0.45)",
    maxWidth: "100%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    fontFamily: "var(--font-ui)",
    color: "var(--ink)",
    animation: "modal-pop 220ms cubic-bezier(.2,.9,.3,1) both",
    overflow: "hidden",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "20px 24px 14px",
    borderBottom: "1px solid var(--rule)",
    gap: 16,
  },
  kicker: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    letterSpacing: "0.1em",
    color: "var(--accent)",
    marginBottom: 6,
  },
  title: {
    fontFamily: "var(--font-display)",
    fontWeight: 400,
    fontSize: 28,
    lineHeight: 1.1,
    letterSpacing: "-0.01em",
    color: "var(--ink)",
  },
  x: {
    width: 28,
    height: 28,
    borderRadius: 8,
    color: "var(--ink-3)",
    fontSize: 13,
  },
  body: {
    padding: "20px 24px 24px",
    overflowY: "auto",
  },
};

if (!document.getElementById("__shared_keyframes")) {
  const style = document.createElement("style");
  style.id = "__shared_keyframes";
  style.textContent = `
    @keyframes toast-in { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }
    @keyframes modal-scrim { from { opacity: 0; } to { opacity: 1; } }
    @keyframes modal-pop { from { opacity: 0; transform: translateY(8px) scale(0.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
  `;
  document.head.appendChild(style);
}

// ── localStorage persistence layer ──────────────────────────────────────────
// Single helper used across stores. Wrapped in try/catch so private-mode and
// Safari quota errors don't crash the prototype. All keys prefixed mint:.
const _LS_KEY = (k) => `mint:${k}`;
function _lsLoad(key, fallback) {
  if (typeof localStorage === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(_LS_KEY(key));
    return raw == null ? fallback : JSON.parse(raw);
  } catch { return fallback; }
}
function _lsSave(key, value) {
  if (typeof localStorage === "undefined") return;
  try { localStorage.setItem(_LS_KEY(key), JSON.stringify(value)); }
  catch { /* quota / private mode — silently skip */ }
}

// ── auth state (global) ─────────────────────────────────────────────────────
// Persisted to localStorage so refresh keeps you signed in. Real Clerk auth
// will replace this layer once wired; for now it's the foundation.
let __authState = _lsLoad("auth", { isAuthed: false, email: null, name: null, provider: null, role: "user", plan: "free" });
const __authListeners = new Set();
const __notifyAuth = () => {
  _lsSave("auth", __authState);
  __authListeners.forEach((fn) => fn(__authState));
};

// P377 — non-React translation lookup helper for auth toasts. Same
// pattern as the timeAgo helper above: window.* functions can't call
// useT(), so we read TRANSLATIONS[__lang] directly and fall through
// to en + raw key on missing.
const _authT = (key, name) => {
  let template;
  try {
    template = TRANSLATIONS[__lang]?.[key] ?? TRANSLATIONS.en?.[key] ?? key;
  } catch { template = key; }
  return name == null ? template : template.replace("{name}", String(name));
};

window.signIn = (email, opts = {}) => {
  const name = opts.name
    || (email ? email.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : _authT("auth.fallbackName"));
  __authState = {
    isAuthed: true,
    email,
    name,
    provider: opts.provider || "email",
    role: opts.role || "user",
    plan: opts.plan || __authState.plan || "free",
  };
  __notifyAuth();
  if (!opts.silent) window.toast(_authT("auth.welcome", name));
};
window.refreshAuth = (j) => {
  if (!j?.authed) return;
  __authState = {
    ...__authState,
    isAuthed: true,
    email: j.email,
    name: j.name || __authState.name,
    role: j.role || __authState.role,
    plan: j.plan || __authState.plan || "free",
  };
  __notifyAuth();
  // turnBudget arrives via /api/auth/me (P111). Stash it in the
  // dedicated store so useTurnBudget consumers don't need to thread
  // it through useAuth — keeps the auth state shape stable.
  if (j.turnBudget) {
    __turnBudget = j.turnBudget;
    __turnBudgetListeners.forEach((fn) => fn(__turnBudget));
  }
};
// Operator authentication is an email-allowlist (see P149/P150):
// sign in via magic link with an allowlisted address (team@forge.app
// or sarvarbeksoporboyev8@gmail.com), and /api/auth/me reflects
// role="operator" automatically. The previous stub flipped
// client-side state to a hardcoded email with no server-side cookie,
// which got reset by the very next API call's 401. Open the standard
// auth modal so the user can complete a real magic-link flow with
// their operator email.
window.signInAsOperator = () => {
  if (__authState.isAuthed && __authState.role === "operator") {
    window.goTo?.("operator");
    return;
  }
  window.openAuth?.("sign-in");
  window.toast?.(_authT("auth.signInAsOperator"));
};
window.signOut = () => {
  // Clear stores BEFORE flipping auth state so the resulting auth
  // listener sees an empty in-memory cache to overwrite (no merge of
  // a previous user's data — see P91 for the cross-user leak this
  // closes).
  if (typeof window._resetUserStoresMint === "function") window._resetUserStoresMint();
  __authState = { isAuthed: false, email: null, name: null, provider: null, role: "user", plan: "free" };
  __notifyAuth();
  fetch("/api/auth/signout", { method: "POST" }).catch(() => { /* tolerate */ });
  window.toast(_authT("auth.signedOut"));
};

// _resetUserStoresMint: wipe in-memory + localStorage state that's
// tied to the current user. Called on signOut (above) and on
// cross-user sign-in (auth listener below). Without this, user A's
// projects merge into user B's account because _syncProjectsFromServer
// pushes "local-only with updatedAt" rows to the server (P91).
window._resetUserStoresMint = function () {
  __projects = [];
  __notifyProjects();
  __operatorQueue = [];
  __notifyOp();
  __notifications = [];
  __notifyNotifs();
  // Server-truth snapshots are also user-scoped. Without these resets
  // the dashboard pills render user A's quota/turn-budget for a frame
  // after user B signs in, before /api/projects + /api/auth/me reply.
  __projectQuota = null;
  __quotaListeners.forEach((fn) => fn(null));
  __turnBudget = null;
  __turnBudgetListeners.forEach((fn) => fn(null));
  if (typeof localStorage !== "undefined") {
    try {
      ["projects", "operatorQueue", "notifications"].forEach((k) => localStorage.removeItem(_LS_KEY(k)));
    } catch { /* tolerate disabled storage */ }
  }
};

// On boot, ask the server who we are (cookie may carry a session the
// browser knows about but localStorage doesn't — e.g. after the
// verify redirect). If the server says authed, mirror into local state.
// If the server says NOT authed but we think we are (expired session
// or admin revoke), do a clean local sign-out (P92) so the rest of
// the app stops trying to call protected endpoints with a dead cookie.
(async () => {
  try {
    const r = await fetch("/api/auth/me");
    if (!r.ok) return;
    const j = await r.json();
    if (j.authed && j.email) {
      const wasAuthed = __authState.isAuthed && __authState.email === j.email;
      window.refreshAuth(j);
      if (!wasAuthed) {
        const friendly = j.name || j.email.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        window.toast(_authT("auth.welcome", friendly));
      }
    } else if (__authState.isAuthed) {
      // Server-side cookie is gone (TTL'd in KV, or admin-revoked).
      // Local state is stale — clear it without re-hitting /signout
      // since there's no session to drop server-side anyway.
      __sessionExpired();
    }
  } catch { /* tolerate offline */ }
})();

// Centralize the "your session expired" UX so any caller that gets a
// 401 on an authed route can trigger it. Wipes the auth-tied stores
// (P91), flips __authState to logged-out, and toasts a friendly note
// pointing the user at sign-in. Idempotent: skips silently if we
// already know we're logged out.
function __sessionExpired() {
  if (!__authState.isAuthed) return;
  // P288 — Pre-fix this function had a 5-second coalesce window
  // (`__sessionExpiredFiredAt` + early return) intended to suppress
  // a burst of 401s from concurrent in-flight requests. But the
  // `if (!__authState.isAuthed) return` line above already does
  // exactly that: the FIRST 401 flips `__authState.isAuthed` to
  // false (line below), so the remaining 9 of 10 in-flight 401s
  // hit the auth-state guard and short-circuit. The 5-second
  // window only ever fired in the "user signed in again, then
  // got an immediate fresh 401" case — which is precisely when
  // we DO want to re-toast (the new session is also broken).
  // Removed the timestamp + coalesce; left the auth-state guard.
  if (typeof window._resetUserStoresMint === "function") window._resetUserStoresMint();
  __authState = { isAuthed: false, email: null, name: null, provider: null, role: "user", plan: "free" };
  __notifyAuth();
  window.toast?.(_authT("auth.sessionExpired"), {
    tone: "warn",
    action: { label: _authT("auth.sessionExpiredAction"), onClick: () => window.openAuth?.("sign-in") },
  });
}
window.__sessionExpiredMint = __sessionExpired;
window.useAuth = function useAuth() {
  const [state, setState] = useStateS(__authState);
  useEffectS(() => {
    __authListeners.add(setState);
    return () => __authListeners.delete(setState);
  }, []);
  return state;
};

// ── UserPill — avatar + dropdown for authed users ───────────────────────────
function UserPill({ size = "md", onDashboard, onAccount, onSignOut, align = "right" }) {
  const auth = window.useAuth();
  const { t } = window.useT();
  const [open, setOpen] = useStateS(false);
  const ref = useRefS(null);
  // P202 — focus-return on close (matches P201 + WAI-ARIA menu-button).
  // Without it, picking a menuitem dropped focus to <body>.
  const triggerRef = useRefS(null);
  // P563 — Continuation of P561: wire useMenuKeys for arrow-key nav
  // on the user-pill dropdown (the third of 4 menus).
  const { ref: menuKeyRef, onKeyDown: onMenuKeyDown } = window.useMenuKeys();
  const closeAndReturnFocus = () => { setOpen(false); triggerRef.current?.focus(); };
  useEffectS(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") closeAndReturnFocus(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    // P563 — Open-with-focus on first menuitem so the user can
    // immediately ArrowDown/Enter. Same shape as P561's
    // ProjectCard kebab menu.
    queueMicrotask(() => {
      const first = menuKeyRef.current?.querySelector('[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]');
      first?.focus();
    });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  if (!auth.isAuthed) return null;
  // P357 — string indexing `[0]` returns the first UTF-16 code unit,
  // which splits surrogate pairs. A user named "🦊 Fox" rendered a
  // half-surrogate as their avatar initial (the browser shows the
  // tofu/replacement glyph). Spread to a code-point array first so
  // the first user-perceived character lands intact regardless of
  // BMP / supplementary plane. Cyrillic / Latin / Uzbek letters are
  // unchanged because they're all single-unit BMP chars.
  const initial = ([...(auth.name || auth.email || "?").trim()][0] || "?").toUpperCase();
  const dim = size === "sm" ? 26 : 30;
  return (
    <div ref={ref} style={pillStyles.wrap}>
      <button
        type="button"
        ref={triggerRef}
        style={{ ...pillStyles.pill, height: dim + 6 }}
        onClick={() => setOpen((o) => !o)}
        aria-label={t("pill.menuAria")}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="user-menu"
      >
        <span style={{ position: "relative", display: "inline-flex" }}>
          <span
            style={{
              ...pillStyles.avatar,
              width: dim, height: dim,
              fontSize: size === "sm" ? 11 : 12,
            }}
          >{initial}</span>
          <span style={pillStyles.onlineDot} aria-hidden="true" />
        </span>
        {size !== "sm" && <span style={pillStyles.name}>{auth.name}</span>}
        <span style={pillStyles.caret} aria-hidden="true">▾</span>
      </button>
      {open && (
        <div
          ref={menuKeyRef}
          onKeyDown={onMenuKeyDown}
          style={{ ...pillStyles.menu, [align]: 0 }}
          id="user-menu"
          role="menu"
          aria-label={t("pill.menuAria")}
        >
          <div style={pillStyles.menuHead}>
            <div style={pillStyles.menuName}>
              {auth.name}
              {auth.plan === "pro" && <span style={pillStyles.planBadge}>PRO</span>}
            </div>
            <div style={pillStyles.menuEmail}>{auth.email}</div>
          </div>
          <button
            type="button"
            style={pillStyles.menuItem}
            role="menuitem"
            onClick={() => { setOpen(false); (onDashboard || (() => window.goTo?.("dashboard")))(); }}
          >{t("pill.dashboard")}</button>
          {auth.plan === "pro" ? (
            <button
              type="button"
              style={pillStyles.menuItem}
              role="menuitem"
              onClick={async () => {
                setOpen(false);
                try {
                  const r = await fetch("/api/billing/portal", { method: "POST", headers: window.langHeader() });
                  if (r.status === 401) { window.__sessionExpiredMint?.(); return; }
                  const j = await r.json().catch(() => ({}));
                  if (r.status === 503) {
                    // P239 — surface the server's actionable message so
                    // a paying Pro user gets a clear "contact us" CTA
                    // instead of "Billing isn't switched on yet" — the
                    // latter is misleading because billing IS on for
                    // them, only the self-serve portal is the stub.
                    // P378 — fallback now localized; server `j.error`
                    // is still preferred (it carries triage detail).
                    window.toast(j.error || t("pill.subscriptionStub"), { tone: "warn", duration: 6000 });
                    return;
                  }
                  if (!r.ok || !j.url) throw new Error(j.error || `status ${r.status}`);
                  window.location.href = j.url;
                } catch (err) {
                  window.toast(t("pill.portalError").replace("{message}", err.message), { tone: "warn" });
                }
              }}
            >{t("pill.manageSubscription")}</button>
          ) : (
            <button
              type="button"
              style={pillStyles.menuItem}
              role="menuitem"
              onClick={() => { setOpen(false); window.goTo?.("pricing"); }}
            >{t("pill.upgradeToPro")}</button>
          )}
          <button
            type="button"
            style={pillStyles.menuItem}
            role="menuitem"
            onClick={() => { setOpen(false); (onAccount || (() => window.goTo?.("account")))(); }}
          >{t("pill.accountSettings")}</button>
          <button
            type="button"
            style={{ ...pillStyles.menuItem, ...pillStyles.menuItemDanger }}
            role="menuitem"
            onClick={() => { setOpen(false); (onSignOut || window.signOut)(); }}
          >{t("pill.signOut")}</button>
        </div>
      )}
    </div>
  );
}

const pillStyles = {
  wrap: { position: "relative" },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "3px 10px 3px 3px",
    border: "1px solid var(--rule)",
    background: "var(--paper)",
    borderRadius: 999,
    color: "var(--ink)",
    fontFamily: "var(--font-ui)",
    fontSize: 13,
  },
  avatar: {
    borderRadius: 999,
    background: "var(--accent)",
    color: "var(--accent-fg)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontFamily: "var(--font-display)",
    fontStyle: "italic",
  },
  onlineDot: {
    position: "absolute",
    bottom: -1,
    right: -1,
    width: 9,
    height: 9,
    borderRadius: 999,
    background: "oklch(0.6 0.16 145)",
    border: "2px solid var(--paper)",
    boxShadow: "0 0 0 0 color-mix(in oklab, oklch(0.6 0.16 145) 50%, transparent)",
    animation: "pulse 2.4s ease-in-out infinite",
  },
  name: { color: "var(--ink-2)", whiteSpace: "nowrap", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" },
  caret: { color: "var(--ink-3)", fontSize: 9 },
  menu: {
    position: "absolute",
    top: "calc(100% + 8px)",
    minWidth: 220,
    background: "var(--paper)",
    border: "1px solid var(--rule)",
    borderRadius: 12,
    boxShadow: "0 16px 40px -12px rgba(0,0,0,0.25)",
    padding: 6,
    zIndex: 2147483644,
    animation: "menu-pop 160ms ease-out both",
  },
  menuHead: {
    padding: "10px 12px",
    borderBottom: "1px solid var(--rule)",
    marginBottom: 4,
  },
  menuName: { fontSize: 13, fontWeight: 500, color: "var(--ink)", display: "inline-flex", alignItems: "center", gap: 6 },
  menuEmail: { fontSize: 11, color: "var(--ink-3)", fontFamily: "var(--font-mono)", marginTop: 2 },
  planBadge: {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    letterSpacing: "0.1em",
    padding: "2px 6px",
    borderRadius: 4,
    background: "color-mix(in oklab, var(--accent) 16%, var(--paper-2))",
    color: "var(--accent)",
    border: "1px solid color-mix(in oklab, var(--accent) 38%, var(--rule))",
    fontWeight: 600,
  },
  menuItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "8px 12px",
    fontSize: 13,
    color: "var(--ink)",
    background: "transparent",
    borderRadius: 8,
  },
  menuItemDanger: { color: "oklch(0.55 0.18 25)" },
};

if (!document.getElementById("__pill_keyframes")) {
  const style = document.createElement("style");
  style.id = "__pill_keyframes";
  style.textContent = `
    @keyframes menu-pop { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
  `;
  document.head.appendChild(style);
}

// ── projects store (global, in-memory) ──────────────────────────────────────
// _SEED_PROJECT_IDS marks the demo entries below. They show up for unauth
// landing/dashboard visitors so the demo isn't empty, but the moment a
// real session syncs, _syncProjectsFromServer drops them so they don't
// get pushed to the user's D1 row or shown alongside their real work.
const _SEED_PROJECT_IDS = new Set([
  "camara-type",
  "northwell-sourdough",
  "ratelimit-dev",
  "atlas-scheduler",
  "mira-and-theo",
  "studio-halfmoon",
]);
// P645 — liveUrl values stored as full URLs (with `https://` scheme)
// to match the server-side `checkLiveUrl` contract (P639). Pre-fix
// these were scheme-less ("camara-type.forge.app"); P643/P644 had to
// add detection logic in dashboard ProjectCard + ShareModal +
// operator's "Mark live" seed because of the format mismatch with
// API-fetched projects (which always have the scheme post-checkLiveUrl).
// Normalizing the seed makes the format consistent across local and
// API paths; the detection logic in P643/P644 stays as defense in
// depth for any persisted-old-format records still in localStorage.
const __seedProjects = [
  {
    id: "camara-type",
    name: "Câmara Type",
    workspace: "personal",
    prompt: "a portfolio for a typeface designer in Lisbon, warm cream paper, italic display",
    swatch: "oklch(0.92 0.04 60)",
    ink: "oklch(0.25 0.05 30)",
    tag: "PORTFOLIO",
    status: "live",
    liveUrl: "https://camara-type.forge.app",
    version: "v0.4",
    edits: 12,
    updatedAt: Date.now() - 1000 * 60 * 60 * 2, // 2 hours ago
  },
  {
    id: "northwell-sourdough",
    name: "Northwell Sourdough",
    workspace: "personal",
    prompt: "a one-pager for a sourdough subscription, warm and rustic",
    swatch: "oklch(0.88 0.06 80)",
    ink: "oklch(0.3 0.08 50)",
    tag: "STOREFRONT",
    status: "live",
    liveUrl: "https://northwell.forge.app",
    version: "v0.7",
    edits: 24,
    updatedAt: Date.now() - 1000 * 60 * 60 * 24, // 1 day ago
  },
  {
    id: "ratelimit-dev",
    name: "Ratelimit.dev",
    workspace: "personal",
    prompt: "a docs site for my open-source rate limiter",
    swatch: "oklch(0.18 0.01 250)",
    ink: "oklch(0.92 0.03 90)",
    tag: "DOCS",
    status: "draft",
    liveUrl: null,
    version: "v0.1",
    edits: 3,
    updatedAt: Date.now() - 1000 * 60 * 30, // 30m ago
  },
  {
    id: "atlas-scheduler",
    name: "Atlas Scheduler",
    workspace: "personal",
    prompt: "a launch page for an AI scheduling assistant, dark mode",
    swatch: "oklch(0.96 0.02 240)",
    ink: "oklch(0.3 0.05 250)",
    tag: "LANDING",
    status: "draft",
    liveUrl: null,
    version: "v0.3",
    edits: 8,
    updatedAt: Date.now() - 1000 * 60 * 60 * 24 * 3, // 3 days ago
  },
  {
    id: "mira-and-theo",
    name: "Mira & Theo",
    workspace: "personal",
    prompt: "a wedding invite site, photo-led, with RSVP form",
    swatch: "oklch(0.94 0.03 30)",
    ink: "oklch(0.35 0.08 20)",
    tag: "INVITE",
    status: "live",
    liveUrl: "https://miraandtheo.com",
    version: "v0.6",
    edits: 19,
    updatedAt: Date.now() - 1000 * 60 * 60 * 24 * 7, // a week ago
  },
  {
    id: "studio-halfmoon",
    name: "Studio Halfmoon",
    workspace: "personal",
    prompt: "an architecture studio portfolio, brutalist, dense",
    swatch: "oklch(0.22 0.02 140)",
    ink: "oklch(0.94 0.05 90)",
    tag: "PORTFOLIO",
    status: "live",
    liveUrl: "https://halfmoon.studio",
    version: "v1.2",
    edits: 47,
    updatedAt: Date.now() - 1000 * 60 * 60 * 24 * 14, // 2 weeks ago
  },
];
// Projects: load persisted list if present, else seed. New seed projects get
// folded in on first load (so the demo content shows up for a fresh visitor)
// but persisted user-created projects survive refreshes.
let __projects = (() => {
  const persisted = _lsLoad("projects", null);
  if (!persisted || !Array.isArray(persisted) || persisted.length === 0) return __seedProjects;
  // Merge: keep persisted as-is, add any new seed entries the user has never seen.
  const persistedIds = new Set(persisted.map((p) => p.id));
  const missingSeeds = __seedProjects.filter((p) => !persistedIds.has(p.id));
  return [...persisted, ...missingSeeds];
})();
const __projectListeners = new Set();
const __notifyProjects = () => {
  _lsSave("projects", __projects);
  __projectListeners.forEach((fn) => fn(__projects));
};

// P291 — exposed on window so publish.jsx can drop its near-identical
// duplicate. NFD-normalize + strip combining diacritical marks
// (U+0300–U+036F), collapse runs of non-alnum to single hyphens,
// trim hyphens at edges, cap at 48 chars (room for the .forge.app
// suffix to stay under common URL display widths).
//
// P343 — Cyrillic transliteration. Pre-fix the [^a-z0-9]+ scrub
// dropped every non-Latin char, so a Russian-speaking user naming
// projects "Мой сайт" / "Магазин" / "Блог" got slugs "untitled" /
// "untitled-2" / "untitled-3" — totally non-descriptive in the
// dashboard URL bar AND in the operator queue's project_id column.
// Same problem for Uzbek Cyrillic. Now: transliterate first via a
// BGN/PCGN-approximation map (with Uzbek-specific letters Қ→q,
// Ғ→gh, Ҳ→h, Ў→o'), THEN run the existing NFD + scrub pipeline.
// Latin input is unchanged because the map only fires on chars in
// the Cyrillic block. Falls back to "untitled" only when even
// transliteration produces nothing slug-worthy.
const __TRANSLIT = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e",
  ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
  н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u",
  ф: "f", х: "kh", ц: "ts", ч: "ch", ш: "sh", щ: "shch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
  // Uzbek-Cyrillic-only letters (kept lowercase only; uppercase
  // pre-mapped via toLowerCase() at slugify entry).
  қ: "q", ғ: "gh", ҳ: "h", ў: "o", ң: "ng",
};
function __translit(s) {
  let out = "";
  for (const ch of s) out += __TRANSLIT[ch] ?? ch;
  return out;
}
const __slugify = (s) => __translit((s || "").toLowerCase())
  .normalize("NFD").replace(/[̀-ͯ]/g, "")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "")
  .slice(0, 48) || "untitled";
window.slugify = __slugify;

const __pastelSwatches = [
  { swatch: "oklch(0.92 0.04 60)", ink: "oklch(0.25 0.05 30)" },
  { swatch: "oklch(0.88 0.06 80)", ink: "oklch(0.3 0.08 50)" },
  { swatch: "oklch(0.96 0.02 240)", ink: "oklch(0.3 0.05 250)" },
  { swatch: "oklch(0.94 0.03 30)", ink: "oklch(0.35 0.08 20)" },
  { swatch: "oklch(0.9 0.05 200)", ink: "oklch(0.28 0.06 220)" },
  { swatch: "oklch(0.91 0.05 130)", ink: "oklch(0.3 0.06 145)" },
];

window.getProjects = () => __projects;
window.getProject = (id) => __projects.find((p) => p.id === id);
window.createProject = async ({ name, prompt }) => {
  // P346 — direct TRANSLATIONS lookup since createProject is a plain
  // function (not a React component), so useT() can't be called here.
  // The hoist concern is moot — TRANSLATIONS and __lang are both
  // initialized by the time any user action fires this. Fallback to
  // "Untitled" if the lookup table is somehow unavailable.
  const fallbackName = (typeof TRANSLATIONS !== "undefined"
    ? (TRANSLATIONS[__lang]?.["project.untitled"] ?? TRANSLATIONS.en?.["project.untitled"])
    : null) || "Untitled";
  const finalName = name?.trim() || (prompt ? prompt.slice(0, 40) : fallbackName);
  const baseSlug = __slugify(finalName);
  let slug = baseSlug, n = 2;
  while (__projects.some((p) => p.id === slug)) slug = `${baseSlug}-${n++}`;
  const swatch = __pastelSwatches[__projects.length % __pastelSwatches.length];
  const now = Date.now();
  const proj = {
    id: slug,
    name: finalName,
    workspace: "personal",
    prompt: prompt || "",
    ...swatch,
    tag: "NEW",
    status: "draft",
    liveUrl: null,
    version: "v0.1",
    edits: 0,
    createdAt: now,
    updatedAt: now,
  };
  __projects = [proj, ...__projects];
  __notifyProjects();
  const res = await window.api?.upsertProject(proj);
  // P193 — also roll back on 401. A dead-session create attempt was
  // previously left in the optimistic local state, then the dashboard's
  // onOpenProject(proj) would route the user into builder for a project
  // the server doesn't have.
  // P229 — same rollback on 409 (new in P228). The slug picker dedupes
  // against `__projects` (active only), but the server's row may exist
  // as a soft-deleted match — concrete trigger: device A creates +
  // trashes "Portfolio" → client B (which has never seen it) creates
  // a new "Portfolio", the slug `portfolio` is fresh locally but
  // matches the server's trashed row, so the POST 409s. Pre-fix the
  // optimistic project hung around in __projects, but the server has
  // no live row for it; the dashboard would route the user into the
  // builder for a phantom project, and a reload would silently drop it.
  if (res && res.ok === false && (res.status === 402 || res.status === 401 || res.status === 409)) {
    __projects = __projects.filter((p) => p.id !== slug);
    __notifyProjects();
    if (res.status === 401) {
      // upsertProject already fired __sessionExpiredMint; bubble a
      // simple error so the caller doesn't proceed with the phantom
      // project.
      const err = new Error("session expired");
      err.code = 401;
      throw err;
    }
    if (res.status === 409) {
      // Trashed-row collision. Surface the server's message so the
      // caller can render the "Restore from Trash first" toast.
      const err = new Error(res.error || "A project with this name is in Trash");
      err.code = 409;
      throw err;
    }
    // 402 — surface the upgrade prompt to the caller via a thrown error.
    const err = new Error(res.error || "Project quota reached");
    err.code = 402;
    err.upgradeTo = res.upgradeTo;
    err.used = res.used;
    err.cap = res.cap;
    throw err;
  }
  return proj;
};
window.deleteProject = (id) => {
  __projects = __projects.filter((p) => p.id !== id);
  __notifyProjects();
  window.api?.deleteProject(id);
};
window.touchProjectOpened = (id) => {
  const ts = Date.now();
  __projects = __projects.map((p) => p.id === id ? { ...p, lastOpenedAt: ts } : p);
  __notifyProjects();
  window.api?.patchProject(id, { lastOpenedAt: ts });
};
window.updateProject = (id, patch) => {
  const next = { ...patch, updatedAt: Date.now() };
  __projects = __projects.map((p) => p.id === id ? { ...p, ...next } : p);
  __notifyProjects();
  window.api?.patchProject(id, patch);
};
// P164 — return the api result so callers (dashboard Undo, Trash
// drawer) can check before showing a success toast. The api helper
// (P151) toasts the 402 quota message itself; without surfacing the
// result here, callers would silently fire a "Restored X" toast on
// top, contradicting the helper.
window.restoreProject = async (project) => {
  if (__projects.some((p) => p.id === project.id)) return { ok: true, alreadyLocal: true };
  __projects = [project, ...__projects];
  __notifyProjects();
  const res = await window.api?.restoreProject(project.id);
  if (res && res.ok === false) {
    // Server rejected — roll back the optimistic add. The api helper
    // already toasted the reason and re-synced.
    __projects = __projects.filter((p) => p.id !== project.id);
    __notifyProjects();
  }
  return res || { ok: true };
};
window.useProjects = function useProjects() {
  const [list, setList] = useStateS(__projects);
  useEffectS(() => {
    __projectListeners.add(setList);
    return () => __projectListeners.delete(setList);
  }, []);
  return list;
};

// ── operator queue (mock backend-wiring inbox) ──────────────────────────────
const HOUR = 60 * 60 * 1000;
// Same hygiene pattern as _SEED_PROJECT_IDS (P77): drop these from the
// merged view once a real operator session syncs so the dashboard isn't
// half demo / half real.
const _SEED_QUEUE_IDS = new Set(["wq1", "wq2", "wq3", "wq4", "wq5", "wq6", "wq7"]);
const __seedOperatorQueue = [
  {
    id: "wq1",
    projectId: "northwell-sourdough",
    projectName: "Northwell Sourdough",
    clientEmail: "tom@northwell.co",
    clientName: "Tom B.",
    addons: ["form", "newsletter", "ordering"],
    requestedAt: Date.now() - 2 * HOUR,
    status: "pending",
    plan: "Pro",
    monthly: 53,
    notes: "Payme merchant + Click; wants order pings to Telegram chat.",
  },
  {
    id: "wq2",
    projectId: "mira-and-theo",
    projectName: "Mira & Theo",
    clientEmail: "mira@miraandtheo.com",
    clientName: "Mira K.",
    addons: ["form"],
    requestedAt: Date.now() - 5 * HOUR,
    status: "in_progress",
    plan: "Pro",
    monthly: 5,
    notes: "RSVP form → Google Sheet they own. Confirmation email in EN + RU.",
  },
  {
    id: "wq3",
    projectId: "ratelimit-dev",
    projectName: "Ratelimit.dev",
    clientEmail: "alex@ratelimit.dev",
    clientName: "Alex P.",
    addons: ["accounts"],
    requestedAt: Date.now() - 22 * HOUR,
    status: "pending",
    plan: "Studio",
    monthly: 20,
    notes: "GitHub OAuth, dashboard with personal usage charts.",
  },
  {
    id: "wq4",
    projectId: "atlas-scheduler",
    projectName: "Atlas Scheduler",
    clientEmail: "nadia@atlas.work",
    clientName: "Nadia R.",
    addons: ["payments", "accounts"],
    requestedAt: Date.now() - 28 * HOUR,
    status: "in_progress",
    plan: "Studio",
    monthly: 35,
    notes: "Click usage-based billing. Magic link auth, no passwords.",
  },
  {
    id: "wq5",
    projectId: "studio-halfmoon",
    projectName: "Studio Halfmoon",
    clientEmail: "kit@halfmoon.studio",
    clientName: "Kit M.",
    addons: ["form"],
    requestedAt: Date.now() - 4 * 24 * HOUR,
    status: "live",
    plan: "Pro",
    monthly: 5,
    notes: "Live since Mon. Form -> kit@halfmoon.studio.",
  },
  {
    id: "wq6",
    projectId: "camara-type",
    projectName: "Câmara Type",
    clientEmail: "ana@camara.type",
    clientName: "Ana M.",
    addons: ["form", "newsletter"],
    requestedAt: Date.now() - 9 * 24 * HOUR,
    status: "live",
    plan: "Pro",
    monthly: 8,
    notes: "Form + Buttondown newsletter. PT + EN copy.",
  },
];
let __operatorQueue = _lsLoad("operatorQueue", null) || __seedOperatorQueue;
const __opListeners = new Set();
const __notifyOp = () => {
  _lsSave("operatorQueue", __operatorQueue);
  __opListeners.forEach((fn) => fn(__operatorQueue));
};

window.useOperatorQueue = function useOperatorQueue() {
  const [list, setList] = useStateS(__operatorQueue);
  useEffectS(() => {
    __opListeners.add(setList);
    return () => __opListeners.delete(setList);
  }, []);
  return list;
};
window.updateQueueItem = (id, patch) => {
  __operatorQueue = __operatorQueue.map((x) => (x.id === id ? { ...x, ...patch } : x));
  __notifyOp();
  window.api?.patchQueueItem(id, patch);
};

// ── notifications inbox ─────────────────────────────────────────────────────
const _M = 60 * 1000;
const _H = 60 * _M;
const _D = 24 * _H;
const __seedNotifs = [
  { id: "n1", kind: "live", title: "Your Câmara Type site went live", body: "camara-type.forge.app · v0.4", at: Date.now() - 2 * _M, read: false, projectId: "camara-type" },
  { id: "n2", kind: "wired", title: "Backend wired for Northwell Sourdough", body: "Form, newsletter, ordering — all live. Test before publish.", at: Date.now() - 1 * _H, read: false, projectId: "northwell-sourdough" },
  { id: "n3", kind: "comment", title: "Mira K. left feedback", body: "“Can the RSVP form fit in one screen on mobile?”", at: Date.now() - 3 * _H, read: false, projectId: "mira-and-theo" },
  { id: "n4", kind: "limit", title: "Plan limit", body: "You're using 2 of 3 published sites on Pro.", at: Date.now() - 1 * _D, read: true },
  { id: "n5", kind: "template", title: "New template: Restaurant menu", body: "Photo-led menu + reservations.", at: Date.now() - 2 * _D, read: true },
  { id: "n6", kind: "payout", title: "Payme payout — 1,840,000 sum", body: "From Northwell · last 30 days.", at: Date.now() - 4 * _D, read: true },
];
let __notifications = _lsLoad("notifications", null) || __seedNotifs;
const __notifListeners = new Set();
const __notifyNotifs = () => {
  _lsSave("notifications", __notifications);
  __notifListeners.forEach((fn) => fn(__notifications));
};

window.useNotifications = function useNotifications() {
  const [list, setList] = useStateS(__notifications);
  useEffectS(() => {
    __notifListeners.add(setList);
    return () => __notifListeners.delete(setList);
  }, []);
  return list;
};
window.markNotificationRead = (id) => {
  __notifications = __notifications.map((n) => (n.id === id ? { ...n, read: true } : n));
  __notifyNotifs();
  window.api?.markNotificationRead(id);
};
window.markAllNotificationsRead = () => {
  __notifications = __notifications.map((n) => ({ ...n, read: true }));
  __notifyNotifs();
  window.api?.markAllNotificationsRead();
};

// Pull server-side notifications and replace the local list with them.
// We deliberately DROP the demo seeds here — once the server has weighed
// in (even with an empty list), an authed user shouldn't see fabricated
// "Payme payout" rows they never received. The seeds stay in place for
// unauth visitors as harmless landing-demo polish.
async function _syncNotificationsFromServer() {
  const items = await window.api?.listNotifications();
  if (!items) return;
  __notifications = [...items];
  if (typeof items.__unread === "number") __notifications.__unread = items.__unread;
  __notifyNotifs();
}

const _NOTIF_ICON = {
  live: "live",
  wired: "check",
  comment: "envelope",
  limit: "alert",
  template: "grid",
  payout: "dollar",
};
const _NOTIF_TONE = {
  live: "var(--accent)",
  wired: "oklch(0.55 0.16 145)",
  comment: "oklch(0.55 0.14 240)",
  limit: "oklch(0.55 0.18 25)",
  template: "var(--ink-2)",
  payout: "oklch(0.55 0.16 145)",
};

// P342 — localized title/body for queue-event notifications. Returns
// null for unknown kinds so the caller falls back to the server's
// stored copy. Project-name interpolation reads from the local
// projects list (window.getProject); if the project hasn't synced
// yet we fall back to a generic placeholder per locale rather than
// rendering "{project}" literally.
function _resolveProjectName(n, t) {
  const proj = n.projectId ? window.getProject?.(n.projectId) : null;
  if (proj?.name) return proj.name;
  // Fallback: extract from server-rendered title if it embeds the
  // name (templates: "X is live", "X hit a snag", "Your team's
  // wiring up X"). Works because the server templates are stable;
  // if they change, the regex misses and we fall back to a generic
  // placeholder.
  const m = (n.title || "").match(/wiring up (.+)$|^(.+?) is live$|^(.+?) hit a snag$/);
  if (m) return m[1] || m[2] || m[3];
  return t("notif.fallbackProject") || "your project";
}
function _localizedNotifTitle(t, n) {
  const project = _resolveProjectName(n, t);
  if (n.kind === "wired") return t("notif.wired.title").replace("{project}", project);
  if (n.kind === "live") return t("notif.live.title").replace("{project}", project);
  if (n.kind === "limit") return t("notif.failed.title").replace("{project}", project);
  return null;
}
function _localizedNotifBody(t, n) {
  if (n.kind === "wired") return t("notif.wired.body");
  if (n.kind === "live") {
    const proj = n.projectId ? window.getProject?.(n.projectId) : null;
    const liveUrl = proj?.liveUrl
      // Server might also have shipped the URL inside the original body
      // ("Live at https://…"); pull it out as a backup if the project
      // lookup didn't yield one (e.g. the bell rendered before sync).
      || ((n.body || "").match(/(https?:\/\/\S+)/) || [])[1]
      || null;
    if (liveUrl) return t("notif.live.bodyAt").replace("{url}", liveUrl);
    return t("notif.live.bodyGeneric");
  }
  if (n.kind === "limit") return t("notif.failed.body");
  return null;
}

function NotificationsBell({ onOpenProject }) {
  const list = window.useNotifications();
  window.useTick();
  // P341 — chrome strings now go through useT(). Per-notification
  // n.title / n.body still come from the server in English; that's a
  // schema-touching follow-up.
  const { t } = window.useT();
  const [open, setOpen] = useStateS(false);
  const ref = useRefS(null);
  // P202 — focus-return on Escape (matches SortMenu P201 + WAI-ARIA
  // disclosure pattern). Without it, hitting Escape dropped focus to
  // <body>; user had to Tab back to find the bell.
  const triggerRef = useRefS(null);
  // Prefer the server's true unread count (P74); fall back to the
  // local-page count when offline / pre-sync.
  const unread = (typeof list.__unread === "number")
    ? list.__unread
    : list.filter((n) => !n.read).length;

  useEffectS(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    // P626 — Move focus into the panel on open. Pre-fix the panel
    // declared `aria-haspopup="dialog"` + `role="dialog"` but focus
    // stayed on the trigger after activation. WAI-ARIA dialog spec
    // says focus moves into the dialog on open; without it AT users
    // heard "expanded" on the trigger but their focus context didn't
    // move, so they had to Tab into the panel content. Same pattern
    // as the SortMenu (P562), workspace menu (P603), and ProjectCard
    // kebab (P561). We focus the first interactive element — usually
    // the "Mark all read" button when unread > 0 or the first
    // notification button when items exist; the panel root as a
    // last-resort if neither is present.
    queueMicrotask(() => {
      const root = ref.current?.querySelector('#notif-panel');
      if (!root) return;
      const focusable = root.querySelector('button, [href], [tabindex]:not([tabindex="-1"])');
      (focusable || root)?.focus?.();
    });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onItem = (n) => {
    if (!n.read) window.markNotificationRead(n.id);
    if (n.projectId && onOpenProject) {
      const proj = window.getProject?.(n.projectId);
      if (proj) {
        setOpen(false);
        onOpenProject(proj);
      }
    }
  };

  return (
    <div ref={ref} style={notifStyles.wrap}>
      <button
        type="button"
        ref={triggerRef}
        style={notifStyles.bellBtn}
        onClick={() => setOpen((o) => !o)}
        aria-label={`${t("notif.bell")} (${t("notif.unreadAriaSuffix").replace("{n}", unread)})`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="notif-panel"
        data-tooltip={unread > 0 ? t("notif.unreadTooltip").replace("{n}", unread) : t("notif.bell")}
      >
        <BellGlyph />
        {unread > 0 && (
          <span style={notifStyles.badge} aria-hidden="true">{unread > 9 ? "9+" : unread}</span>
        )}
      </button>

      {open && (
        <div
          style={notifStyles.panel}
          id="notif-panel"
          role="dialog"
          aria-label={t("notif.bell")}
          tabIndex={-1}
        >
          <div style={notifStyles.panelHead}>
            <div style={notifStyles.panelTitle}>{t("notif.bell")}</div>
            {unread > 0 && (
              <button type="button" style={notifStyles.panelMark} onClick={() => window.markAllNotificationsRead()}>
                {t("notif.markAllRead")}
              </button>
            )}
          </div>
          {list.length === 0 ? (
            <div style={notifStyles.panelEmpty}>{t("notif.empty")}</div>
          ) : (
            <ul style={notifStyles.list}>
              {list.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    style={{
                      ...notifStyles.item,
                      ...(n.read ? {} : notifStyles.itemUnread),
                    }}
                    onClick={() => onItem(n)}
                  >
                    <span
                      style={{
                        ...notifStyles.itemIcon,
                        background: `color-mix(in oklab, ${_NOTIF_TONE[n.kind]} 15%, var(--paper-2))`,
                        color: _NOTIF_TONE[n.kind],
                      }}
                    ><Icon name={_NOTIF_ICON[n.kind] || "live"} size={14} /></span>
                    <div style={notifStyles.itemMain}>
                      <div style={notifStyles.itemTitle}>
                        {_localizedNotifTitle(t, n) || n.title}
                        {!n.read && <span style={notifStyles.unreadDot} role="img" aria-label={t("notif.unreadAria")} />}
                      </div>
                      <div style={notifStyles.itemBody}>{_localizedNotifBody(t, n) || n.body}</div>
                      <div style={notifStyles.itemTime}>{window.timeAgo(n.at)}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div style={notifStyles.panelFoot}>
            <button
              type="button"
              style={notifStyles.panelFootBtn}
              onClick={() => { setOpen(false); window.toast(t("notif.viewAllToast")); }}
            >
              {t("notif.viewAll")} <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BellGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 12.5 C3 12.5 4 11 4 9.5 V7 a4 4 0 0 1 8 0 v2.5 c0 1.5 1 3 1 3 H3 z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M6.5 14.2 a1.6 1.6 0 0 0 3 0"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

const notifStyles = {
  wrap: { position: "relative" },
  bellBtn: {
    position: "relative",
    width: 36,
    height: 36,
    borderRadius: 999,
    background: "transparent",
    color: "var(--ink-2)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  badge: {
    position: "absolute",
    top: 4,
    right: 4,
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    background: "var(--accent)",
    color: "var(--accent-fg)",
    borderRadius: 999,
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid var(--paper)",
  },
  panel: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    width: 360,
    maxHeight: 480,
    background: "var(--paper)",
    border: "1px solid var(--rule)",
    borderRadius: 14,
    boxShadow: "0 24px 60px -16px rgba(0,0,0,0.28)",
    zIndex: 2147483640,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "menu-pop 180ms ease-out both",
  },
  panelHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 14px",
    borderBottom: "1px solid var(--rule)",
  },
  panelTitle: { fontSize: 13, fontWeight: 500, color: "var(--ink)" },
  panelMark: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--accent)",
    background: "transparent",
    padding: "4px 8px",
    borderRadius: 6,
  },
  panelEmpty: {
    padding: "32px 16px",
    textAlign: "center",
    color: "var(--ink-3)",
    fontSize: 13,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    overflowY: "auto",
    maxHeight: 380,
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    width: "100%",
    padding: "12px 14px",
    background: "transparent",
    color: "var(--ink)",
    textAlign: "left",
    borderBottom: "1px solid var(--rule)",
    fontFamily: "var(--font-ui)",
    cursor: "pointer",
  },
  itemUnread: {
    background: "color-mix(in oklab, var(--accent) 6%, var(--paper))",
  },
  itemIcon: {
    width: 28,
    height: 28,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 600,
    flexShrink: 0,
    marginTop: 1,
  },
  itemMain: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 },
  itemTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: "var(--ink)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  unreadDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "var(--accent)",
  },
  itemBody: {
    fontSize: 12,
    color: "var(--ink-2)",
    lineHeight: 1.45,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  itemTime: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--ink-3)",
  },
  panelFoot: {
    padding: "8px 12px",
    borderTop: "1px solid var(--rule)",
    background: "var(--paper-2)",
  },
  panelFootBtn: {
    display: "block",
    width: "100%",
    padding: "6px 10px",
    background: "transparent",
    color: "var(--ink-2)",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    borderRadius: 6,
    textAlign: "center",
  },
};

// Single global tick subscriber so every consumer of timeAgo refreshes
// at the same instant. Keeps the prototype from running N independent
// 30s intervals on big lists.
//
// P255 — Skip the broadcast when the document is hidden. Modern
// browsers throttle setInterval in background tabs to ~1/min, but
// the timer still fires (just less often) and every fire fans out a
// re-render across every <ProjectCard>, <QueueRow>, and any other
// component using useTick — pure waste while the user can't see the
// surface. Re-tick once on visibilitychange so timestamps catch up
// when the user returns.
let __tickCount = 0;
const __tickListeners = new Set();
const __broadcastTick = () => {
  if (typeof document !== "undefined" && document.hidden) return;
  __tickCount += 1;
  __tickListeners.forEach((fn) => fn(__tickCount));
};
setInterval(__broadcastTick, 30000);
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) __broadcastTick();
  });
}
window.useTick = function useTick() {
  const [, setN] = useStateS(__tickCount);
  useEffectS(() => {
    __tickListeners.add(setN);
    return () => __tickListeners.delete(setN);
  }, []);
};

// P334 — shared keyboard helper for `role="radiogroup"` strips. P330
// (MTabs), P331 (Dashboard Filter), P332 (LangSwitcher) each copy-
// pasted the same arrow-key + Home/End pattern; remaining radiogroup
// sites (Operator filter, Templates filter, AI mode picker) need it
// too. Helper returns a ref + onKeyDown for the group container; each
// child <button role="radio"> sets `tabIndex={selected ? 0 : -1}`
// independently. selectedId is the current value; ids is the ordered
// list of option ids; onSelect(id) fires when arrow keys move
// selection. Focus follows selection so screen-reader users hear the
// new option immediately.
//
// Usage:
//   const ids = items.map((it) => it.id);
//   const { ref, onKeyDown } = window.useRadiogroupKeys(ids, value, onChange);
//   <div role="radiogroup" ref={ref} onKeyDown={onKeyDown}> ... </div>
window.useRadiogroupKeys = function useRadiogroupKeys(ids, selectedId, onSelect) {
  const ref = useRefS(null);
  const onKeyDown = (e) => {
    const idx = ids.indexOf(selectedId);
    if (idx < 0) return;
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (idx + 1) % ids.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (idx - 1 + ids.length) % ids.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = ids.length - 1;
    else return;
    e.preventDefault();
    onSelect(ids[next]);
    queueMicrotask(() => {
      const btns = ref.current?.querySelectorAll('[role="radio"]');
      btns?.[next]?.focus();
    });
  };
  return { ref, onKeyDown };
};

// P561 — WAI-ARIA menu pattern: ArrowUp/Down moves focus between
// menuitems; Home/End jump to first/last; Enter/Space activate (the
// browser handles those natively for <button> children); Escape
// closes (parent handles that). Pre-fix the dashboard ProjectCard
// kebab menu, dashboard SortMenu, dashboard workspace menu, and
// shared.jsx UserPill menu had `role="menu"` + role="menuitem"
// children but NO arrow-key handling — keyboard users had to Tab
// through every menuitem (which works because they're <button>s)
// but the role="menu" semantics implied arrow nav to AT, which
// then didn't fire. Helper attaches via ref like useRadiogroupKeys;
// menus don't have a "selected" state so focus is the source of
// truth for "current item" — we read the focused element and step
// from there.
//
// Usage:
//   const { ref, onKeyDown } = window.useMenuKeys();
//   <div role="menu" ref={ref} onKeyDown={onKeyDown}>
//     <button role="menuitem">…</button> ... </div>
window.useMenuKeys = function useMenuKeys() {
  const ref = useRefS(null);
  const onKeyDown = (e) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const root = ref.current;
    if (!root) return;
    // Selector matches the WAI-ARIA menuitem family. Excludes hidden
    // / disabled items so focus skips past them.
    const items = Array.from(root.querySelectorAll('[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]'))
      .filter((el) => !el.disabled && el.offsetParent !== null);
    if (items.length === 0) return;
    const active = document.activeElement;
    const idx = items.indexOf(active);
    let next = idx;
    if (e.key === "ArrowDown") next = idx < 0 ? 0 : (idx + 1) % items.length;
    else if (e.key === "ArrowUp") next = idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    e.preventDefault();
    items[next]?.focus();
  };
  return { ref, onKeyDown };
};

// "X minutes ago" format used in dashboard cards.
// P356 — localized via direct TRANSLATIONS lookup (timeAgo is a
// window.* function called from non-React contexts; same pattern as
// P346's createProject untitled fallback). Falls back to English on
// any error so a stale call before TRANSLATIONS is initialized
// doesn't break the bell / sessions / trash UI.
const _timeAgoT = (key, n) => {
  let template;
  try {
    template = TRANSLATIONS[__lang]?.[key] ?? TRANSLATIONS.en?.[key] ?? key;
  } catch { template = key; }
  return template.replace("{n}", String(n));
};
window.timeAgo = (ts) => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return _timeAgoT("timeAgo.justNow");
  if (s < 3600) return _timeAgoT("timeAgo.minutes", Math.floor(s / 60));
  if (s < 86400) return _timeAgoT("timeAgo.hours", Math.floor(s / 3600));
  if (s < 86400 * 7) return _timeAgoT("timeAgo.days", Math.floor(s / 86400));
  if (s < 86400 * 30) return _timeAgoT("timeAgo.weeks", Math.floor(s / (86400 * 7)));
  // P553 — Pre-fix the function capped at "months" → a 3-year-old
  // timestamp rendered "36mo ago" instead of "3y ago". Sites with
  // long-lived data (notifications kept unread for months, the
  // /api/auth/account memberSince when surfaced via timeAgo) showed
  // unnatural-feeling stale-month counts. Years branch added at the
  // 365-day boundary; months branch now caps at 11 (since 12 months
  // == 1 year and we want the year branch to take over there).
  if (s < 86400 * 365) return _timeAgoT("timeAgo.months", Math.floor(s / (86400 * 30)));
  return _timeAgoT("timeAgo.years", Math.floor(s / (86400 * 365)));
};

// ── i18n (EN / RU / UZ) ─────────────────────────────────────────────────────
// Ships translations for the marketing surfaces and auth modal. Operator
// chrome and builder/dashboard internals stay in EN since the team is
// English-first; client-facing surfaces are the ones that need to localize.
const TRANSLATIONS = {
  en: {
    "nav.how": "How it works",
    "nav.showcase": "Showcase",
    "nav.pricing": "Pricing",
    "nav.faq": "FAQ",
    "nav.templates": "Templates",
    "nav.signIn": "Sign in",
    "nav.startBuilding": "Start building",
    "nav.startFree": "Start free",
    "nav.dashboard": "Dashboard →",
    "nav.mainAria": "Main navigation",

    "hero.eyebrow": "Now in open beta · v0.6",
    "hero.headlineL1": "Describe a website.",
    "hero.headlineL2": "Watch it build itself.",
    "hero.lede": "Forge turns plain language into a real, deployable site — in seconds, not sprints.",
    "hero.promptLabel": "NEW PROJECT",
    "hero.promptHint": "Press ⏎ to build",
    "hero.cta": "Build it",

    // P447 — `how.kicker` + `how.titleA` + `how.titleB` + per-step
    // keys are at the bottom of the EN block; the previous combined
    // `how.title` key was unused (only `how.kicker` had a consumer).
    "showcase.kicker": "MADE WITH FORGE",
    "showcase.title": "A few things built this week.",
    "showcase.seeAll": "See all templates →",
    "showcase.bigCtaTitle": "Your turn. What are you making?",
    "showcase.bigCta": "Open the builder →",
    // P405 — Per-card chrome: "built in 38s" footer pill, hover-
    // reveal "Open →" CTA, aria-label, click toast.
    "showcase.builtIn": "built in 38s",
    "showcase.openCta": "Open",
    "showcase.openAria": "Open {title} preview",
    "showcase.openToast": "Open “{title}” preview — coming soon",

    // P449 — `faq.title` was a defined key never wired (landing FAQ
    // hardcoded the headline as JSX so the italic emphasis on
    // `doubts.` worked). Renamed to A/B parts so the italic is
    // preserved as a separate translatable unit (same pattern as
    // P440/P441/P442).
    "faq.kicker": "QUESTIONS",
    "faq.titleA": "Reasonable",
    "faq.titleB": "doubts.",
    // P404 — Landing FAQ items (4 Q+A pairs).
    "faq.q1": "Is the code real, or is it a screenshot?",
    "faq.a1": "Real. Forge generates a Next.js + Tailwind project. You can inspect every file, edit it in the chat, or hand off the repo to your team.",
    "faq.q2": "Who owns what Forge builds?",
    "faq.a2": "You do. The code, the content, the deploy. Forge keeps no claim. Take the repo with you whenever you want.",
    "faq.q3": "What if I'm not technical?",
    "faq.a3": "You don't need to read a line of code. Talk to Forge in plain language — describe what you want, point at sections, and it'll edit them.",
    "faq.q4": "Can I bring my own brand and components?",
    "faq.a4": "Yes. Drop in a Figma file, a style guide, or a few screenshots. Forge will lift colors, type, and spacing and apply them to anything it builds.",

    "footer.product": "Product",
    "footer.company": "Company",
    "footer.resources": "Resources",
    "footer.madeWith": "Made with Forge",
    // P372 — Landing footer link labels + status pill + copyright.
    // The "coming soon" toast is templated through card.menu.comingSoon
    // for consistency with the rest of the app's stub toasts.
    "footer.builder": "Builder",
    "footer.showcase": "Showcase",
    "footer.changelog": "Changelog",
    "footer.about": "About",
    "footer.blog": "Blog",
    "footer.careers": "Careers",
    "footer.press": "Press",
    "footer.docs": "Docs",
    "footer.privacy": "Privacy",
    "footer.terms": "Terms",
    "footer.copyright": "© 2026 Forge Labs, Inc.",
    "footer.allSystemsNormal": "All systems normal",
    "footer.allSystemsTooltip": "API · Edge · Auth",
    "footer.allSystemsToast": "All systems normal · API · Edge · Auth",

    "pricing.heroEyebrow": "SIMPLE PRICING · CANCEL ANY TIME",
    "pricing.heroL1": "Free to build.",
    "pricing.heroL2": "Pay when you publish.",
    "pricing.heroLede": "Design as many sites as you want, free. Pay only when you ship one to a real domain — and only as much as you actually need.",
    // P448 — Removed 9 dead pricing.* keys superseded by P440-P442
    // structured 2-part headlines and P438 dynamic CTA: compareKicker,
    // compareTitle, faqTitle, enterpriseKicker, enterpriseTitle,
    // enterpriseCta, tierFree, tierPro, tierStudio.

    "templates.heroEyebrow": "TEMPLATES · ALL FREE TO REMIX",
    "templates.heroL1": "Skip the blank page.",
    "templates.heroL2": "Start with a real one.",
    "templates.heroLede": "Each template is a real Forge project. Pick one, then chat with Forge to change anything — copy, palette, layout, sections.",
    "templates.searchPlaceholder": "Search templates by name, kind, or vibe…",
    "templates.scratchTitleA": "Don't see what you need?",
    "templates.scratchTitleB": "Describe it instead.",
    "templates.scratchCta": "Start from scratch →",

    "auth.signInTitle": "Welcome back",
    "auth.signUpTitle": "Create your account",
    "auth.sentTitle": "Check your inbox",
    "auth.signInKicker": "SIGN IN",
    "auth.signUpKicker": "SIGN UP",
    "auth.sentKicker": "MAGIC LINK SENT",
    "auth.email": "Email",
    "auth.name": "Your name",
    "auth.namePlaceholder": "Madina Yusupova",
    "auth.emailPlaceholder": "you@example.com",
    "auth.magicLinkBtn": "Email me a magic link",
    "auth.magicLinkBtnSending": "Sending…",
    "auth.or": "OR",
    "auth.continueGoogle": "Continue with Google",
    "auth.continuePhone": "Continue with Phone",
    "auth.toggleToSignUp": "New to Forge? Create one",
    "auth.toggleToSignIn": "Already have an account? Sign in",
    // P450 — auth.legal removed; superseded by the P338 split into
    // auth.legalBefore + auth.legalTermsLabel + auth.legalConjunction
    // + auth.legalPrivacyLabel + auth.legalAfter (so the embedded
    // links can be real <a> tags rather than plain text).
    // P323 — flat `auth.legal` is unused: the actual JSX renders Terms
    // and Privacy as separate clickable links, which a single string
    // can't carry. Split into 5 pieces so each language can wrap the
    // link labels with its own connectives + word order. Order matters
    // for SOV languages like Uzbek where the verb phrase ("rozilik
    // bildirasiz") comes at the end.
    "auth.legalBefore": "By creating an account you agree to our ",
    "auth.legalTermsLabel": "Terms",
    "auth.legalConjunction": " and ",
    "auth.legalPrivacyLabel": "Privacy Policy",
    "auth.legalAfter": ".",
    // P324 — SentView body + button copy was previously hardcoded
    // English while the rest of the modal was translated; same i18n
    // gap as P323's legal disclaimer. body is split before/after so
    // the email address can be interpolated at the right position
    // for each language's word order.
    "auth.sentBodyBefore": "We sent a magic link to ",
    "auth.sentBodyAfter": ". Click the link in your inbox to finish signing in.",
    // P560 — Expiry hint matches server-side TOKEN_TTL_SEC (15 min).
    "auth.sentExpiry": "The link expires in about 15 minutes.",
    "auth.sentResend": "Resend",
    "auth.sentChangeEmail": "Use a different email",
    "auth.sentPreviewKicker": "PREVIEW SHORTCUT",
    "auth.sentPreviewBody": "Email isn't configured on this environment — click below to follow the link directly.",
    "auth.sentPreviewCta": "Open the magic link",
    // P338 — auth-modal toast strings, still hardcoded English even
    // after P323/P324 translated the form + sent view. Picks up the
    // rest of the user-facing strings in auth.jsx.
    "auth.toastSignInFailed": "Sign-in failed",
    "auth.toastRateLimited": "Slow down — try again in a minute or two",
    "auth.toastResentSent": "Magic link resent — check your inbox",
    "auth.toastResent": "Magic link resent",
    "auth.toastResendFailed": "Could not resend the link",
    "auth.toastGoogleComingSoon": "Google sign-in — coming soon. Use email for now.",
    "auth.toastPhoneComingSoon": "SMS verification — coming soon",
    // P341 — NotificationsBell chrome (panel title, mark-all-read,
    // empty state, aria-label, unread tooltip). The bell is on every
    // authed surface; pre-fix all 5 strings were hardcoded English
    // even on Russian/Uzbek-flagged accounts. Per-notification copy
    // (n.title / n.body) still flows through verbatim from the
    // server's English templates — that's a follow-up since the
    // schema doesn't carry locale yet.
    "notif.bell": "Notifications",
    "notif.unreadAriaSuffix": "{n} unread",
    "notif.unreadTooltip": "{n} new",
    "notif.markAllRead": "Mark all read",
    "notif.empty": "You're all caught up.",
    // P342 — per-notification title/body. Server stores these as
    // pre-rendered English (`title`/`body` columns); the bell now
    // overrides them client-side when `n.kind` is a known queue
    // event, so a Russian/Uzbek user reads localized copy. Falls
    // back to the server's stored text for unknown kinds (legacy
    // seed rows, future kinds we haven't translated yet). The
    // `live` body has two variants: with the live URL and without
    // (operator hasn't filled `liveUrl` yet) — pick by looking up
    // the project's `liveUrl` from the projects list.
    "notif.wired.title": "Your team's wiring up {project}",
    "notif.wired.body": "We've picked up your request and started setting up the backend.",
    "notif.live.title": "{project} is live",
    "notif.live.bodyAt": "Live at {url}",
    "notif.live.bodyGeneric": "Your site went live.",
    "notif.failed.title": "{project} hit a snag",
    "notif.failed.body": "We ran into something and reached out. Check your email.",
    "notif.fallbackProject": "your project",
    // P346 — default project name when the user submits with empty
    // name AND empty prompt. createProject fires this from a
    // non-React context (window.createProject is plain function),
    // so the call site does a direct TRANSLATIONS lookup rather
    // than going through useT().
    "project.untitled": "Untitled",
    // P347 — per-screen <title> for browser tab + SR page-change
    // announcements. Pre-fix all hardcoded English even though the
    // SPA otherwise honored locale.
    "title.landing": "Forge — Build websites by talking",
    "title.pricing": "Pricing — Forge",
    "title.templates": "Templates — Forge",
    "title.privacy": "Privacy — Forge",
    "title.terms": "Terms — Forge",
    "title.dashboard": "Your projects — Forge",
    "title.builder": "Builder — Forge",
    "title.operator": "Operator queue — Forge",
    "title.account": "Account — Forge",
    "title.notfound": "Page not found — Forge",
    // P579 — Per-screen meta descriptions for SEO. Pre-fix /pricing
    // and /templates inherited the static landing description from
    // index.html, so Google indexed all three pages with the same
    // snippet. Each ~155 chars to fit Google's typical SERP cutoff.
    "meta.descLanding": "Forge turns a conversation into a real website. Tell us what you want, watch it appear, then hand the wiring to a human operator. Built for non-technical owners.",
    "meta.descPricing": "Three plans for Forge — Free for one site, Pro at $19/mo for ten, Studio for agencies. Cancel anytime, pay monthly via Payme or Click. No setup fees.",
    "meta.descTemplates": "Browse Forge's starter templates: portfolios, storefronts, docs, landings, invites. Pick one to seed your AI-built site, or start from a blank prompt.",
    // P348 — Dashboard SortMenu chrome.
    "sort.label": "Sort",
    "sort.aria": "Sort projects",
    "sort.recent": "Recent",
    "sort.name": "Name (A→Z)",
    "sort.edits": "Most edited",
    // P352 — dashboard search bar + trash button chrome.
    "search.placeholder": "Search projects",
    "search.clear": "Clear search",
    "search.clearTooltip": "Clear (or press /)",
    "search.openPalette": "Open command palette",
    "search.paletteTooltip": "Command palette",
    "dash.trashTitle": "Recently deleted",
    "dash.trashAria": "Recently deleted projects",
    "dash.trashBtn": "Trash",
    // P353 — NewProjectCard (the prominent "start a new project" panel
    // at the top of the dashboard — the primary CTA path for authed
    // users). Placeholder example strings are intentionally English
    // (the AI picks up English briefs better; localizing them would
    // change demonstrated voice + might mislead the user about the
    // expected input language). Translating chrome only.
    "newproj.kicker": "NEW PROJECT",
    "newproj.placeholderPrefix": "Try: ",
    "newproj.aria": "New project brief",
    "newproj.hint": "Enter to build · Shift+Enter for newline",
    "newproj.button": "Build it",
    // P354 — Dashboard starter-chip labels. Click pre-fills the
    // textarea with "a <type> for " in English (the AI handles
    // English briefs better — same reasoning as P353's example
    // strings). The visible chip label translates per locale.
    "starter.landing": "Landing page",
    "starter.portfolio": "Portfolio",
    "starter.docs": "Docs site",
    "starter.booking": "Booking page",
    "starter.store": "Online store",
    // P356 — timeAgo. Called from non-React contexts (it's a
    // window.* function) so the lookup is direct against TRANSLATIONS,
    // same pattern as P346's createProject Untitled fallback.
    // {n} placeholder is the integer quantity. Spacing/punctuation
    // differs per locale (Russian uses NBSP between number and unit;
    // Uzbek uses normal space).
    "timeAgo.justNow": "just now",
    "timeAgo.minutes": "{n}m ago",
    "timeAgo.hours": "{n}h ago",
    "timeAgo.days": "{n}d ago",
    "timeAgo.weeks": "{n}w ago",
    "timeAgo.months": "{n}mo ago",
    // P553 — Years branch — "36mo ago" → "3y ago".
    "timeAgo.years": "{n}y ago",
    // P359 — project card status pill (dot + label) shown on every
    // dashboard project card. Pre-fix all cards rendered "Live" /
    // "Draft" in English regardless of locale. Operator queue
    // remains English-by-design; this is the user-facing surface.
    "proj.statusLive": "Live",
    "proj.statusDraft": "Draft",
    // P360 — Dashboard stats row labels + hints. The visitor count
    // is derived from live-projects (mock), so the "last 30 days"
    // hint is for vibe; rest are real.
    "stats.projectsLabel": "Projects",
    "stats.liveLabel": "Live",
    "stats.draftsLabel": "Drafts",
    "stats.visitorsLabel": "Visitors",
    "stats.totalSingular": "1 total",
    "stats.totalPlural": "{n} total",
    "stats.publishing": "publishing",
    "stats.inProgress": "in progress",
    "stats.last30days": "last 30 days",
    // P360 — ProjectsHeader filter chip labels (matches the project-
    // status pill keys above). "All" / "Recent" need their own keys
    // since they're not project-status-themed.
    "filter.all": "All",
    "filter.recent": "Recent",
    "filter.aria": "Filter projects",
    // P360 — header for the project list under the stats row.
    "dash.yourProjects": "YOUR PROJECTS",
    // P361 — Project card kebab menu items + the hover-reveal "Open"
    // CTA. The dispatcher uses stable string keys (rename / duplicate
    // / archive / delete) regardless of locale.
    "card.menu.rename": "Rename",
    "card.menu.duplicate": "Duplicate",
    "card.menu.archive": "Archive",
    "card.menu.delete": "Delete",
    "card.menu.comingSoon": "{action} — coming soon",
    "card.openCta": "Open",
    // P362 — soft-delete confirm + Trash toasts. Project name
    // interpolated via {name} placeholder.
    "card.delete.confirm": "Move \"{name}\" to Trash? It waits there 30 days — restore any time from the Trash drawer.",
    "card.delete.toast": "Moved \"{name}\" to Trash",
    "card.delete.undoLabel": "Undo",
    "card.delete.restoredToast": "Restored \"{name}\"",
    // P363 — Trash modal (dashboard's "Recently deleted" panel).
    // restoredToast reuses card.delete.restoredToast above.
    "trash.title": "Recently deleted",
    "trash.kicker": "TRASH",
    "trash.loading": "Loading…",
    "trash.empty": "Nothing deleted recently. Soft-deleted projects stay here so you can restore them.",
    "trash.deletedPrefix": "Deleted ",
    "trash.restore": "Restore",
    "trash.deleteForever": "Delete forever",
    "trash.purgeConfirm": "Permanently delete \"{name}\"? This cannot be undone.",
    "trash.deletedToast": "Deleted \"{name}\" forever",
    "trash.purgeError": "Couldn't purge: {reason}",
    // P364 — Dashboard header brand back-button title + workspace
    // switcher (the "Personal" pill next to the Forge mark, with its
    // dropdown menu and the "+ Create a team workspace" placeholder).
    "dash.brandBackTitle": "Back to landing",
    "ws.switchAria": "Switch workspace",
    "ws.personal": "Personal",
    "ws.menuAria": "Workspaces",
    "ws.createTeam": "+ Create a team workspace",
    "ws.teamsComingSoon": "Teams — coming soon",
    // P365 — Quota pills (project cap + AI-turn cap) on the dashboard.
    // Plural-aware "active project(s)" via two keys, picked by cap.
    "quota.freeCapHit": "you've hit the Free cap",
    "quota.proCapHit": "Pro plan cap of {n}",
    "quota.activeSingular": "active project",
    "quota.activePlural": "active projects",
    "quota.upgradeBtn": "Upgrade",
    "budget.outOfTurns": "out of turns today",
    "budget.dailyCapHit": "daily turn cap hit",
    "budget.turnsLeft": "{n} turns left today",
    "budget.turnsToday": "turns today",
    "budget.resetsInMinutes": "resets in {n}m",
    "budget.resetsInHours": "resets in {n}h",
    // P366 — Builder ChatComposer chrome.
    "composer.placeholderIdle": "Refine, redirect, or ask for something new…",
    "composer.placeholderStreaming": "Receiving the AI's reply…",
    "composer.aria": "Chat message",
    "composer.attach": "Attach file",
    "composer.addImage": "Add image",
    "composer.mention": "Mention file",
    "composer.attachComingSoon": "File attach — coming soon",
    "composer.attachStripAria": "Attachments",
    "composer.mentionComingSoon": "@-mention picker — coming soon",
    "composer.send": "Send",
    // P367 — Builder header. Workspace pill reuses ws.personal /
    // ws.switchAria from P364.
    "builder.backAria": "Back to dashboard",
    "builder.backTitle": "Back",
    "builder.projectSettings": "Project settings",
    "builder.switchBranch": "Switch branch",
    "builder.versionHistory": "Version history",
    "builder.editsLabel": "{n} edits",
    "builder.shareBtn": "Share",
    "builder.exportBtn": "Export",
    "builder.publishBtn": "Publish",
    "builder.savedSecondsAgo": "saved seconds ago",
    "builder.savedAMomentAgo": "saved a moment ago",
    "builder.savedMinutesAgo": "saved {n}m ago",
    "builder.savedHoursAgo": "saved {n}h ago",
    // P368 — Builder chat panel (conversation column header +
    // typing-indicator SR cue).
    "chat.conversation": "Conversation",
    "chat.promptsSingular": "1 prompt",
    "chat.promptsPlural": "{n} prompts",
    "chat.typing": "Assistant is typing",
    // P369 — Assistant message-kind badges. The streaming chat shows
    // these as small labels above the message body, one per kind
    // (thinking / files / diff / system note).
    "chat.thinking": "Thinking",
    "chat.wroteFiles": "WROTE {n}",
    "chat.edited": "EDITED",
    "chat.systemNote": "SYSTEM NOTE",
    // P375 — Builder WorkPane tabs. Operator-only tabs (code, backend)
    // stay English-by-design; non-operator users see Preview/History
    // and those localize.
    "work.tabPreview": "Preview",
    "work.tabHistory": "History",
    // P419 — Preview pane toolbar: refresh + open-in-new-tab buttons
    // (each has both an aria-label for SR and a data-tooltip for the
    // visual on-hover bubble) plus the post-click toast on the
    // pop-out-preview button.
    "work.refreshAria": "Refresh preview",
    "work.refreshTip": "Refresh preview",
    "work.openNewAria": "Open preview in new tab",
    "work.openNewTip": "Open in new tab",
    "work.popoutToast": "Pop-out preview lands with Publish — Step 2",
    // P420 — Preview pane device-size picker (3 buttons rendering
    // as ▯ / ◧ / ▭ glyphs). Pre-fix the `title` attr was the raw id
    // (lowercase English) and there was no aria-label, so SR users
    // heard "button" with no context. Localize both via `device.*`.
    "device.mobile": "Mobile",
    "device.tablet": "Tablet",
    "device.desktop": "Desktop",
    // P426 — Builder chat-pane jump-to-bottom button. Appears only
    // when the user has scrolled up while a stream is in flight.
    "chat.jumpLatest": "Latest",
    // P427 — Builder URL pill (the localhost:3000 pseudo-URL that
    // sits in the work-pane toolbar). Click → clipboard. Pre-fix
    // the tooltip + the post-copy toast were hardcoded English.
    // Adding a proper aria-label since SR users without a tooltip
    // could only hear the URL itself.
    "work.copyUrlTip": "Copy URL",
    "work.copyUrlAria": "Copy preview URL: {url}",
    "work.copiedUrlToast": "Copied {url}",
    // P428 — Builder ▸ History tab chrome (kind labels, files-touched
    // line, Restore button + post-click toast). Item titles and time
    // strings are mock data and stay English.
    "history.kindBuild": "BUILD",
    "history.kindEdit": "EDIT",
    "history.filesTouched": "{n} files touched",
    "history.restore": "Restore",
    "history.restoreToast": "Restore \"{title}\" — coming soon",
    // P429 — Generic "couldn't create/start" failure toasts on the
    // dashboard (Create-new-project) and templates (Use-template)
    // pages — fired on any non-402/401/409 error from createProject.
    "dash.createError": "Couldn't create project: {message}",
    "tpl.startError": "Couldn't start that template: {message}",
    // P430 — Notifications panel footer (View all activity → button +
    // post-click coming-soon toast) and the global restoreProject
    // error toast (fires when the network call fails — distinct from
    // the success/dedupe toasts which are already wired through
    // proj.* keys).
    "notif.viewAll": "View all activity",
    "notif.viewAllToast": "Activity feed — coming soon",
    "proj.restoreError": "Couldn't restore — try again in a moment",
    // P451 — Local fallback for the 402 restore-quota toast. The
    // server returns body.error pre-localized (P391's
    // `_RESTORE_QUOTA_MSG[lang]`), but if it omits the field the
    // toast was English-only.
    "proj.restoreOverCap": "Restore would exceed your project cap",
    // P452 — Dashboard empty-state branches: 'no-projects' (a fresh
    // user with zero projects) and 'no-match' (filter/search zeroed
    // the grid). Both render through `role="status"` + `aria-live`
    // (P251); the SR announces them but the text was English-only.
    "dash.emptyNoProjects": "No projects yet",
    "dash.emptyNoProjectsBody": "Try a prompt above — your first site builds in under a minute.",
    "dash.emptyNoMatch": "Nothing matched",
    "dash.emptyNoMatchBody": "Try a different filter, or clear your search.",
    "dash.emptyClearFilters": "Clear filters",
    // P453 — Landing scrolling marquee — 6 promotional ticker items.
    // Visible on every landing visit; was English-only.
    "marquee.builtToday": "1,284 sites built today",
    "marquee.firstDeploy": "avg. time to first deploy: 47s",
    "marquee.deploys": "deploys to your domain",
    "marquee.stack": "real React + Tailwind under the hood",
    "marquee.editEnglish": "edit in plain English",
    "marquee.versions": "version control built in",
    // P454 — Builder seed-simulation chat (runs on the first visit
    // to a fresh project, before any real chat history exists). Was
    // English-only; localize the generic chrome. The summary line
    // is intentionally generic (not "cream paper, italic display"
    // which only fits the typeface-portfolio sample).
    "seed.thinking": "Setting up a {tag} project from your brief. Drafting hero, primary sections, and a tokens file so the palette stays consistent.",
    "seed.summary": "Done. {name} is ready. Anything you want to refine?",
    "seed.suggestQuieter": "Make the hero quieter",
    "seed.suggestAddForm": "Add a contact form",
    "seed.suggestDarker": "Try a darker palette",
    "seed.suggestMoreItems": "Show 6 items, not 3",
    // P455 — Builder offline-fallback suggestion chips. The text is
    // both the visible label AND the prompt sent to the AI when the
    // user clicks (no sentinel routing for these — they round-trip as
    // free-form user messages). Localize the labels; the AI replies
    // in whatever language the prompt arrived in.
    "chat.suggestOpenPreview": "Open the preview",
    "chat.suggestTryDifferent": "Try a different brief",
    "chat.suggestTryAgain": "Try again",
    "chat.suggestTalkHuman": "Talk to a human",
    // P456 — Dashboard greeting fallback name. Used when auth.name
    // is empty (e.g. user signed in via magic link without setting
    // a name yet). Pre-fix the English "there" mixed with the
    // localized greeting verb — "Доброе утро, there." in RU.
    "dash.greetFallbackName": "there",
    // P431 — `ModalShell` close button (the X in the upper-right of
    // every modal — Share, Export, Settings, Image upload, Auth,
    // Publish). Single string, but used by every modal in the app.
    "modal.close": "Close",
    // P457 — Defensive fallback aria-label for `MTabs` when a caller
    // forgets to pass a `label` prop. Every current caller does pass
    // one, but this fallback shipped English-only.
    "modal.tabsFallback": "Tabs",
    // P460 — Hero textarea rotating placeholder prompts. Pre-fix
    // an RU/UZ user typing in Cyrillic/Latin-Uzbek saw English
    // suggestions ("a portfolio for a typeface designer in Lisbon")
    // — useful for English speakers but a discoverability gap for
    // others. Localize so the rotating examples demonstrate that the
    // builder accepts native-language briefs.
    "heroPrompt.0": "a portfolio for a typeface designer in Lisbon",
    "heroPrompt.1": "a one-pager for a sourdough subscription, warm and rustic",
    "heroPrompt.2": "a docs site for my open-source rate limiter",
    "heroPrompt.3": "a launch page for an AI scheduling assistant, dark mode",
    "heroPrompt.4": "a wedding invite site, photo-led, with RSVP form",
    // P461 — Dashboard new-project textarea placeholder examples
    // (6 cycling). 3 reuse the hero prompt keys; 3 are dashboard-
    // specific (clinic in Tashkent, Friday-sourdough, wedding-no-form).
    "dashExample.clinic": "a clinic site for a dentist in Tashkent, calm and clean",
    "dashExample.fridaySourdough": "a one-pager for our Friday sourdough subscription",
    "dashExample.weddingShort": "a wedding invite site, photo-led, with RSVP",
    // P462 — Publish modal Pro-path step headers (ModalShell title +
    // kicker). The Quick path's headers were already localized
    // (`pub.kickerQuick` / `pub.titleQuick` etc.) but the Pro path's
    // pro_configure + pro_checkout steps' headers were hardcoded.
    "pub.kickerProConfigure": "PRO PUBLISH",
    "pub.titleProConfigure": "Set up your domain & backend",
    "pub.kickerProCheckout": "CHECKOUT",
    "pub.titleProCheckout": "Confirm your plan",
    // P463 — Reassurance hint shown below the Quick / Pro path cards
    // on the publish IntroStep. Was hardcoded English even after the
    // surrounding cards (P379) and step headers (P462) were localized.
    "pub.upgradeHint": "You can upgrade from Quick to Pro any time.",
    // P467 — Command palette footer key-hint labels (navigate/select/
    // toggle). Visible English-only on every locale; localize the
    // verb labels and aria-hide the keycap glyphs so SR doesn't read
    // out arrow-symbol names.
    "cp.footNavigate": "navigate",
    "cp.footSelect": "select",
    "cp.footToggle": "toggle",
    // P432 — Templates page filter chips. The id stays English (used
    // as the cat-state key + filter match against the template's
    // .cat field) but the rendered chip label resolves via t() so
    // RU/UZ users see localized chips. Plus the singular/plural
    // count line (`{n} template[s]`).
    "templates.catAll": "All",
    "templates.catLanding": "Landing",
    "templates.catPortfolio": "Portfolio",
    "templates.catStorefront": "Storefront",
    "templates.catBooking": "Booking",
    "templates.catRestaurant": "Restaurant",
    "templates.catDocs": "Docs",
    "templates.catWedding": "Wedding",
    "templates.catNewsletter": "Newsletter",
    "templates.catResume": "Resume",
    "templates.countOne": "{n} template",
    "templates.countFew": "{n} templates",
    "templates.countMany": "{n} templates",
    // P636 — Template card names (15). Prompts stay English-by-design
    // for now — they seed the AI builder chat and the locale-aware
    // stub story isn't fully settled yet (translating only the prompt
    // would make the AI's inbound brief mismatch its outbound reply).
    "tpl.aiLaunch": "AI launch page",
    "tpl.agencyLanding": "Studio agency",
    "tpl.saasLanding": "Productivity SaaS",
    "tpl.typePortfolio": "Type designer",
    "tpl.photoPortfolio": "Photographer",
    "tpl.archPortfolio": "Architect",
    "tpl.bakery": "Bakery shop",
    "tpl.fashionShop": "Fashion drop",
    "tpl.dental": "Dental clinic",
    "tpl.salon": "Hair salon",
    "tpl.restaurant": "Restaurant menu",
    "tpl.docsSite": "OSS docs",
    "tpl.wedding": "Wedding invite",
    "tpl.newsletter": "Newsletter",
    "tpl.resume": "Resume / CV",
    // P434 — Templates page Grid + ScratchCallout + footer:
    // empty-state copy, per-card aria-label + CTA label, footer
    // Back-to-home + Pricing links. Card prompts and the cat-thumb
    // display (uppercase localized cat) come from the template
    // record's `.cat` field which keys into TEMPLATE_CAT_KEY (P432).
    "templates.emptyTitle": "No templates matched.",
    "templates.emptyBody": "Try a different category or keyword.",
    "templates.useAria": "Use the {name} template",
    "templates.useThis": "Use this",
    "templates.footerBack": "Back to home",
    "templates.footerPricing": "Pricing",
    // P437 — Notifications panel unread-indicator dot. The dot is
    // the ONLY visual cue that a notification is unread (the rest of
    // the row looks identical to read), so it can't be aria-hidden;
    // instead it gets an aria-label so SR announces "Unread".
    "notif.unreadAria": "Unread",
    // P438 — Pricing TierCard CTA label when the user is already on
    // the tier shown — was hardcoded English even after the surrounding
    // tier blurb / feature labels stayed English by design (sample
    // copy). This single string is dynamic state ("you are here")
    // and deserves a localized label.
    "pricing.currentPlan": "Current plan",
    // P439 — TierGrid footnote sitting under the 3-tier card row.
    // Discloses the addon/cancellation terms that the per-card copy
    // doesn't repeat. Was hardcoded English.
    "pricing.tierFootnote": "Prices in USD. Backend addons billed per integration on Studio · cancel any time.",
    // P440 — EnterpriseCallout: kicker, headline (split into 2 spans
    // so the italic emphasis is preserved as a separate translatable
    // unit), body paragraph, CTA button + post-click coming-soon
    // toast.
    "pricing.entKicker": "AGENCIES & TEAMS",
    "pricing.entTitleA": "Shipping for ten clients?",
    "pricing.entTitleB": "Let's talk.",
    "pricing.entBody": "Volume rates, dedicated workspaces, white-label options, and a person on our side who knows your sites by name.",
    "pricing.entCta": "Talk to us",
    "pricing.entToast": "Talk to us — coming soon",
    // P441 — Pricing FAQ section. Section head + 4 Q/A pairs.
    // Mirrors the landing FAQ pattern (P404).
    "pricing.faqKicker": "QUESTIONS",
    "pricing.faqTitleA": "The usual",
    "pricing.faqTitleB": "worries.",
    "pricing.faqQ1": "What does 'hand-wired by us' actually mean?",
    "pricing.faqA1": "Forge generates the frontend automatically. The backend bits — forms that actually deliver, bookings that sync to your calendar, online payments that actually charge — are still set up by a human (us) for each site. Pro gets a light setup; Studio gets the works.",
    "pricing.faqQ2": "Do I get charged before my site is live?",
    "pricing.faqA2": "No. We bill the first month only after your site is live. If you cancel before it ships, you don't pay anything.",
    "pricing.faqQ3": "Can I switch tiers later?",
    "pricing.faqA3": "Up or down, any time. Pro-rated. Cancel any time too — your sites stay up for the rest of the billing period.",
    "pricing.faqQ4": "What if I outgrow Studio?",
    "pricing.faqA4": "We do bespoke arrangements for agencies and teams. Hit 'Talk to us' below — we'll quote based on volume and what you need set up.",
    // P442 — ComparisonSection chrome (kicker + headline + Feature
    // column header). Tier names + comparison rows themselves are
    // sample pricing copy; English by design.
    "pricing.cmpKicker": "COMPARE",
    "pricing.cmpTitleA": "Every difference,",
    "pricing.cmpTitleB": "side by side.",
    "pricing.cmpFeatHeader": "Feature",
    // P542 — Visually-hidden table caption announces the table's
    // purpose to screen-reader users before they hear the column
    // headers. Sighted users see the SectionHead above the table
    // ("Every difference, side by side.") which serves the same
    // function visually.
    "pricing.cmpCaption": "Plan feature comparison",
    // P470 — Pricing comparison table section group headers (Build /
    // Publish / Service span-row dividers). The per-row feature names
    // and tier values stay sample English (P441 decision) but the
    // group dividers are 1-word labels that fit neatly under
    // localization without rewriting per-row content.
    "pricing.cmpGroupBuild": "Build",
    "pricing.cmpGroupPublish": "Publish",
    "pricing.cmpGroupService": "Service",
    // P629 — Comparison feature names. Tier values (Unlimited /
    // 7 days / Forever / Light / Full / 48h / Community / etc.)
    // deferred to a later iteration; the feature names are the
    // higher-leak surface (one per row, more visible).
    "pricing.cmpFeatAi": "AI builder",
    "pricing.cmpFeatPreview": "Live preview",
    "pricing.cmpFeatVersions": "Version history",
    "pricing.cmpFeatTemplates": "Templates",
    "pricing.cmpFeatSites": "Published sites",
    "pricing.cmpFeatSubdomain": "forge.app subdomain",
    "pricing.cmpFeatCustomDomains": "Custom domains",
    "pricing.cmpFeatContactForm": "Contact form",
    "pricing.cmpFeatNewsletter": "Newsletter signup",
    "pricing.cmpFeatBooking": "Booking calendar",
    "pricing.cmpFeatOrdering": "Online ordering / payments",
    "pricing.cmpFeatAccounts": "User accounts + login",
    "pricing.cmpFeatBackend": "Backend hand-wired by us",
    "pricing.cmpFeatTurnaround": "Setup turnaround",
    "pricing.cmpFeatSupport": "Support",
    "pricing.cmpFeatCancel": "Cancel anytime",
    // P630 — Comparison value strings. Numbers (1, 10), em-dash (—),
    // and time-codes (48h, 24h) stay literal — universal across
    // locales. Word-bearing values get keys so RU/UZ readers see a
    // fully localized table.
    "pricing.cmpVal.unlimited": "Unlimited",
    "pricing.cmpVal.7days": "7 days",
    "pricing.cmpVal.30days": "30 days",
    "pricing.cmpVal.forever": "Forever",
    "pricing.cmpVal.public": "Public",
    "pricing.cmpVal.all": "All",
    "pricing.cmpVal.allBespoke": "All + bespoke",
    "pricing.cmpVal.upTo3": "Up to 3",
    "pricing.cmpVal.light": "Light",
    "pricing.cmpVal.full": "Full",
    "pricing.cmpVal.community": "Community",
    "pricing.cmpVal.email1bd": "Email · 1 business day",
    "pricing.cmpVal.prioritySlack": "Priority + Slack",
    // P631 — Tier card content (was hardcoded in TIERS const). Tag,
    // cadence, blurb, CTA, and 5–6 feature labels per tier.
    "pricing.tierFree.tag": "TRY IT",
    "pricing.tierFree.cadence": "forever",
    "pricing.tierFree.blurb": "Design as much as you want. Publish one site to a forge.app subdomain.",
    "pricing.tierFree.cta": "Start free",
    "pricing.tierFree.feat1": "1 active site (draft or live)",
    "pricing.tierFree.feat2": "Live preview",
    "pricing.tierFree.feat3": "Publish to a forge.app subdomain",
    "pricing.tierFree.feat4": "Static only — no forms, no logins, no payments",
    "pricing.tierFree.feat5": "Community support",
    "pricing.tierPro.tag": "MOST CHOSEN",
    "pricing.tierPro.cadence": "per month",
    "pricing.tierPro.blurb": "For solo founders shipping branded sites with a working contact form.",
    "pricing.tierPro.cta": "Get Pro",
    "pricing.tierPro.feat1": "Everything in Free",
    "pricing.tierPro.feat2": "Up to 10 active sites",
    "pricing.tierPro.feat3": "Up to 3 custom domains",
    "pricing.tierPro.feat4": "Hand-wired contact form & newsletter",
    "pricing.tierPro.feat5": "Edge analytics",
    "pricing.tierPro.feat6": "Email support · 1 business day",
    "pricing.tierStudio.tag": "AGENCIES & SHOPS",
    "pricing.tierStudio.cadence": "per month",
    "pricing.tierStudio.blurb": "When the backend is the product. Booking, ordering, payments, accounts — all hand-wired.",
    "pricing.tierStudio.cta": "Talk to us",
    "pricing.tierStudio.feat1": "Everything in Pro",
    "pricing.tierStudio.feat2": "Unlimited custom domains",
    "pricing.tierStudio.feat3": "Booking, ordering, payments, accounts",
    "pricing.tierStudio.feat4": "We hand-wire each integration",
    "pricing.tierStudio.feat5": "Priority support · Slack channel",
    // P477 — Project card kebab button (the ⋯ menu trigger). 3 strings
    // missed in earlier sweeps: the trigger button's aria-label, its
    // hover tooltip, and the popover menu's aria-label.
    "card.kebabAria": "Actions for {name}",
    "card.kebabTip": "More",
    "card.menuAria": "{name} actions",
    // P478 — Two more template aria-labels: project card open
    // (`Open {name}`) and chat composer attachment remove
    // (`Remove {name}`). Same mixed-locale problem as P477.
    "card.openAria": "Open {name}",
    "composer.removeAttachAria": "Remove {name}",
    // P498 — BuilderMobileGate (the "switch to a wider screen" card
    // shown when builder opens below the mobile breakpoint). Title
    // is split for italic on the second line.
    "builder.mobileGateTitleA": "Forge is best on a",
    "builder.mobileGateTitleB": "wider screen.",
    "builder.mobileGateBody": "The builder needs room for both chat and a live preview. Try this on a tablet or desktop — same project, all your work.",
    // P499 — Operator gate (the /operator URL when accessed by a
    // non-operator visitor — usually a wrong-URL hit). Operator
    // ADMIN UI stays English-by-design, but the gate page is
    // user-facing and should localize. Title is split for italic.
    "operator.gateTitleA": "This area is",
    "operator.gateTitleB": "team-only.",
    "operator.gateBody": "Sign in with a Forge operator account to access the backend queue.",
    "operator.gateBack": "Back",
    "operator.gateSignIn": "Sign in as operator",
    // P505 — Auth-form inline email validation error. Shown right
    // below the email input when it doesn't match the basic shape
    // regex; was hardcoded English even after the surrounding form
    // chrome (P323) localized.
    "auth.emailInvalid": "That doesn't look like a valid email.",
    // P506 — Auth-form inline name-too-short error. Pre-fix
    // hardcoded English; mirrors the server-side _NAME_TOO_SHORT
    // (P392) but as a client-side inline hint shown live as the
    // user types.
    "auth.nameTooShort": "Use at least 2 characters.",
    // P518 — Legal-page banner + last-updated. Body content stays
    // English (placeholder until reviewed-by-counsel; per the
    // already-existing DRAFT banner) but the banner + meta-line
    // localize so the warning lands in the user's language.
    "legal.draftBanner": "DRAFT — these are placeholders. Replace with reviewed copy before launch.",
    "legal.lastUpdated": "Last updated: {date} (draft)",
    // P556 — Header-crumb labels for /privacy + /terms. Pre-fix the
    // crumb was a hardcoded English `doc.crumb` field on the static
    // PRIVACY / TERMS docs.
    "legal.crumb.privacy": "Privacy Policy",
    "legal.crumb.terms": "Terms of Service",
    // P557 — Page subtitle (the lede under the H1). Distinct from
    // the crumb because each page has its own marketing-style
    // sentence — not a legal claim, so safe to translate without
    // counsel review.
    "legal.subtitle.privacy": "What we collect, why, and what we never do with it.",
    "legal.subtitle.terms": "The plain-language deal between you and Forge.",
    // P519 — Legal-page footnote with support email. The body is
    // draft/placeholder English by design, but the footnote that
    // tells the user where to ask questions about the policy is
    // product chrome — should land in the user's locale.
    "legal.footnoteBefore": "Questions? Email ",
    "legal.footnoteAfter": ".",
    // P445 — Skip-to-content link (the first interactive element on
    // the page). index.html ships it as static HTML so keyboard
    // users can tab to it before JS loads; once shared.js boots we
    // swap the text + href-target name to match the active locale.
    "a11y.skipToContent": "Skip to content",
    // P446 — Builder header's anon-user CTA (the prominent
    // "Sign up to save" pill). Visible to every anon visitor who
    // wanders into the builder without an account — was hardcoded
    // English even after the rest of the builder header localized.
    "builder.signupCta": "Sign up to save",
    "builder.signupCtaTip": "Save this conversation to your account",
    // P447 — Landing HowItWorks section: kicker, 2-part headline
    // (italic preserved on the timing claim), 4 step titles + bodies.
    "how.kicker": "HOW IT WORKS",
    "how.titleA": "Four steps.",
    "how.titleB": "Roughly forty seconds.",
    "how.step1Title": "Tell Forge what you need",
    "how.step1Body": "A sentence is enough. Mention tone, audience, sections — or just the gist. Forge fills in the rest.",
    "how.step2Title": "Watch it draft, live",
    "how.step2Body": "Pages, components, copy, and a working preview render side-by-side. Nothing is a screenshot — it's real code.",
    "how.step3Title": "Edit by talking back",
    "how.step3Body": "Refine in the same chat. Move sections, swap palettes, restructure layouts. Forge keeps a diff so nothing's lost.",
    "how.step4Title": "Ship to your domain",
    "how.step4Body": "One click pushes to a real URL. Connect your domain, hand off the repo, or keep iterating in Forge.",
    // P376 — Account sessions list user-agent labels. Browser + OS
    // names stay as identifiers (language-neutral) but the connective
    // ("on") and the "Unknown device" fallback localize.
    "ua.unknownDevice": "Unknown device",
    "ua.on": "on",
    // P377 — Auth runtime toasts (signIn / signOut / session-expired
    // / sign-in-as-operator). Live in window.* functions, so the
    // call sites do direct TRANSLATIONS lookup (same pattern as
    // P346 createProject + P356 timeAgo).
    "auth.fallbackName": "Friend",
    "auth.welcome": "Welcome, {name}",
    "auth.signedOut": "Signed out",
    "auth.sessionExpired": "Your session expired — sign in to keep working",
    "auth.sessionExpiredAction": "Sign in",
    "auth.signInAsOperator": "Sign in with your operator email — we'll route you to the queue.",
    // P378 — UserPill account-menu aria-label + the two toasts that
    // fire from the "Manage subscription" branch (503 stub + portal-
    // open error path).
    "pill.menuAria": "Account menu",
    "pill.subscriptionStub": "Subscription management is coming soon — please contact us to update your plan.",
    "pill.portalError": "Could not open portal: {message}",
    // P379 — Publish modal intro step (first card every user sees on
    // clicking Publish). Body splits before/after a literal <code>
    // span so the URL specimen ("your-name.forge.app") can stay
    // inline.
    "pub.kickerIntro": "PUBLISH",
    "pub.titleIntro": "How do you want to ship?",
    "pub.introLede": "Free to ship a static site on a forge.app subdomain. Need a custom domain or a real backend? We hand-wire those for you.",
    "pub.quickTag": "FREE · INSTANT",
    "pub.quickTitle": "Quick publish",
    "pub.quickBodyBefore": "Live in seconds at ",
    "pub.quickBodyAfter": ". Static only — no forms, no logins, no payments.",
    "pub.quickCta": "Publish free",
    "pub.proTag": "PRO · WE WIRE IT",
    "pub.proTitle": "Pro publish",
    "pub.proBody": "Your own domain plus the backend bits (forms, bookings, payments, accounts). We set them up by hand within 24 hours.",
    "pub.proCta": "Configure",
    // P380 — Publish modal QuickConfigureStep (subdomain picker).
    "pub.kickerQuick": "QUICK PUBLISH",
    "pub.titleQuick": "Pick a forge.app address",
    "pub.subdomainLabel": "Subdomain",
    "pub.subdomainTaken": "Taken — try another.",
    "pub.subdomainAvailable": "Available.",
    "pub.subdomainHelp": "2–48 chars, lowercase letters / digits / dashes; no leading or trailing dash.",
    "pub.includedLabel": "What's included",
    "pub.includedSsl": "Free SSL via the edge",
    "pub.includedPush": "Push to live in seconds",
    "pub.includedRepublish": "Re-publish anytime to update",
    "pub.includedUpgrade": "Forms, logins, payments — upgrade later",
    "pub.back": "Back",
    "pub.publishTo": "Publish to {url}",
    // P381 — Publish modal DeployingStep + LiveStep (the rest of the
    // Quick path: progress bar + the post-live success card).
    "pub.kickerDeploy": "DEPLOYING",
    "pub.titleDeploy": "Going live…",
    "pub.kickerLive": "LIVE",
    "pub.titleLive": "You're live.",
    "pub.deployStageProvision": "Provisioning forge.app subdomain",
    "pub.deployStageUpload": "Uploading static bundle",
    "pub.deployStageWarm": "Warming up the edge cache",
    "pub.deployStageTls": "Verifying TLS certificate",
    "pub.deployStageLive": "Live",
    "pub.deployProgressAria": "Deploy progress",
    "pub.deployProgressValueText": "{n} percent — {label}",
    "pub.livePill": "Live · just now",
    "pub.liveVisit": "Visit",
    "pub.liveCopy": "Copy link",
    "pub.linkCopiedToast": "Link copied",
    "pub.liveLede": "Anyone with the link can see it. Want a custom domain or a working contact form? Upgrade to Pro from the Project menu.",
    "pub.liveBackToProject": "Back to project",
    // P382 — Publish modal ProSentStep (success state on Pro path).
    // Body splits before/after the email so each locale can place
    // it naturally.
    "pub.kickerSent": "REQUEST SENT",
    "pub.titleSent": "We'll be in touch.",
    "pub.sentInQueue": "In our queue",
    "pub.sentBig": "You'll hear back within 24h.",
    "pub.sentBodyBefore": "Confirmation sent to ",
    "pub.sentBodyAfter": ". Reply with anything we missed.",
    "pub.summaryPending": "Pending plan",
    "pub.summaryFirstCharge": "First charge runs after we publish.",
    "pub.summaryCancelAnytime": "Cancel any time.",
    // P384 — Pricing comparison table Yes/No SR labels. The visible
    // glyphs are ✓ / —; this is the alt-label SR users hear.
    "compare.yes": "Yes",
    "compare.no": "No",
    // P385 — Command palette dialog chrome. Group names + command
    // labels (Navigate / Projects / Actions / Sign in / etc.) stay
    // English in this iteration; the listbox shell + search affordance
    // localize first.
    "cp.dialogAria": "Command palette",
    "cp.searchPlaceholder": "Type a command or search…",
    "cp.searchAria": "Type a command or search",
    "cp.listAria": "Commands",
    "cp.noMatches": "No matches for ",
    // P386 — Command-palette group names + command labels + hints +
    // the two coming-soon toasts. Project hints (Live / Live · URL /
    // Draft · tag) reuse `proj.statusLive` / `proj.statusDraft`.
    "cp.groupNavigate": "Navigate",
    "cp.groupProjects": "Projects",
    "cp.groupActions": "Actions",
    "cp.navLanding": "Go to landing",
    "cp.navPricing": "Go to pricing",
    "cp.navTemplates": "Browse templates",
    "cp.navDashboard": "Go to dashboard",
    "cp.navOperator": "Go to operator inbox",
    "cp.actSignin": "Sign in",
    "cp.actSignup": "Create account",
    "cp.actSigninOperator": "Sign in as operator",
    "cp.actNewProject": "Create new project",
    "cp.actSignout": "Sign out",
    "cp.actSwitchOperator": "Switch to operator",
    "cp.actTweaks": "Open Tweaks panel",
    "cp.actFeedback": "Send feedback",
    "cp.hintDemoOnly": "Demo only",
    "cp.hintDemo": "Demo",
    "cp.hintTweaks": "Theme, density",
    "cp.toastTweaks": "Use the floating Tweaks button in the bottom-right",
    "cp.toastFeedback": "Feedback — coming soon",
    // P387 — Account screen revoke-all flow (confirm dialog + 2
    // toasts). The DELETE-account flow is already i18n'd via
    // account.* keys; revokeAll was missed.
    "account.revokeAllConfirm": "Sign out of every device? You'll need to sign back in on this one too.",
    "account.revokeAllSuccess": "Signed out everywhere",
    "account.revokeAllError": "Could not revoke: {message}",
    // P532 — Pre-fix 4 error toasts in account.jsx (saveName,
    // revokeOne, toggleNotify, deleteAccount) shipped as raw
    // `${err.message}` — leaking server English ("status 500",
    // "Failed to fetch") with no localized context to a user
    // browsing the Russian or Uzbek UI. Now each error path has its
    // own localized prefix.
    "account.saveNameError": "Couldn't save name: {message}",
    "account.revokeOneError": "Couldn't sign out that session: {message}",
    "account.notifyToggleError": "Couldn't update notifications: {message}",
    "account.deleteAccountError": "Couldn't delete account: {message}",
    // P390 — Pricing CTA toasts. The 503 message comes from the
    // server (now localized via pickLang in checkout.js); this is
    // the local fallback when j.error is empty + the catch-block
    // error toast.
    "pricing.checkoutStub": "Online payments are coming soon — please contact us to start your Pro subscription",
    "pricing.checkoutError": "Couldn't start checkout: {message}",
    "pricing.studioToast": "Talk to us — coming soon",
    // P395 — Pro publish-request submit toasts (success / dedupe /
    // error). The publish flow is the highest-stakes user action;
    // had three English-only toasts at the very last step.
    "pub.requestQueued": "Sent · queue {id}",
    "pub.requestDedupe": "Already in queue · {id}",
    "pub.requestError": "Couldn't send: {message}",
    // P406 — Pro path middle steps: ProConfigureStep + ProCheckoutStep
    // chrome (field labels, placeholders, hints, errors, footer
    // buttons, summary lines, "how this works" notice). ADDONS labels
    // and per-card hints stay English for now — they are module-level
    // constants and need a runtime-localizing helper, separate iter.
    "pub.proCfgFieldLive": "Where should it live?",
    "pub.proCfgSubdomainAria": "Subdomain",
    "pub.proCfgBackupHint": "Always included as a backup URL.",
    "pub.proCfgCdPlaceholder": "Custom domain (e.g. camara.type) — optional",
    "pub.proCfgCdAria": "Custom domain",
    "pub.proCfgCdInvalid": "Use a domain like camara.type — letters, numbers, dashes, dots.",
    "pub.proCfgCdOk": "$8/mo · we'll handle DNS for you.",
    "pub.proCfgFieldAddons": "What backend bits do you need?",
    "pub.proCfgAddonsHint": "We hand-wire each one to your site. Cancel any time.",
    "pub.proCfgContinue": "Continue",
    "pub.proChkSummaryCustom": "Custom domain · {domain}",
    "pub.proChkSummarySub": "Subdomain · {sub}.forge.app",
    "pub.proChkMonthly": "Monthly",
    "pub.proChkFree": "free",
    "pub.proChkFieldEmail": "Where do we email you?",
    "pub.proChkEmailPlaceholder": "you@example.com",
    "pub.proChkEmailAria": "Contact email",
    "pub.proChkEmailInvalid": "That doesn't look like a valid email.",
    "pub.proChkEmailHint": "We confirm scope, then bill once it's live.",
    "pub.proChkNoticeKicker": "HOW THIS WORKS",
    "pub.proChkNoticeBody": "You're not charged yet. We review the brief, hand-wire the backend, and email you a working URL within 24 hours. First charge runs only when it's live.",
    "pub.proChkSending": "Sending…",
    "pub.proChkSend": "Send to our team",
    // P407 — ADDONS labels + hints. Resolved at render-time via t()
    // so the module-level ADDONS const stays the source of truth for
    // ids/prices.
    "pub.addonForm": "Contact form",
    "pub.addonFormHint": "Real form-to-email; spam filtering.",
    "pub.addonNewsletter": "Newsletter signup",
    "pub.addonNewsletterHint": "Synced to Mailchimp / Buttondown.",
    "pub.addonBooking": "Booking calendar",
    "pub.addonBookingHint": "Cal.com or your own slots.",
    "pub.addonOrdering": "Online ordering",
    "pub.addonOrderingHint": "Menu, cart, Payme / Click checkout.",
    "pub.addonPayments": "One-off payments",
    "pub.addonPaymentsHint": "Tip jar / pay-what-you-want / donations.",
    "pub.addonAccounts": "User accounts + login",
    "pub.addonAccountsHint": "Email + Google sign-in, dashboards.",
    // P408 — Project Settings ▸ General ▸ AI mode picker. 3 chips
    // (Premium / Hybrid / Economy) each with a one-liner note plus
    // the radiogroup aria-label, the field label, and the field hint.
    // Pre-fix the entire picker rendered English even with the
    // LangSwitcher set to RU/UZ.
    "settings.aiModeLabel": "AI mode",
    "settings.aiModeAria": "AI mode",
    "settings.aiModeHint": "Premium = Claude every turn. Hybrid = Claude for the first design pass, then a faster model. Economy = the faster model throughout.",
    "settings.aiModePremium": "Premium",
    "settings.aiModePremiumNote": "Claude only",
    "settings.aiModeHybrid": "Hybrid",
    "settings.aiModeHybridNote": "Claude → Kimi",
    "settings.aiModeEconomy": "Economy",
    "settings.aiModeEconomyNote": "Kimi only",
    // P409 — Project Settings modal title + tablist (radiogroup) labels.
    // Reachable from the dashboard kebab menu's Settings, the
    // builder's Settings, and the command palette. Tab IDs stay
    // English (general / domain / collaborators / danger) since the
    // UI keys off them.
    "settings.modalTitle": "Project settings",
    "settings.tabsAria": "Project settings sections",
    "settings.tabGeneral": "General",
    "settings.tabDomain": "Domain",
    "settings.tabPeople": "People",
    "settings.tabDanger": "Danger",
    // P410 — General tab fields: name + slug + description + swatch
    // labels/hints/errors + footer buttons. The aria-label= duplicates
    // of the visible MField label go away too — the input is already
    // associated via the asLabel htmlFor wiring, so the aria duplication
    // was just noise to the SR.
    "settings.fieldName": "Project name",
    "settings.fieldNameRequired": "Required.",
    "settings.fieldSlug": "Slug",
    "settings.fieldSlugHint": "Set when the project was created. Renaming the slug isn't supported yet — contact us if you need it.",
    "settings.fieldDesc": "Description / prompt",
    "settings.fieldDescHint": "The brief you gave Forge. Edits here re-anchor future generations.",
    "settings.fieldSwatch": "Thumbnail color",
    "settings.fieldSwatchHint": "Drives the dashboard card and the preview palette.",
    "settings.cancel": "Cancel",
    "settings.save": "Save changes",
    "settings.savedToast": "Saved — {name}",
    // P411 — Domain tab body: subdomain field + custom-domain field
    // (with placeholder, error, DNS instruction card) + Verify+Connect
    // button + queued toast. Subdomain aria-label dropped (asLabel
    // wires the visible label).
    "settings.domSubLabel": "forge.app subdomain",
    "settings.domSubHint": "Always available as a backup URL.",
    "settings.domCustomLabel": "Custom domain",
    "settings.domCustomHint": "Connect a domain you own. We'll handle DNS.",
    "settings.domCustomPlaceholder": "e.g. camara.type",
    "settings.domCustomInvalid": "Use a domain like camara.type.",
    "settings.domDnsKicker": "POINT YOUR DNS HERE",
    "settings.domDnsType": "Type",
    "settings.domDnsName": "Name",
    "settings.domDnsValue": "Value",
    // P543 — Visually-hidden caption for the DNS-records table.
    // Sighted users see the "POINT YOUR DNS HERE" kicker above; AT
    // users get the caption announced before the column headers so
    // they know what they're navigating before hearing
    // "Type / Name / Value".
    "settings.domDnsCaption": "DNS records to add at your domain registrar",
    "settings.domVerify": "Verify & connect",
    "settings.domQueuedToast": "Domain queued — DNS check starts now",
    // P412 — People tab body: members list (current single-user
    // self-row + Owner badge), Invite-by-email field + placeholder +
    // Send-invite button + coming-soon toast, Done button, fallback
    // labels for the self-row when auth.name/email aren't available.
    "settings.peopleMembers": "Workspace members",
    "settings.peopleYou": "You",
    "settings.peopleOwner": "Owner",
    "settings.peopleInviteLabel": "Invite by email",
    "settings.peopleInviteHint": "They'll get a magic link with view + edit access.",
    "settings.peopleInvitePlaceholder": "someone@example.com",
    "settings.peopleInviteSend": "Send invite",
    "settings.peopleInviteToast": "Invite sent — coming soon",
    "settings.peopleDone": "Done",
    // P413 — Danger tab body + the trash-flow runtime strings (confirm,
    // moved/restored toasts, Undo action). The dashboard's own kebab
    // → Trash flow has its own copy of these toasts already localized
    // via proj.* keys; this is the parallel set used from inside
    // the Settings modal's Danger tab.
    "settings.dangerHead": "Move to Trash",
    "settings.dangerBody": "The project disappears from your dashboard but stays recoverable from the Trash button for 30 days, then is permanently purged. Use Trash → Delete forever to remove it sooner.",
    "settings.dangerBtn": "Move {name} to Trash",
    "settings.dangerConfirm": "Move \"{name}\" to Trash? It'll wait there 30 days; restore from the dashboard's Trash button anytime.",
    "settings.dangerMovedToast": "Moved \"{name}\" to Trash",
    "settings.dangerRestoredToast": "Restored \"{name}\"",
    "settings.dangerUndo": "Undo",
    // P414 — ImageUploadModal: title + kicker + 3 tab labels + tablist
    // aria-label, the upload tab dropzone (icon stays as glyph; title +
    // hint), the URL tab (field label + hint + placeholder + invalid
    // error + preview alt + Cancel + Use button), and the post-pick
    // toast template. Tab IDs stay English.
    "img.title": "Add an image",
    "img.kicker": "ATTACH",
    "img.tabUpload": "Upload",
    "img.tabLibrary": "Library",
    "img.tabUrl": "From URL",
    "img.tabsAria": "Image source",
    "img.dropTitle": "Drop an image here",
    "img.dropHint": "or click to choose · jpg, png, webp · up to 8 MB",
    "img.urlLabel": "Image URL",
    "img.urlHint": "Paste a direct link to an image (jpg, png, webp).",
    "img.urlPlaceholder": "https://images.example.com/cover.jpg",
    "img.urlInvalid": "Needs to start with http:// or https://",
    "img.urlPreviewAlt": "Image preview",
    "img.urlCancel": "Cancel",
    "img.urlUse": "Use this image",
    "img.attachedToast": "Attached {name}",
    // P415 — ShareModal: title + kicker, 2 tab labels (Draft preview /
    // Live site) + tablist aria, "no live yet" empty-state copy, the
    // 2 link-tab field labels + hints + Copy button + copied toast,
    // permission picker (Anyone / Password-protected with PRO badge,
    // password placeholder), embed details summary, footer Revoke +
    // Done buttons + revoke/publish toasts.
    "share.title": "Share this project",
    "share.kicker": "SHARE",
    "share.tabPreview": "Draft preview",
    "share.tabLive": "Live site",
    "share.tabsAria": "Share which version",
    "share.noLiveBody": "This project hasn't been published yet.",
    "share.noLiveCta": "How to publish",
    "share.publishToast": "Open the Publish button to ship it",
    "share.previewLabel": "Preview link",
    "share.previewHint": "Live editing of the draft. Anyone with the link sees the latest version.",
    "share.liveLabel": "Live link",
    "share.liveHint": "Your published site.",
    "share.copyBtn": "Copy",
    "share.copiedToast": "Link copied",
    // P536 — Surfaced when navigator.clipboard.writeText rejects
    // (permission denied, API unavailable, transient failure).
    // Pre-fix the success toast fired even on rejection — silent
    // failure mode where the user thinks they copied something.
    "copy.failToast": "Couldn't copy — please copy manually",
    "share.permLabel": "Who can see it",
    "share.permAnyoneTitle": "Anyone with the link",
    "share.permAnyoneBody": "Open by default. No sign-in needed.",
    "share.permPwdTitle": "Password-protected",
    "share.permPwdBody": "Visitors enter a password before they see the site.",
    "share.permPwdPlaceholder": "Set a password",
    "share.embedSummary": "Embed in a page (iframe)",
    "share.revoke": "Revoke link",
    "share.revokedToast": "Link revoked",
    "share.done": "Done",
    // P416 — ExportModal: title + EXPORT kicker + lede + 3 option cards
    // (ZIP / GitHub / Handoff) each with a tag, title, body, CTA, and
    // a coming-soon toast. Tags (INSTANT / RECOMMENDED / TEAM) stay in
    // English brand-flavour caps.
    "export.title": "Export this project",
    "export.kicker": "EXPORT",
    "export.lede": "You own everything Forge builds. Take it with you any time.",
    "export.zipTitle": "Download a ZIP",
    "export.zipTag": "INSTANT",
    "export.zipBody": "The full Next.js + Tailwind project, ready to run with pnpm install.",
    "export.zipCta": "Download ZIP",
    "export.zipToast": "{name}.zip — coming soon",
    "export.ghTitle": "Push to GitHub",
    "export.ghTag": "RECOMMENDED",
    "export.ghBody": "Connect a repo. We push the latest version on every save.",
    "export.ghCta": "Connect GitHub",
    "export.ghToast": "GitHub OAuth — coming soon",
    "export.handoffTitle": "Hand off to a developer",
    "export.handoffTag": "TEAM",
    "export.handoffBody": "Generates a README, env template, and deploy notes alongside the source.",
    "export.handoffCta": "Generate handoff bundle",
    "export.handoffToast": "Handoff bundle — coming soon",
    // P417 — small batch of un-localized toasts the modals sweep
    // missed. Each one is reachable on its own code path:
    //   billing.cancelledToast — post-checkout cancel return
    //   dash.nameInTrash       — Create-new-project name collision
    //   tpl.nameInTrash        — Use-template name collision
    //   nf.bugReportToast      — NotFound page bug-report button
    "billing.cancelledToast": "Checkout cancelled — no charge made",
    "dash.nameInTrash": "A project with this name is in Trash. Restore it or pick a different name.",
    "tpl.nameInTrash": "A project with this name is in Trash. Restore it or rename the template.",
    "nf.bugReportToast": "Bug? Email hello@forge.app",
    // P418 — NotFound page surface (kicker / h1 / body / 2 buttons).
    // The 404 big-digit and `data-screen-label="404"` are kept literal.
    // The italicized "doesn't exist." emphasis flattens; same trade-off
    // as the P405 Showcase title — Cyrillic display italics don't
    // carry well at the scale.
    "nf.kicker": "404 · NOT FOUND",
    "nf.h1": "That page doesn't exist.",
    "nf.body": "Maybe it moved, or maybe it never did. Either way, the home page is where the prompt input lives.",
    "nf.btnHome": "Back to home",
    "nf.btnReport": "Report a bug",
    // P397 — /billing/success landing toasts (post-checkout return).
    // Two branches: authed-and-Pro success vs cookie-died-during-
    // checkout-redirect.
    "billing.successToast": "You're on Pro now — your operator will follow up shortly",
    "billing.confirmSignInToast": "Sign in to confirm your upgrade.",
    // P398 — Daily turn-budget exhausted toast (Free=30, Pro=200).
    // Reachable even on stub-only env since assertTurnBudget runs
    // regardless of LLM-key configuration. {plan} stays as a plan-
    // name identifier (Pro / Free).
    "chat.budgetExhausted": "You've used all {cap} {plan} chat turns for today — resets in about {mins}m.",
    // P399 — Templates page filter aria-label.
    "templates.filterAria": "Filter templates by category",
    // P400 — Lazy-screens loading placeholder. Brief — fades in
    // after 150ms — but visible enough to localize.
    "loading.screens": "Loading…",
    // P401 — Builder chat-stream-failed toast. Fires when the SSE
    // chat request fails (transport error / 5xx / pre-persist crash)
    // — distinct from the budget-exhausted path (P398) which has its
    // own message. {idSuffix} is the optional `(id X)` request-id
    // hint for support; the surrounding parenthesis lives in the
    // template so each locale formats it naturally.
    "chat.streamFailedToast": "Couldn't reach the AI{idSuffix} — falling back to a local reply.",
    "chat.streamFailedReqId": " (id {reqId})",
    "chat.retryAction": "Retry",
    // P402 — Builder offline-fallback chat replies (rendered as
    // assistant turns when send fails). Two paths: anon (no project
    // id) and authed-but-offline. Suggestion strings stay English
    // for now — "Sign up to save" is matched literally as an action
    // key in builder.jsx; refactoring to localized labels needs a
    // separate sentinel-based fix.
    "chat.offlineAnonText": "Got that. To keep going with real replies — and to save this conversation — sign up. Takes one email.",
    "chat.offlineAuthedText": "Got it — saving your prompt. The smart copilot will pick this up as soon as you're back online.",
    // P403 — Sign-up suggestion chip on the anon-fallback chat
    // reply. Special-cased in builder.jsx's onSuggestion handler
    // (routes to auth modal). Sentinel value `:action:signup`
    // identifies it across the wire; the renderer localizes the
    // visible chip text via this key.
    "chat.suggestSignup": "Sign up to save",
    // P371 — Landing hero starter chips. Different label set from
    // the dashboard chips (P354) — landing's are more compact +
    // marketing-funnel oriented. Same pattern: visible label flows
    // through useT, the prompt seed stays English.
    "landing.chipLanding": "Landing page",
    "landing.chipPortfolio": "Portfolio",
    "landing.chipDocs": "Docs",
    "landing.chipInvite": "Invite",
    "landing.chipStorefront": "Storefront",

    "lang.label": "Language",
    "greet.morning": "Good morning",
    "greet.afternoon": "Good afternoon",
    "greet.evening": "Good evening",
    "greet.night": "Working late",
    "dash.welcomeKicker": "WELCOME BACK",

    "pill.dashboard": "Dashboard",
    "pill.manageSubscription": "Manage subscription",
    "pill.upgradeToPro": "Upgrade to Pro",
    "pill.accountSettings": "Account settings",
    "pill.signOut": "Sign out",

    "account.title": "Account",
    "account.back": "← Back",
    "account.profileTitle": "Profile",
    "account.profileSubtitle": "How you show up across Forge.",
    "account.displayName": "Display name",
    "account.emailLabel": "Email",
    "account.emailHint": "Email is your sign-in identifier — it can't be changed yet. Contact support to migrate.",
    "account.memberSince": "Member since",
    "account.save": "Save changes",
    "account.saving": "Saving…",
    "account.discard": "Discard",
    "account.planTitle": "Plan",
    "account.planProSubtitle": "You're on Pro. Manage card, invoices, and cancel from the portal.",
    "account.planFreeSubtitle": "You're on Free. Upgrade for more active projects, custom domains, and hand-wired forms.",
    "account.dangerTitle": "Danger zone",
    "account.dangerSubtitle": "Destructive actions. There is no undo.",
    "account.deleteAccount": "Delete account",
    "account.deleteConfirmTitle": "This will permanently delete your account.",
    "account.deleteConfirmBody": "Every project, conversation, and notification will be erased. If you're on Pro, cancel the subscription first via Manage subscription.",
    "account.deleteTypeLabel": "Type \"delete\" to confirm",
    "account.deletePermanent": "Permanently delete",
    "account.deleting": "Deleting…",
    "account.cancel": "Cancel",
    "account.toastNameSaved": "Display name updated",
    "account.toastDeleted": "Account deleted",
    "account.renewsOn": "Renews on",
    "account.notifyTitle": "Notifications",
    "account.notifySubtitle": "Email updates when your project changes status.",
    "account.notifyToggle": "Email me when status changes",
    "account.notifyHint": "We'll email you on wiring start, go-live, and any blockers — alongside the in-app bell.",
    "account.activityTitle": "Recent activity",
    "account.activitySubtitle": "The last few times you signed in. If you see something you don't recognise, sign out everywhere.",
    "account.activityLoading": "Loading…",
    "account.activityEmpty": "No prior sign-ins on record yet.",
    "account.activityCurrent": "THIS DEVICE",
    "account.revokeAll": "Sign out everywhere",
    "account.revoking": "Signing out everywhere…",
    "account.revokeOne": "Revoke",
    "account.sessionRevoked": "Session revoked",
    "account.exportTitle": "Your data",
    "account.exportSubtitle": "Self-serve dump of everything we hold for your account.",
    "account.exportDownload": "Download as JSON",
    "account.exportHint": "Includes profile, every project (live + archived), all chat messages, notifications, sessions, and operator queue rows. Payment history lives with your provider (Payme or Click).",

    "err.title": "Something went sideways.",
    "err.body": "Your work is safe. The error has been reported and we'll look at it. Try again, or head back to the dashboard.",
    "err.tryAgain": "Try again",
    "err.backHome": "Back home",
  },

  ru: {
    "nav.how": "Как это работает",
    "nav.showcase": "Примеры",
    "nav.pricing": "Тарифы",
    "nav.faq": "Вопросы",
    "nav.templates": "Шаблоны",
    "nav.signIn": "Войти",
    "nav.startBuilding": "Начать",
    "nav.startFree": "Бесплатно",
    "nav.dashboard": "Кабинет →",
    "nav.mainAria": "Основная навигация",

    "hero.eyebrow": "Открытая бета · v0.6",
    "hero.headlineL1": "Опишите сайт.",
    "hero.headlineL2": "Смотрите, как он соберётся сам.",
    "hero.lede": "Forge превращает обычный текст в настоящий, готовый к публикации сайт — за секунды, а не недели.",
    "hero.promptLabel": "НОВЫЙ ПРОЕКТ",
    "hero.promptHint": "⏎ — запустить",
    "hero.cta": "Собрать",

    "showcase.kicker": "СДЕЛАНО НА FORGE",
    "showcase.title": "Что собрали на этой неделе.",
    "showcase.seeAll": "Все шаблоны →",
    "showcase.bigCtaTitle": "Ваша очередь. Что вы создаёте?",
    "showcase.bigCta": "Открыть редактор →",
    "showcase.builtIn": "собран за 38с",
    "showcase.openCta": "Открыть",
    "showcase.openAria": "Открыть превью «{title}»",
    "showcase.openToast": "Превью «{title}» — скоро",

    "faq.kicker": "ВОПРОСЫ",
    "faq.titleA": "Резонные",
    "faq.titleB": "сомнения.",
    "faq.q1": "Это настоящий код или скриншот?",
    "faq.a1": "Настоящий. Forge генерирует Next.js + Tailwind проект. Можно посмотреть любой файл, отредактировать в чате или передать репозиторий команде.",
    "faq.q2": "Кому принадлежит то, что собрал Forge?",
    "faq.a2": "Вам. Код, контент, деплой. Forge ни на что не претендует. Заберите репозиторий когда захотите.",
    "faq.q3": "Что если я не разработчик?",
    "faq.a3": "Не нужно читать ни строки кода. Говорите с Forge на обычном языке — описывайте, что хотите, указывайте на секции, и он отредактирует их.",
    "faq.q4": "Можно ли подключить свой бренд и компоненты?",
    "faq.a4": "Да. Закиньте Figma-файл, гайдлайн или пару скриншотов. Forge подхватит цвета, типографику и отступы и применит их во всём, что собирает.",

    "footer.product": "Продукт",
    "footer.company": "Компания",
    "footer.resources": "Ресурсы",
    "footer.madeWith": "Сделано на Forge",
    "footer.builder": "Конструктор",
    "footer.showcase": "Витрина",
    "footer.changelog": "Изменения",
    "footer.about": "О нас",
    "footer.blog": "Блог",
    "footer.careers": "Вакансии",
    "footer.press": "Пресса",
    "footer.docs": "Документация",
    "footer.privacy": "Конфиденциальность",
    "footer.terms": "Условия",
    "footer.copyright": "© 2026 Forge Labs, Inc.",
    "footer.allSystemsNormal": "Всё работает",
    "footer.allSystemsTooltip": "API · Edge · Auth",
    "footer.allSystemsToast": "Всё работает · API · Edge · Auth",

    "pricing.heroEyebrow": "ПРОСТЫЕ ТАРИФЫ · ОТМЕНА В ЛЮБОЙ МОМЕНТ",
    "pricing.heroL1": "Создавайте бесплатно.",
    "pricing.heroL2": "Платите, когда публикуете.",
    "pricing.heroLede": "Создавайте сколько угодно сайтов бесплатно. Платите только за публикацию на собственный домен — и ровно за то, что нужно.",
    // P448 — see EN block above.

    "templates.heroEyebrow": "ШАБЛОНЫ · СВОБОДНО МЕНЯТЬ",
    "templates.heroL1": "Пропустите пустую страницу.",
    "templates.heroL2": "Начните с настоящей.",
    "templates.heroLede": "Каждый шаблон — настоящий проект Forge. Выберите один и попросите Forge изменить что угодно: текст, палитру, разделы.",
    "templates.searchPlaceholder": "Найти шаблон по названию, типу или настроению…",
    "templates.scratchTitleA": "Не нашли подходящее?",
    "templates.scratchTitleB": "Опишите своими словами.",
    "templates.scratchCta": "Начать с нуля →",

    "auth.signInTitle": "С возвращением",
    "auth.signUpTitle": "Создайте аккаунт",
    "auth.sentTitle": "Проверьте почту",
    "auth.signInKicker": "ВХОД",
    "auth.signUpKicker": "РЕГИСТРАЦИЯ",
    "auth.sentKicker": "ССЫЛКА ОТПРАВЛЕНА",
    "auth.email": "Email",
    "auth.name": "Ваше имя",
    "auth.namePlaceholder": "Мадина Юсупова",
    "auth.emailPlaceholder": "you@example.com",
    "auth.magicLinkBtn": "Отправить ссылку на почту",
    "auth.magicLinkBtnSending": "Отправляем…",
    "auth.or": "ИЛИ",
    "auth.continueGoogle": "Войти через Google",
    "auth.continuePhone": "Войти по телефону",
    "auth.toggleToSignUp": "Впервые? Создать аккаунт",
    "auth.toggleToSignIn": "Уже есть аккаунт? Войти",
    // P450 — see EN block above.
    "auth.legalBefore": "Создавая аккаунт, вы соглашаетесь с ",
    "auth.legalTermsLabel": "Условиями",
    "auth.legalConjunction": " и ",
    "auth.legalPrivacyLabel": "Политикой конфиденциальности",
    "auth.legalAfter": ".",
    "auth.sentBodyBefore": "Мы отправили ссылку для входа на ",
    "auth.sentBodyAfter": ". Откройте письмо и перейдите по ссылке, чтобы завершить вход.",
    "auth.sentExpiry": "Ссылка действительна около 15 минут.",
    "auth.sentResend": "Отправить ещё раз",
    "auth.sentChangeEmail": "Использовать другую почту",
    "auth.sentPreviewKicker": "БЫСТРЫЙ ПЕРЕХОД",
    "auth.sentPreviewBody": "В этой среде почта не настроена — нажмите ниже, чтобы открыть ссылку напрямую.",
    "auth.sentPreviewCta": "Открыть ссылку",
    "auth.toastSignInFailed": "Не удалось войти",
    "auth.toastRateLimited": "Слишком быстро — попробуйте через минуту-две",
    "auth.toastResentSent": "Ссылка отправлена — проверьте почту",
    "auth.toastResent": "Ссылка отправлена ещё раз",
    "auth.toastResendFailed": "Не удалось отправить ссылку",
    "auth.toastGoogleComingSoon": "Google-вход скоро будет — пока используйте почту.",
    "auth.toastPhoneComingSoon": "SMS-подтверждение скоро будет",
    "notif.bell": "Уведомления",
    "notif.unreadAriaSuffix": "не прочитано: {n}",
    "notif.unreadTooltip": "новых: {n}",
    "notif.markAllRead": "Отметить все прочитанными",
    "notif.empty": "Всё прочитано.",
    "notif.wired.title": "Команда подключает {project}",
    "notif.wired.body": "Мы взяли ваш запрос в работу — настраиваем бэкенд.",
    "notif.live.title": "{project} запущен",
    "notif.live.bodyAt": "Доступен по {url}",
    "notif.live.bodyGeneric": "Ваш сайт заработал.",
    "notif.failed.title": "{project} требует внимания",
    "notif.failed.body": "Возникла загвоздка — мы связались с вами. Проверьте почту.",
    "notif.fallbackProject": "ваш проект",
    "project.untitled": "Без названия",
    "title.landing": "Forge — Сайты через диалог",
    "title.pricing": "Цены — Forge",
    "title.templates": "Шаблоны — Forge",
    "title.privacy": "Политика конфиденциальности — Forge",
    "title.terms": "Условия использования — Forge",
    "title.dashboard": "Ваши проекты — Forge",
    "title.builder": "Конструктор — Forge",
    "title.operator": "Очередь операторов — Forge",
    "title.account": "Аккаунт — Forge",
    "title.notfound": "Страница не найдена — Forge",
    "meta.descLanding": "Forge превращает разговор в настоящий сайт. Опишите, что вам нужно — посмотрите, как он появляется, и передайте бэкенд живому оператору. Для тех, кто не разработчик.",
    "meta.descPricing": "Три тарифа Forge: Free для одного сайта, Pro $19/мес для десяти, Studio для агентств. Отмена в любой момент, оплата ежемесячно через Payme или Click.",
    "meta.descTemplates": "Шаблоны Forge: портфолио, магазины, документация, лендинги, приглашения. Выберите как стартовый бриф для ИИ или начните с чистого промта.",
    "sort.label": "Сортировка",
    "sort.aria": "Сортировка проектов",
    "sort.recent": "Недавние",
    "sort.name": "Имя (А→Я)",
    "sort.edits": "Чаще редактируются",
    "search.placeholder": "Поиск проектов",
    "search.clear": "Очистить поиск",
    "search.clearTooltip": "Очистить (или /)",
    "search.openPalette": "Открыть палитру команд",
    "search.paletteTooltip": "Палитра команд",
    "dash.trashTitle": "Недавно удалённые",
    "dash.trashAria": "Недавно удалённые проекты",
    "dash.trashBtn": "Корзина",
    "newproj.kicker": "НОВЫЙ ПРОЕКТ",
    "newproj.placeholderPrefix": "Попробуйте: ",
    "newproj.aria": "Описание нового проекта",
    "newproj.hint": "Enter — собрать · Shift+Enter — новая строка",
    "newproj.button": "Собрать",
    "starter.landing": "Лендинг",
    "starter.portfolio": "Портфолио",
    "starter.docs": "Документация",
    "starter.booking": "Запись на приём",
    "starter.store": "Интернет-магазин",
    "timeAgo.justNow": "только что",
    "timeAgo.minutes": "{n} мин назад",
    "timeAgo.hours": "{n} ч назад",
    "timeAgo.days": "{n} дн назад",
    "timeAgo.weeks": "{n} нед назад",
    "timeAgo.months": "{n} мес назад",
    "timeAgo.years": "{n} г назад",
    "proj.statusLive": "Запущен",
    "proj.statusDraft": "Черновик",
    "stats.projectsLabel": "Проекты",
    "stats.liveLabel": "Запущены",
    "stats.draftsLabel": "Черновики",
    "stats.visitorsLabel": "Посетители",
    "stats.totalSingular": "всего: 1",
    "stats.totalPlural": "всего: {n}",
    "stats.publishing": "опубликованы",
    "stats.inProgress": "в работе",
    "stats.last30days": "за 30 дней",
    "filter.all": "Все",
    "filter.recent": "Недавние",
    "filter.aria": "Фильтр проектов",
    "dash.yourProjects": "ВАШИ ПРОЕКТЫ",
    "card.menu.rename": "Переименовать",
    "card.menu.duplicate": "Дублировать",
    "card.menu.archive": "В архив",
    "card.menu.delete": "Удалить",
    "card.menu.comingSoon": "{action} — скоро",
    "card.openCta": "Открыть",
    "card.delete.confirm": "Переместить «{name}» в Корзину? Хранится 30 дней — восстановить можно из Корзины в любой момент.",
    "card.delete.toast": "«{name}» перемещён в Корзину",
    "card.delete.undoLabel": "Отменить",
    "card.delete.restoredToast": "«{name}» восстановлен",
    "trash.title": "Недавно удалённые",
    "trash.kicker": "КОРЗИНА",
    "trash.loading": "Загружаем…",
    "trash.empty": "В корзине пусто. Удалённые проекты ждут здесь — их можно восстановить.",
    "trash.deletedPrefix": "Удалено ",
    "trash.restore": "Восстановить",
    "trash.deleteForever": "Удалить безвозвратно",
    "trash.purgeConfirm": "Удалить «{name}» навсегда? Это нельзя отменить.",
    "trash.deletedToast": "«{name}» удалён навсегда",
    "trash.purgeError": "Не удалось удалить: {reason}",
    "dash.brandBackTitle": "Назад на главную",
    "ws.switchAria": "Сменить рабочую область",
    "ws.personal": "Личное",
    "ws.menuAria": "Рабочие области",
    "ws.createTeam": "+ Создать командное пространство",
    "ws.teamsComingSoon": "Команды — скоро",
    "quota.freeCapHit": "достигнут лимит Free",
    "quota.proCapHit": "Pro · лимит {n}",
    "quota.activeSingular": "активный проект",
    "quota.activePlural": "активных проектов",
    "quota.upgradeBtn": "Перейти на Pro",
    "budget.outOfTurns": "лимит на сегодня исчерпан",
    "budget.dailyCapHit": "дневной лимит достигнут",
    "budget.turnsLeft": "осталось {n} на сегодня",
    "budget.turnsToday": "за сегодня",
    "budget.resetsInMinutes": "обновится через {n} мин",
    "budget.resetsInHours": "обновится через {n} ч",
    "composer.placeholderIdle": "Уточните, перенаправьте или попросите что-то новое…",
    "composer.placeholderStreaming": "ИИ печатает ответ…",
    "composer.aria": "Сообщение в чат",
    "composer.attach": "Прикрепить файл",
    "composer.addImage": "Добавить изображение",
    "composer.mention": "Упомянуть файл",
    "composer.attachComingSoon": "Вложения — скоро",
    "composer.attachStripAria": "Вложения",
    "composer.mentionComingSoon": "@-меню упоминаний — скоро",
    "composer.send": "Отправить",
    "builder.backAria": "Назад в дашборд",
    "builder.backTitle": "Назад",
    "builder.projectSettings": "Настройки проекта",
    "builder.switchBranch": "Сменить ветку",
    "builder.versionHistory": "История версий",
    "builder.editsLabel": "правок: {n}",
    "builder.shareBtn": "Поделиться",
    "builder.exportBtn": "Экспорт",
    "builder.publishBtn": "Опубликовать",
    "builder.savedSecondsAgo": "сохранено секунды назад",
    "builder.savedAMomentAgo": "сохранено только что",
    "builder.savedMinutesAgo": "сохранено {n} мин назад",
    "builder.savedHoursAgo": "сохранено {n} ч назад",
    "chat.conversation": "Беседа",
    "chat.promptsSingular": "1 запрос",
    "chat.promptsPlural": "запросов: {n}",
    "chat.typing": "Ассистент печатает",
    "chat.thinking": "Думает",
    "chat.wroteFiles": "ФАЙЛОВ · {n}",
    "chat.edited": "ИЗМЕНЕНО",
    "chat.systemNote": "СИСТЕМА",
    "work.tabPreview": "Превью",
    "work.tabHistory": "История",
    "work.refreshAria": "Обновить превью",
    "work.refreshTip": "Обновить превью",
    "work.openNewAria": "Открыть превью в новой вкладке",
    "work.openNewTip": "Открыть в новой вкладке",
    "work.popoutToast": "Открытие превью в отдельной вкладке появится после Publish — этап 2",
    "device.mobile": "Мобильный",
    "device.tablet": "Планшет",
    "device.desktop": "Десктоп",
    "chat.jumpLatest": "К последним",
    "work.copyUrlTip": "Скопировать URL",
    "work.copyUrlAria": "Скопировать URL превью: {url}",
    "work.copiedUrlToast": "Скопировано: {url}",
    "history.kindBuild": "СБОРКА",
    "history.kindEdit": "ПРАВКА",
    "history.filesTouched": "Файлов изменено: {n}",
    "history.restore": "Восстановить",
    "history.restoreToast": "Восстановление «{title}» — скоро",
    "dash.createError": "Не удалось создать проект: {message}",
    "tpl.startError": "Не удалось запустить шаблон: {message}",
    "notif.viewAll": "Вся активность",
    "notif.viewAllToast": "Лента активности — скоро",
    "proj.restoreError": "Не удалось восстановить — попробуйте через мгновение",
    "proj.restoreOverCap": "Восстановление превысит лимит проектов",
    "dash.emptyNoProjects": "Проектов пока нет",
    "dash.emptyNoProjectsBody": "Введите запрос выше — первый сайт соберётся меньше чем за минуту.",
    "dash.emptyNoMatch": "Ничего не нашлось",
    "dash.emptyNoMatchBody": "Попробуйте другой фильтр или очистите поиск.",
    "dash.emptyClearFilters": "Сбросить фильтры",
    "marquee.builtToday": "1 284 сайта собрано сегодня",
    "marquee.firstDeploy": "среднее время до публикации: 47 сек",
    "marquee.deploys": "публикуем на ваш домен",
    "marquee.stack": "под капотом — настоящий React + Tailwind",
    "marquee.editEnglish": "редактируйте обычным языком",
    "marquee.versions": "история версий встроена",
    "seed.thinking": "Готовлю проект «{tag}» по вашему брифу. Намечаю hero, основные секции и токены — чтобы палитра не плыла.",
    "seed.summary": "Готово. {name} собран. Что-то поправить?",
    "seed.suggestQuieter": "Сделать hero спокойнее",
    "seed.suggestAddForm": "Добавить форму обратной связи",
    "seed.suggestDarker": "Попробовать палитру потемнее",
    "seed.suggestMoreItems": "Показать 6 элементов вместо 3",
    "chat.suggestOpenPreview": "Открыть превью",
    "chat.suggestTryDifferent": "Попробовать другой бриф",
    "chat.suggestTryAgain": "Попробовать снова",
    "chat.suggestTalkHuman": "Связаться с человеком",
    "dash.greetFallbackName": "друг",
    "modal.close": "Закрыть",
    "modal.tabsFallback": "Вкладки",
    "heroPrompt.0": "портфолио шрифтового дизайнера из Лиссабона",
    "heroPrompt.1": "одностраничник про подписку на закваску, тепло и по-домашнему",
    "heroPrompt.2": "сайт документации для моего opensource-rate-limiter'а",
    "heroPrompt.3": "лендинг AI-планировщика встреч, тёмная тема",
    "heroPrompt.4": "приглашение на свадьбу с фотографиями и RSVP-формой",
    "dashExample.clinic": "сайт стоматологической клиники в Ташкенте, спокойно и чисто",
    "dashExample.fridaySourdough": "одностраничник про нашу пятничную подписку на закваску",
    "dashExample.weddingShort": "сайт-приглашение на свадьбу, фото и RSVP",
    "pub.kickerProConfigure": "PRO-ПУБЛИКАЦИЯ",
    "pub.titleProConfigure": "Настройте домен и бэкенд",
    "pub.kickerProCheckout": "ОПЛАТА",
    "pub.titleProCheckout": "Подтвердите план",
    "pub.upgradeHint": "С Quick на Pro можно перейти в любое время.",
    "cp.footNavigate": "перемещение",
    "cp.footSelect": "выбрать",
    "cp.footToggle": "открыть/закрыть",
    "templates.catAll": "Все",
    "templates.catLanding": "Лендинг",
    "templates.catPortfolio": "Портфолио",
    "templates.catStorefront": "Магазин",
    "templates.catBooking": "Бронирование",
    "templates.catRestaurant": "Ресторан",
    "templates.catDocs": "Документация",
    "templates.catWedding": "Свадьба",
    "templates.catNewsletter": "Рассылка",
    "templates.catResume": "Резюме",
    "tpl.aiLaunch": "AI лендинг",
    "tpl.agencyLanding": "Студия / агентство",
    "tpl.saasLanding": "SaaS продуктивность",
    "tpl.typePortfolio": "Шрифтовой дизайнер",
    "tpl.photoPortfolio": "Фотограф",
    "tpl.archPortfolio": "Архитектор",
    "tpl.bakery": "Пекарня",
    "tpl.fashionShop": "Модный дроп",
    "tpl.dental": "Стоматология",
    "tpl.salon": "Парикмахерская",
    "tpl.restaurant": "Меню ресторана",
    "tpl.docsSite": "OSS-документация",
    "tpl.wedding": "Свадебное приглашение",
    "tpl.newsletter": "Рассылка",
    "tpl.resume": "Резюме / CV",
    "templates.countOne": "{n} шаблон",
    "templates.countFew": "{n} шаблона",
    "templates.countMany": "{n} шаблонов",
    "templates.emptyTitle": "Шаблонов не нашлось.",
    "templates.emptyBody": "Попробуйте другую категорию или ключевое слово.",
    "templates.useAria": "Использовать шаблон «{name}»",
    "templates.useThis": "Использовать",
    "templates.footerBack": "На главную",
    "templates.footerPricing": "Тарифы",
    "notif.unreadAria": "Непрочитанное",
    "pricing.currentPlan": "Текущий тариф",
    "pricing.tierFootnote": "Цены в USD. Бэкенд-аддоны на Studio оплачиваются по интеграции · отменить можно в любой момент.",
    "pricing.entKicker": "АГЕНТСТВА И КОМАНДЫ",
    "pricing.entTitleA": "Запускаете десять клиентских сайтов?",
    "pricing.entTitleB": "Давайте поговорим.",
    "pricing.entBody": "Скидки за объём, выделенные рабочие пространства, white-label, и человек на нашей стороне, который знает ваши сайты в лицо.",
    "pricing.entCta": "Связаться с нами",
    "pricing.entToast": "Связь с нами — скоро",
    "pricing.faqKicker": "ВОПРОСЫ",
    "pricing.faqTitleA": "Обычные",
    "pricing.faqTitleB": "сомнения.",
    "pricing.faqQ1": "Что значит «настраиваем вручную»?",
    "pricing.faqA1": "Forge собирает фронт автоматически. Бэкенд — формы, которые реально отправляют письма, бронирования с синхронизацией календаря, онлайн-платежи, которые реально списывают — настраивает живой человек (мы) для каждого сайта. На Pro — лёгкая настройка; на Studio — полный комплект.",
    "pricing.faqQ2": "Спишут ли с меня деньги до запуска сайта?",
    "pricing.faqA2": "Нет. Первый месяц списывается только после того, как сайт станет публичным. Если отмените до запуска — ничего не платите.",
    "pricing.faqQ3": "Можно ли поменять тариф?",
    "pricing.faqA3": "Вверх или вниз, в любое время. Пропорционально дням. Отменить тоже можно в любой момент — сайты будут жить до конца оплаченного периода.",
    "pricing.faqQ4": "А если мне станет мало Studio?",
    "pricing.faqA4": "Делаем индивидуальные условия для агентств и команд. Жмите «Связаться с нами» ниже — рассчитаем по объёму и нужным интеграциям.",
    "pricing.cmpKicker": "СРАВНЕНИЕ",
    "pricing.cmpTitleA": "Каждое отличие,",
    "pricing.cmpTitleB": "бок о бок.",
    "pricing.cmpFeatHeader": "Возможность",
    "pricing.cmpCaption": "Сравнение функций тарифов",
    "pricing.cmpGroupBuild": "Создание",
    "pricing.cmpGroupPublish": "Публикация",
    "pricing.cmpGroupService": "Сервис",
    "pricing.cmpFeatAi": "AI-конструктор",
    "pricing.cmpFeatPreview": "Живой предпросмотр",
    "pricing.cmpFeatVersions": "История версий",
    "pricing.cmpFeatTemplates": "Шаблоны",
    "pricing.cmpFeatSites": "Опубликовано сайтов",
    "pricing.cmpFeatSubdomain": "Поддомен forge.app",
    "pricing.cmpFeatCustomDomains": "Свои домены",
    "pricing.cmpFeatContactForm": "Форма обратной связи",
    "pricing.cmpFeatNewsletter": "Подписка на рассылку",
    "pricing.cmpFeatBooking": "Календарь записей",
    "pricing.cmpFeatOrdering": "Онлайн-заказы / оплата",
    "pricing.cmpFeatAccounts": "Личные кабинеты",
    "pricing.cmpFeatBackend": "Бэкенд под ключ от нас",
    "pricing.cmpFeatTurnaround": "Срок настройки",
    "pricing.cmpFeatSupport": "Поддержка",
    "pricing.cmpFeatCancel": "Отмена в любое время",
    "pricing.cmpVal.unlimited": "Без ограничений",
    "pricing.cmpVal.7days": "7 дней",
    "pricing.cmpVal.30days": "30 дней",
    "pricing.cmpVal.forever": "Бессрочно",
    "pricing.cmpVal.public": "Публичные",
    "pricing.cmpVal.all": "Все",
    "pricing.cmpVal.allBespoke": "Все + индивидуальные",
    "pricing.cmpVal.upTo3": "До 3-х",
    "pricing.cmpVal.light": "Базовый",
    "pricing.cmpVal.full": "Полный",
    "pricing.cmpVal.community": "Сообщество",
    "pricing.cmpVal.email1bd": "Email · 1 рабочий день",
    "pricing.cmpVal.prioritySlack": "Приоритет + Slack",
    "pricing.tierFree.tag": "ПОПРОБОВАТЬ",
    "pricing.tierFree.cadence": "навсегда",
    "pricing.tierFree.blurb": "Создавайте сколько угодно. Опубликуйте один сайт на поддомене forge.app.",
    "pricing.tierFree.cta": "Начать бесплатно",
    "pricing.tierFree.feat1": "1 активный сайт (черновик или опубликованный)",
    "pricing.tierFree.feat2": "Живой предпросмотр",
    "pricing.tierFree.feat3": "Публикация на поддомене forge.app",
    "pricing.tierFree.feat4": "Только статика — без форм, входа и платежей",
    "pricing.tierFree.feat5": "Поддержка сообщества",
    "pricing.tierPro.tag": "ВЫБИРАЮТ ЧАЩЕ",
    "pricing.tierPro.cadence": "в месяц",
    "pricing.tierPro.blurb": "Для соло-основателей, выпускающих брендированные сайты с рабочей формой обратной связи.",
    "pricing.tierPro.cta": "Получить Pro",
    "pricing.tierPro.feat1": "Всё из Free",
    "pricing.tierPro.feat2": "До 10 активных сайтов",
    "pricing.tierPro.feat3": "До 3 собственных доменов",
    "pricing.tierPro.feat4": "Подключённая форма и рассылка",
    "pricing.tierPro.feat5": "Edge-аналитика",
    "pricing.tierPro.feat6": "Email-поддержка · 1 рабочий день",
    "pricing.tierStudio.tag": "АГЕНТСТВАМ И БУТИКАМ",
    "pricing.tierStudio.cadence": "в месяц",
    "pricing.tierStudio.blurb": "Когда бэкенд — это продукт. Брони, заказы, платежи, аккаунты — всё под ключ.",
    "pricing.tierStudio.cta": "Связаться",
    "pricing.tierStudio.feat1": "Всё из Pro",
    "pricing.tierStudio.feat2": "Неограниченные собственные домены",
    "pricing.tierStudio.feat3": "Брони, заказы, платежи, аккаунты",
    "pricing.tierStudio.feat4": "Подключаем каждую интеграцию вручную",
    "pricing.tierStudio.feat5": "Приоритетная поддержка · Slack-канал",
    "card.kebabAria": "Действия для «{name}»",
    "card.kebabTip": "Ещё",
    "card.menuAria": "Действия для «{name}»",
    "card.openAria": "Открыть «{name}»",
    "composer.removeAttachAria": "Удалить «{name}»",
    "builder.mobileGateTitleA": "Forge удобнее",
    "builder.mobileGateTitleB": "на широком экране.",
    "builder.mobileGateBody": "Билдеру нужно место и для чата, и для живого превью. Откройте этот же проект на планшете или ноутбуке — всё ваше будет на месте.",
    "operator.gateTitleA": "Этот раздел —",
    "operator.gateTitleB": "только для команды.",
    "operator.gateBody": "Войдите как оператор Forge, чтобы попасть в очередь бэкенда.",
    "operator.gateBack": "Назад",
    "operator.gateSignIn": "Войти как оператор",
    "auth.emailInvalid": "Это не похоже на корректный email.",
    "auth.nameTooShort": "Минимум 2 символа.",
    "legal.draftBanner": "ЧЕРНОВИК — это заглушки. Замените на согласованный текст до запуска.",
    "legal.lastUpdated": "Обновлено: {date} (черновик)",
    "legal.crumb.privacy": "Политика конфиденциальности",
    "legal.crumb.terms": "Условия использования",
    "legal.subtitle.privacy": "Что мы собираем, зачем и чего никогда не делаем с этим.",
    "legal.subtitle.terms": "Прозрачное соглашение между вами и Forge.",
    "legal.footnoteBefore": "Вопросы? Напишите на ",
    "legal.footnoteAfter": ".",
    "a11y.skipToContent": "Перейти к содержимому",
    "builder.signupCta": "Зарегистрироваться",
    "builder.signupCtaTip": "Сохранить эту переписку в вашем аккаунте",
    "how.kicker": "КАК ЭТО РАБОТАЕТ",
    "how.titleA": "Четыре шага.",
    "how.titleB": "Примерно сорок секунд.",
    "how.step1Title": "Расскажите Forge, что вам нужно",
    "how.step1Body": "Одного предложения достаточно. Тон, аудитория, разделы — или просто общая идея. Forge доделает остальное.",
    "how.step2Title": "Смотрите, как это рисуется в реальном времени",
    "how.step2Body": "Страницы, компоненты, тексты и работающее превью отрисовываются рядом. Это не скриншот — это настоящий код.",
    "how.step3Title": "Правьте, продолжая разговор",
    "how.step3Body": "Уточняйте в том же чате. Переставляйте секции, меняйте палитры, перекраивайте раскладки. Forge ведёт diff — ничего не теряется.",
    "how.step4Title": "Опубликуйте на свой домен",
    "how.step4Body": "Один клик — и сайт по реальному URL. Подключите свой домен, заберите репо или продолжайте править в Forge.",
    "ua.unknownDevice": "Неизвестное устройство",
    "ua.on": "на",
    "auth.fallbackName": "Друг",
    "auth.welcome": "Добро пожаловать, {name}",
    "auth.signedOut": "Вы вышли",
    "auth.sessionExpired": "Сессия истекла — войдите, чтобы продолжить",
    "auth.sessionExpiredAction": "Войти",
    "auth.signInAsOperator": "Войдите с операторской почты — мы перекинем вас в очередь.",
    "pill.menuAria": "Меню аккаунта",
    "pill.subscriptionStub": "Самостоятельное управление подпиской скоро — свяжитесь с нами, чтобы изменить план.",
    "pill.portalError": "Не удалось открыть портал: {message}",
    "pub.kickerIntro": "ПУБЛИКАЦИЯ",
    "pub.titleIntro": "Как опубликовать?",
    "pub.introLede": "Статичный сайт на поддомене forge.app — бесплатно. Нужен свой домен или настоящий бэкенд? Подключим вручную.",
    "pub.quickTag": "БЕСПЛАТНО · СРАЗУ",
    "pub.quickTitle": "Быстрая публикация",
    "pub.quickBodyBefore": "Опубликуется за секунды по адресу ",
    "pub.quickBodyAfter": ". Только статика — без форм, входа и платежей.",
    "pub.quickCta": "Опубликовать бесплатно",
    "pub.proTag": "PRO · МЫ НАСТРОИМ",
    "pub.proTitle": "Pro-публикация",
    "pub.proBody": "Свой домен и бэкенд (формы, бронирования, платежи, аккаунты). Настраиваем вручную в течение 24 часов.",
    "pub.proCta": "Настроить",
    "pub.kickerQuick": "БЫСТРАЯ ПУБЛИКАЦИЯ",
    "pub.titleQuick": "Выберите адрес forge.app",
    "pub.subdomainLabel": "Субдомен",
    "pub.subdomainTaken": "Занят — попробуйте другой.",
    "pub.subdomainAvailable": "Доступен.",
    "pub.subdomainHelp": "2–48 символов, маленькие латинские буквы / цифры / дефисы; без дефиса в начале или конце.",
    "pub.includedLabel": "Что входит",
    "pub.includedSsl": "Бесплатный SSL на эдже",
    "pub.includedPush": "Мгновенная публикация",
    "pub.includedRepublish": "Можно перепубликовать в любой момент",
    "pub.includedUpgrade": "Формы, логин, платежи — апгрейд позже",
    "pub.back": "Назад",
    "pub.publishTo": "Опубликовать на {url}",
    "pub.kickerDeploy": "ПУБЛИКАЦИЯ",
    "pub.titleDeploy": "Запускаем…",
    "pub.kickerLive": "ОПУБЛИКОВАНО",
    "pub.titleLive": "Готово — сайт онлайн.",
    "pub.deployStageProvision": "Создаём субдомен forge.app",
    "pub.deployStageUpload": "Загружаем статический бандл",
    "pub.deployStageWarm": "Прогреваем эдж-кэш",
    "pub.deployStageTls": "Проверяем TLS-сертификат",
    "pub.deployStageLive": "Готово",
    "pub.deployProgressAria": "Прогресс публикации",
    "pub.deployProgressValueText": "{n} процентов — {label}",
    "pub.livePill": "Онлайн · только что",
    "pub.liveVisit": "Открыть",
    "pub.liveCopy": "Скопировать ссылку",
    "pub.linkCopiedToast": "Ссылка скопирована",
    "pub.liveLede": "Сайт виден всем по ссылке. Нужны свой домен или рабочая форма? Перейдите на Pro из меню проекта.",
    "pub.liveBackToProject": "Назад к проекту",
    "pub.kickerSent": "ЗАЯВКА ОТПРАВЛЕНА",
    "pub.titleSent": "Мы свяжемся с вами.",
    "pub.sentInQueue": "В очереди",
    "pub.sentBig": "Вернёмся к вам в течение 24 часов.",
    "pub.sentBodyBefore": "Подтверждение отправлено на ",
    "pub.sentBodyAfter": ". Ответьте, если что-то нужно уточнить.",
    "pub.summaryPending": "План в ожидании",
    "pub.summaryFirstCharge": "Первое списание — после публикации.",
    "pub.summaryCancelAnytime": "Отмена в любой момент.",
    "compare.yes": "Да",
    "compare.no": "Нет",
    "cp.dialogAria": "Палитра команд",
    "cp.searchPlaceholder": "Команда или поиск…",
    "cp.searchAria": "Введите команду или поиск",
    "cp.listAria": "Команды",
    "cp.noMatches": "Ничего не найдено по запросу ",
    "cp.groupNavigate": "Навигация",
    "cp.groupProjects": "Проекты",
    "cp.groupActions": "Действия",
    "cp.navLanding": "На главную",
    "cp.navPricing": "К ценам",
    "cp.navTemplates": "Шаблоны",
    "cp.navDashboard": "К дашборду",
    "cp.navOperator": "Очередь операторов",
    "cp.actSignin": "Войти",
    "cp.actSignup": "Создать аккаунт",
    "cp.actSigninOperator": "Войти как оператор",
    "cp.actNewProject": "Новый проект",
    "cp.actSignout": "Выйти",
    "cp.actSwitchOperator": "Переключиться в оператора",
    "cp.actTweaks": "Панель тонкой настройки",
    "cp.actFeedback": "Отправить отзыв",
    "cp.hintDemoOnly": "Только демо",
    "cp.hintDemo": "Демо",
    "cp.hintTweaks": "Тема, плотность",
    "cp.toastTweaks": "Используйте плавающую кнопку Tweaks справа внизу",
    "cp.toastFeedback": "Отзывы — скоро",
    "account.revokeAllConfirm": "Выйти на всех устройствах? Придётся войти заново и здесь.",
    "account.revokeAllSuccess": "Вы вышли со всех устройств",
    "account.revokeAllError": "Не удалось выйти: {message}",
    "account.saveNameError": "Не удалось сохранить имя: {message}",
    "account.revokeOneError": "Не удалось выйти из сессии: {message}",
    "account.notifyToggleError": "Не удалось обновить уведомления: {message}",
    "account.deleteAccountError": "Не удалось удалить аккаунт: {message}",
    "pricing.checkoutStub": "Онлайн-оплата скоро — свяжитесь с нами, чтобы оформить Pro-подписку",
    "pricing.checkoutError": "Не удалось начать оплату: {message}",
    "pricing.studioToast": "Свяжемся с вами — скоро",
    "pub.requestQueued": "Заявка отправлена · номер {id}",
    "pub.requestDedupe": "Уже в очереди · номер {id}",
    "pub.requestError": "Не удалось отправить: {message}",
    "pub.proCfgFieldLive": "Где разместим сайт?",
    "pub.proCfgSubdomainAria": "Поддомен",
    "pub.proCfgBackupHint": "Всегда включён как резервный URL.",
    "pub.proCfgCdPlaceholder": "Свой домен (например camara.type) — по желанию",
    "pub.proCfgCdAria": "Свой домен",
    "pub.proCfgCdInvalid": "Введите домен вида camara.type — буквы, цифры, дефисы, точки.",
    "pub.proCfgCdOk": "$8/мес · DNS настроим мы.",
    "pub.proCfgFieldAddons": "Какие бэкенд-функции нужны?",
    "pub.proCfgAddonsHint": "Каждую подключаем вручную. Отменить можно в любой момент.",
    "pub.proCfgContinue": "Продолжить",
    "pub.proChkSummaryCustom": "Свой домен · {domain}",
    "pub.proChkSummarySub": "Поддомен · {sub}.forge.app",
    "pub.proChkMonthly": "В месяц",
    "pub.proChkFree": "бесплатно",
    "pub.proChkFieldEmail": "Куда писать вам?",
    "pub.proChkEmailPlaceholder": "you@example.com",
    "pub.proChkEmailAria": "Email для связи",
    "pub.proChkEmailInvalid": "Это не похоже на корректный email.",
    "pub.proChkEmailHint": "Согласуем объём, спишем после запуска.",
    "pub.proChkNoticeKicker": "КАК ЭТО РАБОТАЕТ",
    "pub.proChkNoticeBody": "Сейчас денег не списываем. Мы проверим бриф, вручную подключим бэкенд и пришлём рабочий URL в течение 24 часов. Первое списание — только после запуска.",
    "pub.proChkSending": "Отправляем…",
    "pub.proChkSend": "Отправить нашей команде",
    "pub.addonForm": "Форма обратной связи",
    "pub.addonFormHint": "Настоящая форма с отправкой на email; антиспам.",
    "pub.addonNewsletter": "Подписка на рассылку",
    "pub.addonNewsletterHint": "Синхронизация с Mailchimp / Buttondown.",
    "pub.addonBooking": "Календарь бронирования",
    "pub.addonBookingHint": "Cal.com или ваши собственные слоты.",
    "pub.addonOrdering": "Онлайн-заказы",
    "pub.addonOrderingHint": "Меню, корзина, оплата через Payme / Click.",
    "pub.addonPayments": "Разовые платежи",
    "pub.addonPaymentsHint": "Чаевые / pay-what-you-want / донаты.",
    "pub.addonAccounts": "Аккаунты пользователей + вход",
    "pub.addonAccountsHint": "Email и Google-вход, личные кабинеты.",
    "settings.aiModeLabel": "Режим ИИ",
    "settings.aiModeAria": "Режим ИИ",
    "settings.aiModeHint": "Premium — Claude на каждом ходе. Hybrid — Claude для первого дизайн-прохода, дальше более быстрая модель. Economy — быстрая модель на всех ходах.",
    "settings.aiModePremium": "Premium",
    "settings.aiModePremiumNote": "Только Claude",
    "settings.aiModeHybrid": "Hybrid",
    "settings.aiModeHybridNote": "Claude → Kimi",
    "settings.aiModeEconomy": "Economy",
    "settings.aiModeEconomyNote": "Только Kimi",
    "settings.modalTitle": "Настройки проекта",
    "settings.tabsAria": "Разделы настроек проекта",
    "settings.tabGeneral": "Общие",
    "settings.tabDomain": "Домен",
    "settings.tabPeople": "Команда",
    "settings.tabDanger": "Опасная зона",
    "settings.fieldName": "Название проекта",
    "settings.fieldNameRequired": "Обязательное поле.",
    "settings.fieldSlug": "Слаг",
    "settings.fieldSlugHint": "Задаётся при создании проекта. Пока нельзя переименовать — напишите нам, если нужно.",
    "settings.fieldDesc": "Описание / бриф",
    "settings.fieldDescHint": "Бриф, который вы дали Forge. Правки тут переориентируют будущие генерации.",
    "settings.fieldSwatch": "Цвет миниатюры",
    "settings.fieldSwatchHint": "Используется в карточке на дашборде и в палитре превью.",
    "settings.cancel": "Отмена",
    "settings.save": "Сохранить изменения",
    "settings.savedToast": "Сохранено — {name}",
    "settings.domSubLabel": "Поддомен forge.app",
    "settings.domSubHint": "Всегда доступен как резервный URL.",
    "settings.domCustomLabel": "Свой домен",
    "settings.domCustomHint": "Подключите свой домен — DNS настроим мы.",
    "settings.domCustomPlaceholder": "например camara.type",
    "settings.domCustomInvalid": "Введите домен вида camara.type.",
    "settings.domDnsKicker": "НАСТРОЙТЕ DNS НА ЭТИ ЗАПИСИ",
    "settings.domDnsType": "Тип",
    "settings.domDnsName": "Имя",
    "settings.domDnsValue": "Значение",
    "settings.domDnsCaption": "DNS-записи для регистратора домена",
    "settings.domVerify": "Проверить и подключить",
    "settings.domQueuedToast": "Домен в очереди — проверка DNS начата",
    "settings.peopleMembers": "Участники рабочей области",
    "settings.peopleYou": "Вы",
    "settings.peopleOwner": "Владелец",
    "settings.peopleInviteLabel": "Пригласить по email",
    "settings.peopleInviteHint": "Получит magic-ссылку с правами на просмотр и редактирование.",
    "settings.peopleInvitePlaceholder": "someone@example.com",
    "settings.peopleInviteSend": "Отправить приглашение",
    "settings.peopleInviteToast": "Приглашение отправлено — скоро",
    "settings.peopleDone": "Готово",
    "settings.dangerHead": "Переместить в Корзину",
    "settings.dangerBody": "Проект исчезнет с дашборда, но 30 дней доступен в Корзине — потом удаляется навсегда. Через Корзина → Удалить навсегда можно убрать сразу.",
    "settings.dangerBtn": "Переместить {name} в Корзину",
    "settings.dangerConfirm": "Переместить «{name}» в Корзину? Будет лежать там 30 дней; восстановить можно через кнопку Корзина на дашборде.",
    "settings.dangerMovedToast": "«{name}» перемещён в Корзину",
    "settings.dangerRestoredToast": "«{name}» восстановлен",
    "settings.dangerUndo": "Отмена",
    "img.title": "Добавить изображение",
    "img.kicker": "ВЛОЖЕНИЕ",
    "img.tabUpload": "Загрузка",
    "img.tabLibrary": "Библиотека",
    "img.tabUrl": "По URL",
    "img.tabsAria": "Источник изображения",
    "img.dropTitle": "Перетащите изображение сюда",
    "img.dropHint": "или нажмите, чтобы выбрать · jpg, png, webp · до 8 МБ",
    "img.urlLabel": "URL изображения",
    "img.urlHint": "Вставьте прямую ссылку на изображение (jpg, png, webp).",
    "img.urlPlaceholder": "https://images.example.com/cover.jpg",
    "img.urlInvalid": "Должен начинаться с http:// или https://",
    "img.urlPreviewAlt": "Превью изображения",
    "img.urlCancel": "Отмена",
    "img.urlUse": "Использовать",
    "img.attachedToast": "Прикреплено: {name}",
    "share.title": "Поделиться проектом",
    "share.kicker": "ПОДЕЛИТЬСЯ",
    "share.tabPreview": "Превью черновика",
    "share.tabLive": "Опубликованный",
    "share.tabsAria": "Какая версия",
    "share.noLiveBody": "Этот проект ещё не опубликован.",
    "share.noLiveCta": "Как опубликовать",
    "share.publishToast": "Откройте кнопку Опубликовать, чтобы запустить",
    "share.previewLabel": "Ссылка на превью",
    "share.previewHint": "Живое редактирование черновика. Все, у кого есть ссылка, видят последнюю версию.",
    "share.liveLabel": "Ссылка на сайт",
    "share.liveHint": "Ваш опубликованный сайт.",
    "share.copyBtn": "Копировать",
    "share.copiedToast": "Ссылка скопирована",
    "copy.failToast": "Не удалось скопировать — скопируйте вручную",
    "share.permLabel": "Кому видно",
    "share.permAnyoneTitle": "Любой по ссылке",
    "share.permAnyoneBody": "Открыто по умолчанию. Вход не требуется.",
    "share.permPwdTitle": "С паролем",
    "share.permPwdBody": "Посетители вводят пароль, чтобы увидеть сайт.",
    "share.permPwdPlaceholder": "Задайте пароль",
    "share.embedSummary": "Вставка на страницу (iframe)",
    "share.revoke": "Отозвать ссылку",
    "share.revokedToast": "Ссылка отозвана",
    "share.done": "Готово",
    "export.title": "Экспорт проекта",
    "export.kicker": "ЭКСПОРТ",
    "export.lede": "Всё, что создаёт Forge — ваше. Заберите в любой момент.",
    "export.zipTitle": "Скачать ZIP",
    "export.zipTag": "СРАЗУ",
    "export.zipBody": "Полный проект Next.js + Tailwind, запускается через pnpm install.",
    "export.zipCta": "Скачать ZIP",
    "export.zipToast": "{name}.zip — скоро",
    "export.ghTitle": "Запушить в GitHub",
    "export.ghTag": "РЕКОМЕНДУЕМ",
    "export.ghBody": "Подключите репо. Мы пушим последнюю версию при каждом сохранении.",
    "export.ghCta": "Подключить GitHub",
    "export.ghToast": "GitHub OAuth — скоро",
    "export.handoffTitle": "Передать разработчику",
    "export.handoffTag": "КОМАНДА",
    "export.handoffBody": "Готовит README, шаблон env и заметки по деплою вместе с исходниками.",
    "export.handoffCta": "Собрать пакет передачи",
    "export.handoffToast": "Пакет передачи — скоро",
    "billing.cancelledToast": "Оплата отменена — списания не было",
    "dash.nameInTrash": "Проект с таким именем в Корзине. Восстановите его или выберите другое имя.",
    "tpl.nameInTrash": "Проект с таким именем в Корзине. Восстановите его или переименуйте шаблон.",
    "nf.bugReportToast": "Баг? Напишите на hello@forge.app",
    "nf.kicker": "404 · НЕ НАЙДЕНО",
    "nf.h1": "Такой страницы нет.",
    "nf.body": "Может, она переехала, а может, её никогда и не было. В любом случае главная — там, где живёт поле ввода.",
    "nf.btnHome": "На главную",
    "nf.btnReport": "Сообщить о баге",
    "billing.successToast": "Pro активирован — наш оператор скоро свяжется с вами",
    "billing.confirmSignInToast": "Войдите, чтобы подтвердить апгрейд.",
    "chat.budgetExhausted": "Вы использовали все {cap} ходов чата на сегодня ({plan}) — обновление через ~{mins} мин.",
    "templates.filterAria": "Фильтр шаблонов по категории",
    "loading.screens": "Загружаем…",
    "chat.streamFailedToast": "Не удалось связаться с ИИ{idSuffix} — переходим на локальный ответ.",
    "chat.streamFailedReqId": " (id {reqId})",
    "chat.retryAction": "Повторить",
    "chat.offlineAnonText": "Понял. Чтобы продолжить с настоящими ответами и сохранить эту беседу — зарегистрируйтесь. Нужен только email.",
    "chat.offlineAuthedText": "Принято — сохраняем ваш запрос. Умный сопилот подхватит, как только вернётся связь.",
    "chat.suggestSignup": "Зарегистрироваться, чтобы сохранить",
    "landing.chipLanding": "Лендинг",
    "landing.chipPortfolio": "Портфолио",
    "landing.chipDocs": "Документация",
    "landing.chipInvite": "Приглашение",
    "landing.chipStorefront": "Магазин",

    "lang.label": "Язык",
    "greet.morning": "Доброе утро",
    "greet.afternoon": "Добрый день",
    "greet.evening": "Добрый вечер",
    "greet.night": "Поздно работаешь",
    "dash.welcomeKicker": "С ВОЗВРАЩЕНИЕМ",

    "pill.dashboard": "Кабинет",
    "pill.manageSubscription": "Управление подпиской",
    "pill.upgradeToPro": "Перейти на Pro",
    "pill.accountSettings": "Настройки аккаунта",
    "pill.signOut": "Выйти",

    "account.title": "Аккаунт",
    "account.back": "← Назад",
    "account.profileTitle": "Профиль",
    "account.profileSubtitle": "Как вы выглядите в Forge.",
    "account.displayName": "Отображаемое имя",
    "account.emailLabel": "Email",
    "account.emailHint": "Email — это ваш идентификатор для входа, его пока нельзя изменить. Напишите в поддержку для переноса.",
    "account.memberSince": "С нами с",
    "account.save": "Сохранить",
    "account.saving": "Сохраняем…",
    "account.discard": "Отменить",
    "account.planTitle": "Тариф",
    "account.planProSubtitle": "У вас тариф Pro. Карта, счета и отмена — в портале.",
    "account.planFreeSubtitle": "У вас бесплатный тариф. Перейдите на Pro для большего числа проектов, своих доменов и готовых форм.",
    "account.dangerTitle": "Опасная зона",
    "account.dangerSubtitle": "Необратимые действия. Отменить нельзя.",
    "account.deleteAccount": "Удалить аккаунт",
    "account.deleteConfirmTitle": "Аккаунт будет удалён навсегда.",
    "account.deleteConfirmBody": "Все проекты, переписки и уведомления исчезнут. Если у вас Pro — сначала отмените подписку в \"Управление подпиской\".",
    "account.deleteTypeLabel": "Введите \"delete\" для подтверждения",
    "account.deletePermanent": "Удалить навсегда",
    "account.deleting": "Удаляем…",
    "account.cancel": "Отмена",
    "account.toastNameSaved": "Имя обновлено",
    "account.toastDeleted": "Аккаунт удалён",
    "account.renewsOn": "Продлевается",
    "account.notifyTitle": "Уведомления",
    "account.notifySubtitle": "Email-сообщения при смене статуса вашего проекта.",
    "account.notifyToggle": "Сообщать на email при смене статуса",
    "account.notifyHint": "Мы напишем при старте сборки, при публикации и если что-то сломается — параллельно с уведомлениями в приложении.",
    "account.activityTitle": "Недавняя активность",
    "account.activitySubtitle": "Последние входы в аккаунт. Если что-то не узнаёте — выйдите со всех устройств.",
    "account.activityLoading": "Загрузка…",
    "account.activityEmpty": "Пока нет данных о входах.",
    "account.activityCurrent": "ЭТО УСТРОЙСТВО",
    "account.revokeAll": "Выйти со всех устройств",
    "account.revoking": "Выходим…",
    "account.revokeOne": "Отозвать",
    "account.sessionRevoked": "Сессия отозвана",
    "account.exportTitle": "Ваши данные",
    "account.exportSubtitle": "Самостоятельная выгрузка всего, что мы храним по вашему аккаунту.",
    "account.exportDownload": "Скачать JSON",
    "account.exportHint": "Включает профиль, все проекты (активные и удалённые), переписки, уведомления, сессии и заявки на подключение бэкенда. История платежей — у вашего провайдера (Payme или Click).",

    "err.title": "Что-то пошло не так.",
    "err.body": "Ваша работа в безопасности. Ошибка отправлена нам — мы посмотрим. Попробуйте ещё раз или вернитесь на главную.",
    "err.tryAgain": "Попробовать ещё раз",
    "err.backHome": "На главную",
  },

  uz: {
    "nav.how": "Qanday ishlaydi",
    "nav.showcase": "Misollar",
    "nav.pricing": "Narxlar",
    "nav.faq": "Savollar",
    "nav.templates": "Andozalar",
    "nav.signIn": "Kirish",
    "nav.startBuilding": "Boshlash",
    "nav.startFree": "Bepul boshlash",
    "nav.dashboard": "Kabinet →",
    "nav.mainAria": "Asosiy navigatsiya",

    "hero.eyebrow": "Ochiq beta · v0.6",
    "hero.headlineL1": "Saytni tasvirlab bering.",
    "hero.headlineL2": "Qanday yasalishini kuzating.",
    "hero.lede": "Forge oddiy so'zlarni haqiqiy, nashr qilishga tayyor saytga aylantiradi — daqiqalarda, oylar emas.",
    "hero.promptLabel": "YANGI LOYIHA",
    "hero.promptHint": "Yasash uchun ⏎",
    "hero.cta": "Yasash",

    "showcase.kicker": "FORGE'DA YASALGAN",
    "showcase.title": "Bu hafta yasalganlardan.",
    "showcase.seeAll": "Barcha andozalar →",
    "showcase.bigCtaTitle": "Sizning navbatingiz. Nima yasayapsiz?",
    "showcase.bigCta": "Tahrirlovchini ochish →",
    "showcase.builtIn": "38 sek ichida yasaldi",
    "showcase.openCta": "Ochish",
    "showcase.openAria": "{title} preview'ini ochish",
    "showcase.openToast": "“{title}” preview — tez orada",

    "faq.kicker": "SAVOLLAR",
    "faq.titleA": "Asosli",
    "faq.titleB": "shubhalar.",
    "faq.q1": "Bu haqiqiy kodmi yoki ekran tasviri?",
    "faq.a1": "Haqiqiy. Forge Next.js + Tailwind loyihasini yaratadi. Har bir faylni ko'rishingiz, chatda tahrir qilishingiz yoki jamoangizga repozitoriyni topshirishingiz mumkin.",
    "faq.q2": "Forge yaratgan narsa kimga tegishli?",
    "faq.a2": "Sizga. Kod, mazmun, deploy. Forge hech narsaga da'vo qilmaydi. Repozitoriyni xohlaganingizda olib keting.",
    "faq.q3": "Men dasturchi bo'lmasamchi?",
    "faq.a3": "Bir qator ham kod o'qish shart emas. Forge bilan oddiy tilda gaplashing — nima istasangiz tasvirlab bering, qismlarga ishora qiling, u tahrirlaydi.",
    "faq.q4": "O'z brendim va komponentlarimni keltira olamanmi?",
    "faq.a4": "Ha. Figma fayli, uslub qo'llanmasi yoki bir nechta skrinshotni tashlang. Forge ranglar, tipografika va oraliqlarni olib, yaratayotgan narsalarda qo'llaydi.",

    "footer.product": "Mahsulot",
    "footer.builder": "Konstruktor",
    "footer.showcase": "Vitrina",
    "footer.changelog": "Yangilanishlar",
    "footer.about": "Biz haqimizda",
    "footer.blog": "Blog",
    "footer.careers": "Ish o'rinlari",
    "footer.press": "Matbuot",
    "footer.docs": "Hujjatlar",
    "footer.privacy": "Maxfiylik",
    "footer.terms": "Shartlar",
    "footer.copyright": "© 2026 Forge Labs, Inc.",
    "footer.allSystemsNormal": "Hammasi ishlamoqda",
    "footer.allSystemsTooltip": "API · Edge · Auth",
    "footer.allSystemsToast": "Hammasi ishlamoqda · API · Edge · Auth",
    "footer.company": "Kompaniya",
    "footer.resources": "Manbalar",
    "footer.madeWith": "Forge'da yasalgan",

    "pricing.heroEyebrow": "ODDIY NARXLAR · ISTALGAN VAQT BEKOR QILING",
    "pricing.heroL1": "Bepul yarating.",
    "pricing.heroL2": "Nashr qilganingizda to'lang.",
    "pricing.heroLede": "Hech qanday haq to'lamasdan istagancha sayt yarating. Faqat haqiqiy domenga nashr qilganingizda, va faqat kerakli funksiyalar uchun to'lang.",
    // P448 — see EN block above.

    "templates.heroEyebrow": "ANDOZALAR · ERKIN O'ZGARTIRISH",
    "templates.heroL1": "Bo'sh sahifani tashlang.",
    "templates.heroL2": "Haqiqiysidan boshlang.",
    "templates.heroLede": "Har bir andoza — haqiqiy Forge loyihasi. Birini tanlang, keyin Forge'ga matn, palitra, bo'limlarni o'zgartirishni so'rang.",
    "templates.searchPlaceholder": "Nomi, turi yoki kayfiyati bo'yicha qidiring…",
    "templates.scratchTitleA": "Mosini topa olmadingizmi?",
    "templates.scratchTitleB": "O'z so'zlaringiz bilan tasvirlang.",
    "templates.scratchCta": "Noldan boshlash →",

    "auth.signInTitle": "Xush kelibsiz",
    "auth.signUpTitle": "Hisob yarating",
    "auth.sentTitle": "Pochtangizni tekshiring",
    "auth.signInKicker": "KIRISH",
    "auth.signUpKicker": "RO'YXATDAN O'TISH",
    "auth.sentKicker": "HAVOLA YUBORILDI",
    "auth.email": "Email",
    "auth.name": "Ismingiz",
    "auth.namePlaceholder": "Madina Yusupova",
    "auth.emailPlaceholder": "siz@misol.com",
    "auth.magicLinkBtn": "Sehrli havola yuborish",
    "auth.magicLinkBtnSending": "Yuborilmoqda…",
    "auth.or": "YOKI",
    "auth.continueGoogle": "Google orqali davom etish",
    "auth.continuePhone": "Telefon orqali davom etish",
    "auth.toggleToSignUp": "Forge'da yangimisiz? Hisob yarating",
    "auth.toggleToSignIn": "Hisobingiz bormi? Kirish",
    // P450 — see EN block above.
    "auth.legalBefore": "Hisob yaratish orqali ",
    "auth.legalTermsLabel": "Foydalanish shartlari",
    "auth.legalConjunction": " va ",
    "auth.legalPrivacyLabel": "Maxfiylik siyosatiga",
    "auth.legalAfter": " rozilik bildirasiz.",
    "auth.sentBodyBefore": "Sehrli havolani ",
    "auth.sentBodyAfter": " manziliga yubordik. Kirishni yakunlash uchun pochtangizdagi havolaga o'ting.",
    "auth.sentExpiry": "Havola taxminan 15 daqiqa ichida amal qiladi.",
    "auth.sentResend": "Qayta yuborish",
    "auth.sentChangeEmail": "Boshqa email ishlatish",
    "auth.sentPreviewKicker": "TEZ KIRISH",
    "auth.sentPreviewBody": "Bu muhitda email sozlanmagan — havolani to'g'ridan-to'g'ri ochish uchun pastdagi tugmani bosing.",
    "auth.sentPreviewCta": "Sehrli havolani ochish",
    "auth.toastSignInFailed": "Kirish amalga oshmadi",
    "auth.toastRateLimited": "Sekinroq — bir-ikki daqiqadan so'ng qayta urinib ko'ring",
    "auth.toastResentSent": "Havola qayta yuborildi — pochtangizni tekshiring",
    "auth.toastResent": "Havola qayta yuborildi",
    "auth.toastResendFailed": "Havolani qayta yuborib bo'lmadi",
    "auth.toastGoogleComingSoon": "Google orqali kirish tez orada — hozircha email ishlating.",
    "auth.toastPhoneComingSoon": "SMS-tasdiqlash tez orada",
    "notif.bell": "Bildirishnomalar",
    "notif.unreadAriaSuffix": "o'qilmagan: {n}",
    "notif.unreadTooltip": "yangi: {n}",
    "notif.markAllRead": "Hammasini o'qilgan deb belgilash",
    "notif.empty": "Yangi bildirishnomalar yo'q.",
    "notif.wired.title": "Jamoa {project} uchun backendni ulamoqda",
    "notif.wired.body": "So'rovingizni qabul qildik va backendni sozlashni boshladik.",
    "notif.live.title": "{project} ishga tushdi",
    "notif.live.bodyAt": "Manzil: {url}",
    "notif.live.bodyGeneric": "Saytingiz ishga tushdi.",
    "notif.failed.title": "{project} bilan muammo",
    "notif.failed.body": "Bir muammoga duch keldik va siz bilan bog'landik. Pochtangizni tekshiring.",
    "notif.fallbackProject": "loyihangiz",
    "project.untitled": "Nomsiz",
    "title.landing": "Forge — Suhbat orqali sayt yarating",
    "title.pricing": "Narxlar — Forge",
    "title.templates": "Shablonlar — Forge",
    "title.privacy": "Maxfiylik siyosati — Forge",
    "title.terms": "Foydalanish shartlari — Forge",
    "title.dashboard": "Loyihalaringiz — Forge",
    "title.builder": "Konstruktor — Forge",
    "title.operator": "Operator navbati — Forge",
    "title.account": "Hisob — Forge",
    "title.notfound": "Sahifa topilmadi — Forge",
    "meta.descLanding": "Forge suhbatni haqiqiy saytga aylantiradi. Nima xohlayotganingizni ayting, paydo bo'lishini ko'ring, keyin backendni inson operatorga topshiring. Texnik bo'lmaganlar uchun.",
    "meta.descPricing": "Forge'ning uch tarifi: Free bitta sayt uchun, Pro o'nta uchun $19/oy, Studio agentliklar uchun. Istalgan vaqtda bekor qilish, Payme yoki Click orqali oylik to'lov.",
    "meta.descTemplates": "Forge boshlang'ich shablonlari: portfoliolar, do'konlar, hujjatlar, lendinglar, taklifnomalar. AI-saytingiz uchun urug' sifatida tanlang yoki bo'sh promptdan boshlang.",
    "sort.label": "Saralash",
    "sort.aria": "Loyihalarni saralash",
    "sort.recent": "So'nggi",
    "sort.name": "Nom (A→Z)",
    "sort.edits": "Ko'p tahrirlangan",
    "search.placeholder": "Loyihalarni qidirish",
    "search.clear": "Qidiruvni tozalash",
    "search.clearTooltip": "Tozalash (yoki /)",
    "search.openPalette": "Komanda palitrasini ochish",
    "search.paletteTooltip": "Komanda palitrasi",
    "dash.trashTitle": "Yaqinda o'chirilgan",
    "dash.trashAria": "Yaqinda o'chirilgan loyihalar",
    "dash.trashBtn": "Savat",
    "newproj.kicker": "YANGI LOYIHA",
    "newproj.placeholderPrefix": "Sinab ko'ring: ",
    "newproj.aria": "Yangi loyiha tavsifi",
    "newproj.hint": "Enter — yaratish · Shift+Enter — yangi qator",
    "newproj.button": "Yaratish",
    "starter.landing": "Lending sahifa",
    "starter.portfolio": "Portfolio",
    "starter.docs": "Hujjatlar sayti",
    "starter.booking": "Bron qilish sahifasi",
    "starter.store": "Onlayn do'kon",
    "timeAgo.justNow": "hozirgina",
    "timeAgo.minutes": "{n} daqiqa oldin",
    "timeAgo.hours": "{n} soat oldin",
    "timeAgo.days": "{n} kun oldin",
    "timeAgo.weeks": "{n} hafta oldin",
    "timeAgo.months": "{n} oy oldin",
    "timeAgo.years": "{n} yil oldin",
    "proj.statusLive": "Ishga tushgan",
    "proj.statusDraft": "Qoralama",
    "stats.projectsLabel": "Loyihalar",
    "stats.liveLabel": "Ishga tushgan",
    "stats.draftsLabel": "Qoralamalar",
    "stats.visitorsLabel": "Tashrif buyuruvchilar",
    "stats.totalSingular": "jami: 1",
    "stats.totalPlural": "jami: {n}",
    "stats.publishing": "nashr qilingan",
    "stats.inProgress": "ishlayapti",
    "stats.last30days": "30 kun ichida",
    "filter.all": "Hammasi",
    "filter.recent": "So'nggi",
    "filter.aria": "Loyihalar filtri",
    "dash.yourProjects": "LOYIHALARINGIZ",
    "card.menu.rename": "Qayta nomlash",
    "card.menu.duplicate": "Nusxa olish",
    "card.menu.archive": "Arxivga",
    "card.menu.delete": "O'chirish",
    "card.menu.comingSoon": "{action} — tez orada",
    "card.openCta": "Ochish",
    "card.delete.confirm": "\"{name}\" Savatga ko'chirilsinmi? 30 kun saqlanadi — istalgan vaqtda Savatdan tiklash mumkin.",
    "card.delete.toast": "\"{name}\" Savatga ko'chirildi",
    "card.delete.undoLabel": "Bekor qilish",
    "card.delete.restoredToast": "\"{name}\" tiklandi",
    "trash.title": "Yaqinda o'chirilgan",
    "trash.kicker": "SAVAT",
    "trash.loading": "Yuklanmoqda…",
    "trash.empty": "Savat bo'sh. O'chirilgan loyihalar shu yerda saqlanadi — istalgan vaqtda tiklash mumkin.",
    "trash.deletedPrefix": "O'chirilgan: ",
    "trash.restore": "Tiklash",
    "trash.deleteForever": "Butunlay o'chirish",
    "trash.purgeConfirm": "\"{name}\" butunlay o'chirilsinmi? Bu amalni bekor qilib bo'lmaydi.",
    "trash.deletedToast": "\"{name}\" butunlay o'chirildi",
    "trash.purgeError": "O'chirib bo'lmadi: {reason}",
    "dash.brandBackTitle": "Bosh sahifaga qaytish",
    "ws.switchAria": "Ish maydonini almashtirish",
    "ws.personal": "Shaxsiy",
    "ws.menuAria": "Ish maydonlari",
    "ws.createTeam": "+ Jamoa ish maydoni yaratish",
    "ws.teamsComingSoon": "Jamoalar — tez orada",
    "quota.freeCapHit": "Free chegarasiga yetdingiz",
    "quota.proCapHit": "Pro · {n} chegara",
    "quota.activeSingular": "faol loyiha",
    "quota.activePlural": "faol loyiha",
    "quota.upgradeBtn": "Pro'ga o'tish",
    "budget.outOfTurns": "bugungi limit tugadi",
    "budget.dailyCapHit": "kunlik limit tugadi",
    "budget.turnsLeft": "bugungi qoldiq: {n}",
    "budget.turnsToday": "bugun",
    "budget.resetsInMinutes": "{n} daqiqada yangilanadi",
    "budget.resetsInHours": "{n} soatda yangilanadi",
    "composer.placeholderIdle": "Aniqlashtiring, qayta yo'naltiring yoki yangi narsa so'rang…",
    "composer.placeholderStreaming": "AI javobini yozmoqda…",
    "composer.aria": "Chat xabari",
    "composer.attach": "Fayl biriktirish",
    "composer.addImage": "Rasm qo'shish",
    "composer.mention": "Faylni eslab o'tish",
    "composer.attachComingSoon": "Fayl biriktirish — tez orada",
    "composer.attachStripAria": "Biriktirilganlar",
    "composer.mentionComingSoon": "@-menyu — tez orada",
    "composer.send": "Yuborish",
    "builder.backAria": "Boshqaruv paneliga qaytish",
    "builder.backTitle": "Orqaga",
    "builder.projectSettings": "Loyiha sozlamalari",
    "builder.switchBranch": "Tarmoqni almashtirish",
    "builder.versionHistory": "Versiyalar tarixi",
    "builder.editsLabel": "{n} ta tahrir",
    "builder.shareBtn": "Ulashish",
    "builder.exportBtn": "Eksport",
    "builder.publishBtn": "Nashr qilish",
    "builder.savedSecondsAgo": "soniyalar oldin saqlandi",
    "builder.savedAMomentAgo": "hozirgina saqlandi",
    "builder.savedMinutesAgo": "{n} daqiqa oldin saqlandi",
    "builder.savedHoursAgo": "{n} soat oldin saqlandi",
    "chat.conversation": "Suhbat",
    "chat.promptsSingular": "1 ta so'rov",
    "chat.promptsPlural": "{n} ta so'rov",
    "chat.typing": "Yordamchi yozmoqda",
    "chat.thinking": "O'ylayapti",
    "chat.wroteFiles": "{n} TA FAYL",
    "chat.edited": "TAHRIRLANGAN",
    "chat.systemNote": "TIZIM XABARI",
    "work.tabPreview": "Ko'rib chiqish",
    "work.tabHistory": "Tarix",
    "work.refreshAria": "Ko'rib chiqishni yangilash",
    "work.refreshTip": "Ko'rib chiqishni yangilash",
    "work.openNewAria": "Ko'rib chiqishni yangi tabda ochish",
    "work.openNewTip": "Yangi tabda ochish",
    "work.popoutToast": "Pop-out ko'rib chiqish E'lon — 2-bosqichdan keyin paydo bo'ladi",
    "device.mobile": "Mobil",
    "device.tablet": "Planshet",
    "device.desktop": "Desktop",
    "chat.jumpLatest": "Eng so'nggiga",
    "work.copyUrlTip": "URL'ni nusxalash",
    "work.copyUrlAria": "Ko'rib chiqish URL'ini nusxalash: {url}",
    "work.copiedUrlToast": "Nusxalandi: {url}",
    "history.kindBuild": "QURISH",
    "history.kindEdit": "TAHRIR",
    "history.filesTouched": "{n} ta fayl o'zgartirildi",
    "history.restore": "Tiklash",
    "history.restoreToast": "«{title}»'ni tiklash — tez orada",
    "dash.createError": "Loyihani yaratib bo'lmadi: {message}",
    "tpl.startError": "Shablonni ishga tushirib bo'lmadi: {message}",
    "notif.viewAll": "Barcha faollik",
    "notif.viewAllToast": "Faollik tasmasi — tez orada",
    "proj.restoreError": "Tiklab bo'lmadi — bir lahzadan keyin urinib ko'ring",
    "proj.restoreOverCap": "Tiklash loyiha limitidan oshib ketadi",
    "dash.emptyNoProjects": "Hozircha loyihalar yo'q",
    "dash.emptyNoProjectsBody": "Yuqorida prompt kiriting — birinchi saytingiz bir daqiqada quriladi.",
    "dash.emptyNoMatch": "Hech narsa topilmadi",
    "dash.emptyNoMatchBody": "Boshqa filtr sinab ko'ring yoki qidiruvni tozalang.",
    "dash.emptyClearFilters": "Filtrlarni tozalash",
    "marquee.builtToday": "Bugun 1 284 ta sayt yasaldi",
    "marquee.firstDeploy": "birinchi nashrgacha o'rtacha vaqt: 47 soniya",
    "marquee.deploys": "domeningizga nashr qilamiz",
    "marquee.stack": "ostida — haqiqiy React + Tailwind",
    "marquee.editEnglish": "oddiy til bilan tahrirlang",
    "marquee.versions": "versiya tarixi ichiga o'rnatilgan",
    "seed.thinking": "Brifingiz bo'yicha «{tag}» loyihani tayyorlamoqdaman. Hero, asosiy bo'limlar va palitra bir xil bo'lishi uchun tokenlar faylini chizyapman.",
    "seed.summary": "Tayyor. {name} yaratildi. Biror narsani sayqallash kerakmi?",
    "seed.suggestQuieter": "Hero'ni jimroq qilish",
    "seed.suggestAddForm": "Aloqa formasi qo'shish",
    "seed.suggestDarker": "To'qroq palitrani sinab ko'rish",
    "seed.suggestMoreItems": "3 emas, 6 ta element ko'rsatish",
    "chat.suggestOpenPreview": "Oldindan ko'rishni ochish",
    "chat.suggestTryDifferent": "Boshqa brifni sinab ko'rish",
    "chat.suggestTryAgain": "Qayta urinib ko'rish",
    "chat.suggestTalkHuman": "Inson bilan bog'lanish",
    "dash.greetFallbackName": "do'st",
    "modal.close": "Yopish",
    "modal.tabsFallback": "Tablar",
    "heroPrompt.0": "Lissabondagi shrift dizayneri portfolioi",
    "heroPrompt.1": "xachitan obunasi uchun bir sahifali sayt, iliq va uy sharoitida",
    "heroPrompt.2": "ochiq kodli rate-limiter'im uchun hujjatlar sayti",
    "heroPrompt.3": "AI uchrashuv rejalashtiruvchi uchun lending, qorong'u tema",
    "heroPrompt.4": "to'y taklifnomasi sayti, fotosuratlar va RSVP formasi bilan",
    "dashExample.clinic": "Toshkentdagi stomatologik klinika sayti, sokin va toza",
    "dashExample.fridaySourdough": "juma kungi xachitan obunamiz uchun bir sahifali sayt",
    "dashExample.weddingShort": "to'y taklifnomasi sayti, fotosurat va RSVP bilan",
    "pub.kickerProConfigure": "PRO NASHR",
    "pub.titleProConfigure": "Domen va backendni sozlang",
    "pub.kickerProCheckout": "TO'LOV",
    "pub.titleProCheckout": "Rejani tasdiqlang",
    "pub.upgradeHint": "Quick'dan Pro'ga istalgan vaqtda o'tish mumkin.",
    "cp.footNavigate": "harakatlanish",
    "cp.footSelect": "tanlash",
    "cp.footToggle": "ochish/yopish",
    "templates.catAll": "Barchasi",
    "templates.catLanding": "Lending",
    "templates.catPortfolio": "Portfolio",
    "templates.catStorefront": "Do'kon",
    "templates.catBooking": "Bron",
    "templates.catRestaurant": "Restoran",
    "templates.catDocs": "Hujjatlar",
    "templates.catWedding": "To'y",
    "templates.catNewsletter": "Yangiliklar",
    "templates.catResume": "Rezyume",
    "tpl.aiLaunch": "AI lending",
    "tpl.agencyLanding": "Studiya / agentlik",
    "tpl.saasLanding": "SaaS samaradorlik",
    "tpl.typePortfolio": "Shrift dizayneri",
    "tpl.photoPortfolio": "Fotograf",
    "tpl.archPortfolio": "Arxitektor",
    "tpl.bakery": "Non do'koni",
    "tpl.fashionShop": "Moda kolleksiyasi",
    "tpl.dental": "Stomatologiya",
    "tpl.salon": "Sartaroshxona",
    "tpl.restaurant": "Restoran menyusi",
    "tpl.docsSite": "OSS hujjatlari",
    "tpl.wedding": "To'y taklifnomasi",
    "tpl.newsletter": "Yangiliklar",
    "tpl.resume": "Rezyume / CV",
    "templates.countOne": "{n} ta shablon",
    "templates.countFew": "{n} ta shablon",
    "templates.countMany": "{n} ta shablon",
    "templates.emptyTitle": "Mos shablon topilmadi.",
    "templates.emptyBody": "Boshqa kategoriya yoki kalit so'zni sinab ko'ring.",
    "templates.useAria": "«{name}» shablonidan foydalanish",
    "templates.useThis": "Foydalanish",
    "templates.footerBack": "Bosh sahifaga",
    "templates.footerPricing": "Tariflar",
    "notif.unreadAria": "O'qilmagan",
    "pricing.currentPlan": "Joriy tarif",
    "pricing.tierFootnote": "Narxlar USD'da. Studio'dagi backend qo'shimchalari integratsiyaga qarab hisoblanadi · istalgan vaqtda bekor qilish mumkin.",
    "pricing.entKicker": "AGENTLIKLAR VA JAMOALAR",
    "pricing.entTitleA": "O'nta mijoz uchun sayt qilyapsizmi?",
    "pricing.entTitleB": "Gaplashaylik.",
    "pricing.entBody": "Hajmga ko'ra chegirmalar, alohida ish makonlari, white-label imkoniyatlari va saytlaringizni nomma-nom biladigan biz tomondagi inson.",
    "pricing.entCta": "Biz bilan bog'lanish",
    "pricing.entToast": "Biz bilan bog'lanish — tez orada",
    "pricing.faqKicker": "SAVOLLAR",
    "pricing.faqTitleA": "Odatdagi",
    "pricing.faqTitleB": "shubhalar.",
    "pricing.faqQ1": "«Qo'lda ulaymiz» nimani anglatadi?",
    "pricing.faqA1": "Forge frontendni avtomatik yaratadi. Backend qismlari — haqiqatdan email yuboradigan formalar, kalendar bilan sinxron bron, haqiqatdan to'lov oladigan onlayn-to'lovlar — har bir sayt uchun jonli odam (biz) tomonidan sozlanadi. Pro'da yengil sozlash; Studio'da to'liq paket.",
    "pricing.faqQ2": "Sayt ishga tushishidan oldin pul olinadimi?",
    "pricing.faqA2": "Yo'q. Birinchi oylik sayt ishga tushgandan keyingina hisoblanadi. Ishga tushishdan oldin bekor qilsangiz — hech narsa to'lamaysiz.",
    "pricing.faqQ3": "Tarifni keyinchalik o'zgartira olamanmi?",
    "pricing.faqA3": "Yuqoriga yoki pastga, istalgan vaqtda. Kunlarga qarab proporsional. Bekor qilish ham — istalgan vaqtda; saytlar to'lov muddati oxirigacha ishlaydi.",
    "pricing.faqQ4": "Studio'dan o'zib ketsam-chi?",
    "pricing.faqA4": "Agentlik va jamoalar uchun individual shartlar tuzamiz. Quyida «Biz bilan bog'lanish»ni bosing — hajm va kerakli integratsiyalarga qarab narx beramiz.",
    "pricing.cmpKicker": "TAQQOSLASH",
    "pricing.cmpTitleA": "Har bir farq,",
    "pricing.cmpTitleB": "yonma-yon.",
    "pricing.cmpFeatHeader": "Imkoniyat",
    "pricing.cmpCaption": "Tarif imkoniyatlari taqqoslamasi",
    "pricing.cmpGroupBuild": "Yaratish",
    "pricing.cmpGroupPublish": "Nashr",
    "pricing.cmpGroupService": "Xizmat",
    "pricing.cmpFeatAi": "AI konstruktor",
    "pricing.cmpFeatPreview": "Jonli ko'rish",
    "pricing.cmpFeatVersions": "Versiya tarixi",
    "pricing.cmpFeatTemplates": "Andozalar",
    "pricing.cmpFeatSites": "Nashr etilgan saytlar",
    "pricing.cmpFeatSubdomain": "forge.app subdomeni",
    "pricing.cmpFeatCustomDomains": "Shaxsiy domenlar",
    "pricing.cmpFeatContactForm": "Murojaat formasi",
    "pricing.cmpFeatNewsletter": "Yangiliklarga obuna",
    "pricing.cmpFeatBooking": "Yozuv kalendari",
    "pricing.cmpFeatOrdering": "Onlayn buyurtma / to'lov",
    "pricing.cmpFeatAccounts": "Foydalanuvchi hisoblari",
    "pricing.cmpFeatBackend": "Backend biz tomondan ulanadi",
    "pricing.cmpFeatTurnaround": "Sozlash muddati",
    "pricing.cmpFeatSupport": "Qo'llab-quvvatlash",
    "pricing.cmpFeatCancel": "Istalgan vaqtda bekor qilish",
    "pricing.cmpVal.unlimited": "Cheksiz",
    "pricing.cmpVal.7days": "7 kun",
    "pricing.cmpVal.30days": "30 kun",
    "pricing.cmpVal.forever": "Cheksiz muddat",
    "pricing.cmpVal.public": "Ochiq",
    "pricing.cmpVal.all": "Hammasi",
    "pricing.cmpVal.allBespoke": "Hammasi + maxsus",
    "pricing.cmpVal.upTo3": "3 tagacha",
    "pricing.cmpVal.light": "Asosiy",
    "pricing.cmpVal.full": "To'liq",
    "pricing.cmpVal.community": "Hamjamiyat",
    "pricing.cmpVal.email1bd": "Email · 1 ish kuni",
    "pricing.cmpVal.prioritySlack": "Ustuvor + Slack",
    "pricing.tierFree.tag": "SINAB KO'RING",
    "pricing.tierFree.cadence": "abadiy",
    "pricing.tierFree.blurb": "Xohlagancha loyihalashtiring. Bitta saytni forge.app subdomeniga nashr eting.",
    "pricing.tierFree.cta": "Bepul boshlash",
    "pricing.tierFree.feat1": "1 ta faol sayt (qoralama yoki nashr etilgan)",
    "pricing.tierFree.feat2": "Jonli ko'rish",
    "pricing.tierFree.feat3": "forge.app subdomeniga nashr",
    "pricing.tierFree.feat4": "Faqat statik — forma, kirish va to'lovsiz",
    "pricing.tierFree.feat5": "Hamjamiyat qo'llab-quvvatlovi",
    "pricing.tierPro.tag": "KO'PROQ TANLANADI",
    "pricing.tierPro.cadence": "oyiga",
    "pricing.tierPro.blurb": "Yakka ta'sischilar uchun: brendli saytlar va ishlaydigan murojaat formasi.",
    "pricing.tierPro.cta": "Pro olish",
    "pricing.tierPro.feat1": "Free dagi hammasi",
    "pricing.tierPro.feat2": "10 tagacha faol sayt",
    "pricing.tierPro.feat3": "3 tagacha shaxsiy domen",
    "pricing.tierPro.feat4": "Qo'lda ulangan forma va xabarnoma",
    "pricing.tierPro.feat5": "Edge analitika",
    "pricing.tierPro.feat6": "Email qo'llab-quvvatlash · 1 ish kuni",
    "pricing.tierStudio.tag": "AGENTLIKLAR & DO'KONLAR",
    "pricing.tierStudio.cadence": "oyiga",
    "pricing.tierStudio.blurb": "Backend — bu mahsulot. Yozuv, buyurtma, to'lov, hisoblar — barchasini biz ulaymiz.",
    "pricing.tierStudio.cta": "Bog'lanish",
    "pricing.tierStudio.feat1": "Pro dagi hammasi",
    "pricing.tierStudio.feat2": "Cheksiz shaxsiy domenlar",
    "pricing.tierStudio.feat3": "Yozuv, buyurtma, to'lov, hisoblar",
    "pricing.tierStudio.feat4": "Har bir integratsiyani biz ulaymiz",
    "pricing.tierStudio.feat5": "Ustuvor qo'llab-quvvatlash · Slack kanali",
    "card.kebabAria": "«{name}» uchun amallar",
    "card.kebabTip": "Ko'proq",
    "card.menuAria": "«{name}» uchun amallar",
    "card.openAria": "«{name}»'ni ochish",
    "composer.removeAttachAria": "«{name}»'ni o'chirish",
    "builder.mobileGateTitleA": "Forge yaxshiroq ishlaydi",
    "builder.mobileGateTitleB": "kengroq ekranda.",
    "builder.mobileGateBody": "Builderga chat va jonli ko'rib chiqish uchun joy kerak. Ushbu loyihani planshet yoki noutbukda oching — hammasi joyida bo'ladi.",
    "operator.gateTitleA": "Bu bo'lim —",
    "operator.gateTitleB": "faqat jamoa uchun.",
    "operator.gateBody": "Backend navbatiga kirish uchun Forge operatori sifatida tizimga kiring.",
    "operator.gateBack": "Orqaga",
    "operator.gateSignIn": "Operator sifatida kirish",
    "auth.emailInvalid": "Bu to'g'ri email ko'rinmayapti.",
    "auth.nameTooShort": "Kamida 2 ta belgi kiriting.",
    "legal.draftBanner": "QORALAMA — bu vaqtinchalik matn. Ishga tushishdan oldin ko'rib chiqilgan nusxa bilan almashtiring.",
    "legal.lastUpdated": "Yangilangan: {date} (qoralama)",
    "legal.crumb.privacy": "Maxfiylik siyosati",
    "legal.crumb.terms": "Foydalanish shartlari",
    "legal.subtitle.privacy": "Nimani yig'amiz, nima uchun va nima qilmaymiz.",
    "legal.subtitle.terms": "Siz va Forge o'rtasidagi ochiq kelishuv.",
    "legal.footnoteBefore": "Savollar bormi? Yozing: ",
    "legal.footnoteAfter": ".",
    "a11y.skipToContent": "Asosiy qismga o'tish",
    "builder.signupCta": "Ro'yxatdan o'tish",
    "builder.signupCtaTip": "Bu suhbatni hisobingizga saqlash",
    "how.kicker": "QANDAY ISHLAYDI",
    "how.titleA": "To'rt qadam.",
    "how.titleB": "Taxminan qirq soniya.",
    "how.step1Title": "Forge'ga nima kerakligini ayting",
    "how.step1Body": "Bitta jumla yetarli. Ohang, auditoriya, bo'limlar — yoki shunchaki umumiy fikr. Qolganini Forge to'ldiradi.",
    "how.step2Title": "Real vaqtda chizilishini kuzating",
    "how.step2Body": "Sahifalar, komponentlar, matnlar va ishlovchi oldindan ko'rish yonma-yon paydo bo'ladi. Bu skrinshot emas — haqiqiy kod.",
    "how.step3Title": "Suhbatda davom etib tahrirlang",
    "how.step3Body": "O'sha chatdayoq aniqlashtiring. Bo'limlarni ko'chiring, palitrani almashtiring, joylashuvni qayta tuzing. Forge diff yuritadi — hech narsa yo'qolmaydi.",
    "how.step4Title": "Domeningizga e'lon qiling",
    "how.step4Body": "Bir bosish — va sayt haqiqiy URL'da. Domeningizni ulang, repozitoriyni oling yoki Forge'da tahrirlashda davom eting.",
    "ua.unknownDevice": "Noma'lum qurilma",
    "ua.on": "·",
    "auth.fallbackName": "Do'st",
    "auth.welcome": "Xush kelibsiz, {name}",
    "auth.signedOut": "Tizimdan chiqdingiz",
    "auth.sessionExpired": "Sessiya muddati tugadi — davom etish uchun kiring",
    "auth.sessionExpiredAction": "Kirish",
    "auth.signInAsOperator": "Operator pochtangiz bilan kiring — sizni navbatga yo'naltiramiz.",
    "pill.menuAria": "Hisob menyusi",
    "pill.subscriptionStub": "Obunani o'zingiz boshqarish tez orada — rejani o'zgartirish uchun biz bilan bog'laning.",
    "pill.portalError": "Portalni ochib bo'lmadi: {message}",
    "pub.kickerIntro": "NASHR QILISH",
    "pub.titleIntro": "Qanday nashr qilamiz?",
    "pub.introLede": "forge.app subdomenida statik saytni bepul nashr qiling. Shaxsiy domen yoki haqiqiy backend kerakmi? Biz qo'lda ulaymiz.",
    "pub.quickTag": "BEPUL · TEZ",
    "pub.quickTitle": "Tez nashr",
    "pub.quickBodyBefore": "Soniyalarda ushbu manzilda ishga tushadi: ",
    "pub.quickBodyAfter": ". Faqat statik — formalar, kirish, to'lovlar yo'q.",
    "pub.quickCta": "Bepul nashr qilish",
    "pub.proTag": "PRO · BIZ ULAYMIZ",
    "pub.proTitle": "Pro nashr",
    "pub.proBody": "Shaxsiy domen va backend (formalar, bron, to'lovlar, hisoblar). 24 soat ichida qo'lda sozlaymiz.",
    "pub.proCta": "Sozlash",
    "pub.kickerQuick": "TEZ NASHR",
    "pub.titleQuick": "forge.app manzilini tanlang",
    "pub.subdomainLabel": "Subdomen",
    "pub.subdomainTaken": "Band — boshqasini sinab ko'ring.",
    "pub.subdomainAvailable": "Bo'sh.",
    "pub.subdomainHelp": "2–48 belgi, kichik lotin harflari / raqamlar / chiziqcha; boshida yoki oxirida chiziqcha bo'lmasligi kerak.",
    "pub.includedLabel": "Nima kiradi",
    "pub.includedSsl": "Edge orqali bepul SSL",
    "pub.includedPush": "Soniyalarda nashr",
    "pub.includedRepublish": "Istalgan vaqtda qayta nashr qilish",
    "pub.includedUpgrade": "Formalar, kirish, to'lovlar — keyinroq yangilash",
    "pub.back": "Orqaga",
    "pub.publishTo": "Nashr: {url}",
    "pub.kickerDeploy": "NASHR",
    "pub.titleDeploy": "Ishga tushirilmoqda…",
    "pub.kickerLive": "ISHGA TUSHDI",
    "pub.titleLive": "Saytingiz ishga tushdi.",
    "pub.deployStageProvision": "forge.app subdomeni yaratilmoqda",
    "pub.deployStageUpload": "Statik to'plam yuklanmoqda",
    "pub.deployStageWarm": "Edge keshi qizdirilmoqda",
    "pub.deployStageTls": "TLS sertifikat tekshirilmoqda",
    "pub.deployStageLive": "Tayyor",
    "pub.deployProgressAria": "Nashr jarayoni",
    "pub.deployProgressValueText": "{n} foiz — {label}",
    "pub.livePill": "Onlayn · hozirgina",
    "pub.liveVisit": "Ochish",
    "pub.liveCopy": "Havolani nusxalash",
    "pub.linkCopiedToast": "Havola nusxalandi",
    "pub.liveLede": "Sayt havola bo'yicha hammaga ko'rinadi. Shaxsiy domen yoki ishlayotgan forma kerakmi? Loyiha menyusidan Pro'ga o'ting.",
    "pub.liveBackToProject": "Loyihaga qaytish",
    "pub.kickerSent": "SO'ROV YUBORILDI",
    "pub.titleSent": "Tez orada bog'lanamiz.",
    "pub.sentInQueue": "Navbatda",
    "pub.sentBig": "24 soat ichida javob beramiz.",
    "pub.sentBodyBefore": "Tasdiqnoma yuborildi: ",
    "pub.sentBodyAfter": ". Aniqlashtirish kerak bo'lsa, javob bering.",
    "pub.summaryPending": "Kutilayotgan reja",
    "pub.summaryFirstCharge": "Birinchi to'lov nashrdan keyin amalga oshadi.",
    "pub.summaryCancelAnytime": "Istalgan vaqtda bekor qilish.",
    "compare.yes": "Ha",
    "compare.no": "Yo'q",
    "cp.dialogAria": "Komanda palitrasi",
    "cp.searchPlaceholder": "Komanda yoki qidiruv…",
    "cp.searchAria": "Komanda yoki qidiruvni kiriting",
    "cp.listAria": "Komandalar",
    "cp.noMatches": "Hech narsa topilmadi: ",
    "cp.groupNavigate": "Navigatsiya",
    "cp.groupProjects": "Loyihalar",
    "cp.groupActions": "Amallar",
    "cp.navLanding": "Bosh sahifaga",
    "cp.navPricing": "Narxlarga",
    "cp.navTemplates": "Shablonlarga",
    "cp.navDashboard": "Boshqaruv paneliga",
    "cp.navOperator": "Operator navbatiga",
    "cp.actSignin": "Kirish",
    "cp.actSignup": "Hisob yaratish",
    "cp.actSigninOperator": "Operator sifatida kirish",
    "cp.actNewProject": "Yangi loyiha",
    "cp.actSignout": "Chiqish",
    "cp.actSwitchOperator": "Operatorga o'tish",
    "cp.actTweaks": "Tweaks panelini ochish",
    "cp.actFeedback": "Fikr yuborish",
    "cp.hintDemoOnly": "Faqat demo",
    "cp.hintDemo": "Demo",
    "cp.hintTweaks": "Mavzu, zichlik",
    "cp.toastTweaks": "O'ng pastdagi suzuvchi Tweaks tugmasidan foydalaning",
    "cp.toastFeedback": "Fikr-mulohaza — tez orada",
    "account.revokeAllConfirm": "Barcha qurilmalarda chiqilsinmi? Bu yerda ham qayta kirish kerak bo'ladi.",
    "account.revokeAllSuccess": "Barcha qurilmalardan chiqildi",
    "account.revokeAllError": "Chiqib bo'lmadi: {message}",
    "account.saveNameError": "Nomni saqlab bo'lmadi: {message}",
    "account.revokeOneError": "Sessiyadan chiqib bo'lmadi: {message}",
    "account.notifyToggleError": "Bildirishnomalarni yangilab bo'lmadi: {message}",
    "account.deleteAccountError": "Hisobni o'chirib bo'lmadi: {message}",
    "pricing.checkoutStub": "Onlayn to'lov tez orada — Pro obunani boshlash uchun biz bilan bog'laning",
    "pricing.checkoutError": "To'lovni boshlab bo'lmadi: {message}",
    "pricing.studioToast": "Biz bilan bog'lanish — tez orada",
    "pub.requestQueued": "Yuborildi · navbat raqami {id}",
    "pub.requestDedupe": "Allaqachon navbatda · {id}",
    "pub.requestError": "Yuborib bo'lmadi: {message}",
    "pub.proCfgFieldLive": "Sayt qayerda joylashsin?",
    "pub.proCfgSubdomainAria": "Subdomen",
    "pub.proCfgBackupHint": "Zaxira URL sifatida har doim qo'shiladi.",
    "pub.proCfgCdPlaceholder": "Shaxsiy domen (masalan camara.type) — ixtiyoriy",
    "pub.proCfgCdAria": "Shaxsiy domen",
    "pub.proCfgCdInvalid": "camara.type kabi domen kiriting — harflar, raqamlar, chiziqchalar, nuqtalar.",
    "pub.proCfgCdOk": "$8/oy · DNS'ni biz sozlaymiz.",
    "pub.proCfgFieldAddons": "Qaysi backend funksiyalar kerak?",
    "pub.proCfgAddonsHint": "Har birini qo'lda ulaymiz. Istalgan vaqtda bekor qilish mumkin.",
    "pub.proCfgContinue": "Davom etish",
    "pub.proChkSummaryCustom": "Shaxsiy domen · {domain}",
    "pub.proChkSummarySub": "Subdomen · {sub}.forge.app",
    "pub.proChkMonthly": "Oyiga",
    "pub.proChkFree": "bepul",
    "pub.proChkFieldEmail": "Sizga qayerga yozaylik?",
    "pub.proChkEmailPlaceholder": "you@example.com",
    "pub.proChkEmailAria": "Aloqa uchun email",
    "pub.proChkEmailInvalid": "Bu to'g'ri email ko'rinmayapti.",
    "pub.proChkEmailHint": "Hajmni tasdiqlaymiz, ishga tushgach hisob-kitob qilamiz.",
    "pub.proChkNoticeKicker": "QANDAY ISHLAYDI",
    "pub.proChkNoticeBody": "Hozircha hisobdan yechilmaydi. Brifni ko'rib chiqamiz, backendni qo'lda ulaymiz va 24 soat ichida ishlovchi URL yuboramiz. Birinchi to'lov faqat ishga tushgandan keyin.",
    "pub.proChkSending": "Yuborilmoqda…",
    "pub.proChkSend": "Jamoaga yuborish",
    "pub.addonForm": "Aloqa formasi",
    "pub.addonFormHint": "Email'ga yuboriluvchi haqiqiy forma; spam filtri.",
    "pub.addonNewsletter": "Yangiliklar uchun obuna",
    "pub.addonNewsletterHint": "Mailchimp / Buttondown bilan sinxron.",
    "pub.addonBooking": "Bron qilish kalendari",
    "pub.addonBookingHint": "Cal.com yoki shaxsiy vaqt oraliqlari.",
    "pub.addonOrdering": "Onlayn buyurtma",
    "pub.addonOrderingHint": "Menyu, savat, Payme / Click orqali to'lov.",
    "pub.addonPayments": "Bir martalik to'lovlar",
    "pub.addonPaymentsHint": "Chaevoy / pay-what-you-want / xayriya.",
    "pub.addonAccounts": "Foydalanuvchi akkauntlari + kirish",
    "pub.addonAccountsHint": "Email va Google orqali kirish, shaxsiy kabinetlar.",
    "settings.aiModeLabel": "AI rejimi",
    "settings.aiModeAria": "AI rejimi",
    "settings.aiModeHint": "Premium — har bir qadamda Claude. Hybrid — birinchi dizayn bosqichi uchun Claude, keyin tezroq model. Economy — barcha qadamlarda tezroq model.",
    "settings.aiModePremium": "Premium",
    "settings.aiModePremiumNote": "Faqat Claude",
    "settings.aiModeHybrid": "Hybrid",
    "settings.aiModeHybridNote": "Claude → Kimi",
    "settings.aiModeEconomy": "Economy",
    "settings.aiModeEconomyNote": "Faqat Kimi",
    "settings.modalTitle": "Loyiha sozlamalari",
    "settings.tabsAria": "Loyiha sozlamalari bo'limlari",
    "settings.tabGeneral": "Umumiy",
    "settings.tabDomain": "Domen",
    "settings.tabPeople": "Jamoa",
    "settings.tabDanger": "Xavfli zona",
    "settings.fieldName": "Loyiha nomi",
    "settings.fieldNameRequired": "Majburiy maydon.",
    "settings.fieldSlug": "Slag",
    "settings.fieldSlugHint": "Loyiha yaratilganda belgilanadi. Hozircha qayta nomlash imkoni yo'q — kerak bo'lsa biz bilan bog'laning.",
    "settings.fieldDesc": "Tavsif / brif",
    "settings.fieldDescHint": "Forge'ga bergan brifingiz. Bu yerdagi tahrir keyingi generatsiyalarga ta'sir qiladi.",
    "settings.fieldSwatch": "Miniatyura rangi",
    "settings.fieldSwatchHint": "Boshqaruv panelidagi karta va oldindan ko'rish palitrasida ishlatiladi.",
    "settings.cancel": "Bekor qilish",
    "settings.save": "O'zgarishlarni saqlash",
    "settings.savedToast": "Saqlandi — {name}",
    "settings.domSubLabel": "forge.app subdomeni",
    "settings.domSubHint": "Zaxira URL sifatida har doim mavjud.",
    "settings.domCustomLabel": "Shaxsiy domen",
    "settings.domCustomHint": "O'z domeningizni ulang — DNS'ni biz sozlaymiz.",
    "settings.domCustomPlaceholder": "masalan camara.type",
    "settings.domCustomInvalid": "camara.type kabi domen kiriting.",
    "settings.domDnsKicker": "DNS'NI USHBU YOZUVLARGA YO'NALTIRING",
    "settings.domDnsType": "Tur",
    "settings.domDnsName": "Nom",
    "settings.domDnsValue": "Qiymat",
    "settings.domDnsCaption": "Domen registratoriga qo'shiladigan DNS yozuvlari",
    "settings.domVerify": "Tekshirish va ulash",
    "settings.domQueuedToast": "Domen navbatga qo'yildi — DNS tekshiruvi boshlandi",
    "settings.peopleMembers": "Ishchi makon a'zolari",
    "settings.peopleYou": "Siz",
    "settings.peopleOwner": "Egasi",
    "settings.peopleInviteLabel": "Email orqali taklif",
    "settings.peopleInviteHint": "Ko'rish va tahrirlash uchun magic-havola olishadi.",
    "settings.peopleInvitePlaceholder": "someone@example.com",
    "settings.peopleInviteSend": "Taklif yuborish",
    "settings.peopleInviteToast": "Taklif yuborildi — tez orada",
    "settings.peopleDone": "Tayyor",
    "settings.dangerHead": "Savatchaga ko'chirish",
    "settings.dangerBody": "Loyiha boshqaruv panelidan yo'qoladi, lekin Savatcha tugmasi orqali 30 kun davomida tiklash mumkin, so'ng butunlay o'chiriladi. Tezroq o'chirish uchun Savatcha → Butunlay o'chirish.",
    "settings.dangerBtn": "{name}'ni Savatchaga ko'chirish",
    "settings.dangerConfirm": "«{name}»'ni Savatchaga ko'chirilsinmi? U yerda 30 kun saqlanadi; istalgan vaqtda boshqaruv panelidagi Savatcha tugmasi orqali tiklash mumkin.",
    "settings.dangerMovedToast": "«{name}» Savatchaga ko'chirildi",
    "settings.dangerRestoredToast": "«{name}» tiklandi",
    "settings.dangerUndo": "Bekor qilish",
    "img.title": "Rasm qo'shish",
    "img.kicker": "ILOVA",
    "img.tabUpload": "Yuklash",
    "img.tabLibrary": "Kutubxona",
    "img.tabUrl": "URL orqali",
    "img.tabsAria": "Rasm manbai",
    "img.dropTitle": "Rasmni shu yerga tashlang",
    "img.dropHint": "yoki tanlash uchun bosing · jpg, png, webp · 8 MB gacha",
    "img.urlLabel": "Rasm URL'i",
    "img.urlHint": "Rasmga to'g'ridan-to'g'ri havolani joylashtiring (jpg, png, webp).",
    "img.urlPlaceholder": "https://images.example.com/cover.jpg",
    "img.urlInvalid": "http:// yoki https:// bilan boshlanishi kerak",
    "img.urlPreviewAlt": "Rasm oldindan ko'rinishi",
    "img.urlCancel": "Bekor qilish",
    "img.urlUse": "Foydalanish",
    "img.attachedToast": "Biriktirildi: {name}",
    "share.title": "Loyihani ulashish",
    "share.kicker": "ULASHISH",
    "share.tabPreview": "Qoralama oldindan ko'rish",
    "share.tabLive": "Ishga tushgan sayt",
    "share.tabsAria": "Qaysi versiya",
    "share.noLiveBody": "Bu loyiha hali e'lon qilinmagan.",
    "share.noLiveCta": "Qanday e'lon qilish",
    "share.publishToast": "Ishga tushirish uchun E'lon tugmasini oching",
    "share.previewLabel": "Qoralama havolasi",
    "share.previewHint": "Qoralamani jonli tahrirlash. Havolaga ega bo'lganlarning hammasi eng so'nggi versiyani ko'radi.",
    "share.liveLabel": "Sayt havolasi",
    "share.liveHint": "Sizning e'lon qilingan saytingiz.",
    "share.copyBtn": "Nusxalash",
    "share.copiedToast": "Havola nusxalandi",
    "copy.failToast": "Nusxalab bo'lmadi — qo'lda nusxa oling",
    "share.permLabel": "Kim ko'ra oladi",
    "share.permAnyoneTitle": "Havolaga ega har kim",
    "share.permAnyoneBody": "Standart sifatida ochiq. Kirish talab qilinmaydi.",
    "share.permPwdTitle": "Parol bilan himoyalangan",
    "share.permPwdBody": "Tashrif buyuruvchilar saytni ko'rishdan oldin parol kiritadilar.",
    "share.permPwdPlaceholder": "Parol o'rnating",
    "share.embedSummary": "Sahifaga joylashtirish (iframe)",
    "share.revoke": "Havolani bekor qilish",
    "share.revokedToast": "Havola bekor qilindi",
    "share.done": "Tayyor",
    "export.title": "Loyihani eksport qilish",
    "export.kicker": "EKSPORT",
    "export.lede": "Forge yaratgan hamma narsa sizniki. Istalgan vaqtda olib keting.",
    "export.zipTitle": "ZIP yuklab olish",
    "export.zipTag": "DARHOL",
    "export.zipBody": "To'liq Next.js + Tailwind loyihasi, pnpm install bilan ishga tushadi.",
    "export.zipCta": "ZIP yuklab olish",
    "export.zipToast": "{name}.zip — tez orada",
    "export.ghTitle": "GitHub'ga push qilish",
    "export.ghTag": "TAVSIYA",
    "export.ghBody": "Repozitoriy ulang. Har bir saqlashda eng so'nggi versiyani push qilamiz.",
    "export.ghCta": "GitHub'ni ulash",
    "export.ghToast": "GitHub OAuth — tez orada",
    "export.handoffTitle": "Dasturchiga topshirish",
    "export.handoffTag": "JAMOA",
    "export.handoffBody": "Manba bilan birga README, env shabloni va deploy izohlarini tayyorlaydi.",
    "export.handoffCta": "Topshirish paketini yaratish",
    "export.handoffToast": "Topshirish paketi — tez orada",
    "billing.cancelledToast": "To'lov bekor qilindi — hisobdan yechilmagan",
    "dash.nameInTrash": "Bu nomli loyiha Savatchada. Uni tiklang yoki boshqa nom tanlang.",
    "tpl.nameInTrash": "Bu nomli loyiha Savatchada. Uni tiklang yoki shablonni qayta nomlang.",
    "nf.bugReportToast": "Xatolikmi? hello@forge.app ga yozing",
    "nf.kicker": "404 · TOPILMADI",
    "nf.h1": "Bunday sahifa yo'q.",
    "nf.body": "Balki ko'chirilgandir, yoki hech qachon mavjud bo'lmagan. Qanday bo'lmasin, bosh sahifa — bu prompt maydoni joylashgan joy.",
    "nf.btnHome": "Bosh sahifaga",
    "nf.btnReport": "Xatolik haqida xabar berish",
    "billing.successToast": "Pro faollashtirildi — operator tez orada bog'lanadi",
    "billing.confirmSignInToast": "Yangilanishni tasdiqlash uchun kiring.",
    "chat.budgetExhausted": "Bugungi {cap} ta {plan} chat navbatini ishlatib bo'ldingiz — taxminan {mins} daqiqada yangilanadi.",
    "templates.filterAria": "Shablonlarni kategoriya bo'yicha saralash",
    "loading.screens": "Yuklanmoqda…",
    "chat.streamFailedToast": "AI bilan bog'lanib bo'lmadi{idSuffix} — mahalliy javobga o'tamiz.",
    "chat.streamFailedReqId": " (id {reqId})",
    "chat.retryAction": "Qayta urinish",
    "chat.offlineAnonText": "Tushundim. Haqiqiy javoblar bilan davom etish va suhbatni saqlash uchun ro'yxatdan o'ting. Faqat email kerak.",
    "chat.offlineAuthedText": "Tushundim — so'rovingizni saqlayapman. Aloqa qaytishi bilan aqlli yordamchi davom ettiradi.",
    "chat.suggestSignup": "Saqlash uchun ro'yxatdan o'ting",
    "landing.chipLanding": "Lending",
    "landing.chipPortfolio": "Portfolio",
    "landing.chipDocs": "Hujjatlar",
    "landing.chipInvite": "Taklif",
    "landing.chipStorefront": "Do'kon",

    "lang.label": "Til",
    "greet.morning": "Xayrli tong",
    "greet.afternoon": "Xayrli kun",
    "greet.evening": "Xayrli kech",
    "greet.night": "Kech ishlayapsiz",
    "dash.welcomeKicker": "QAYTGANINGIZ BILAN",

    "pill.dashboard": "Kabinet",
    "pill.manageSubscription": "Obunani boshqarish",
    "pill.upgradeToPro": "Pro tarifiga o'tish",
    "pill.accountSettings": "Hisob sozlamalari",
    "pill.signOut": "Chiqish",

    "account.title": "Hisob",
    "account.back": "← Ortga",
    "account.profileTitle": "Profil",
    "account.profileSubtitle": "Forge'da qanday ko'ringansiz.",
    "account.displayName": "Ko'rinadigan ism",
    "account.emailLabel": "Email",
    "account.emailHint": "Email — bu sizning kirish identifikatoringiz, hozircha o'zgartirib bo'lmaydi. Ko'chirish uchun yordamga yozing.",
    "account.memberSince": "Hisob ochilgan sana:",
    "account.save": "O'zgarishlarni saqlash",
    "account.saving": "Saqlanmoqda…",
    "account.discard": "Bekor qilish",
    "account.planTitle": "Tarif",
    "account.planProSubtitle": "Siz Pro tarifidasiz. Karta, hisob-faktura va bekor qilish portalda.",
    "account.planFreeSubtitle": "Siz bepul tarifdasiz. Ko'proq faol loyiha, shaxsiy domen va tayyor formalar uchun Pro'ga o'ting.",
    "account.dangerTitle": "Xavfli hudud",
    "account.dangerSubtitle": "Qaytarib bo'lmaydigan amallar. Ortga yo'l yo'q.",
    "account.deleteAccount": "Hisobni o'chirish",
    "account.deleteConfirmTitle": "Hisob butunlay o'chiriladi.",
    "account.deleteConfirmBody": "Barcha loyihalar, suhbatlar va bildirishnomalar o'chadi. Pro tarifida bo'lsangiz, avval \"Obunani boshqarish\"dan obunani bekor qiling.",
    "account.deleteTypeLabel": "Tasdiqlash uchun \"delete\" deb yozing",
    "account.deletePermanent": "Butunlay o'chirish",
    "account.deleting": "O'chirilmoqda…",
    "account.cancel": "Bekor qilish",
    "account.toastNameSaved": "Ism yangilandi",
    "account.toastDeleted": "Hisob o'chirildi",
    "account.renewsOn": "Yangilanadi",
    "account.notifyTitle": "Bildirishnomalar",
    "account.notifySubtitle": "Loyihangiz statusi o'zgarganda email bilan xabardor qilamiz.",
    "account.notifyToggle": "Status o'zgarganda email yuborish",
    "account.notifyHint": "Yig'ish boshlanganda, nashr qilinganda va biror narsa to'xtab qolganda email yuboramiz — ilovadagi qo'ng'iroq bilan birga.",
    "account.activityTitle": "So'nggi faollik",
    "account.activitySubtitle": "Hisobga oxirgi marta qachon kirilgani. Tanimagan narsa ko'rsangiz — barcha qurilmalardan chiqib turing.",
    "account.activityLoading": "Yuklanmoqda…",
    "account.activityEmpty": "Hozircha kirish ma'lumotlari yo'q.",
    "account.activityCurrent": "BU QURILMA",
    "account.revokeAll": "Barcha qurilmalardan chiqish",
    "account.revoking": "Chiqilmoqda…",
    "account.revokeOne": "Bekor qilish",
    "account.sessionRevoked": "Sessiya bekor qilindi",
    "account.exportTitle": "Sizning ma'lumotlaringiz",
    "account.exportSubtitle": "Hisobingiz haqida saqlaganimizning hammasini o'zingiz yuklab oling.",
    "account.exportDownload": "JSON yuklab olish",
    "account.exportHint": "Profil, barcha loyihalar (jonli + arxivlangan), suhbatlar, bildirishnomalar, sessiyalar va backend buyurtmalari kiritiladi. To'lovlar tarixi to'lov provayderingizda (Payme yoki Click) saqlanadi.",

    "err.title": "Nimadir noto'g'ri ketdi.",
    "err.body": "Ishingiz xavfsiz. Xato bizga yuborildi — ko'rib chiqamiz. Qayta urinib ko'ring yoki bosh sahifaga qayting.",
    "err.tryAgain": "Qayta urinish",
    "err.backHome": "Bosh sahifa",
  },
};

let __lang = _lsLoad("lang", "en");
if (!TRANSLATIONS[__lang]) __lang = "en";
const __langListeners = new Set();
// P166 — keep <html lang> in sync with the active UI language so
// screen readers, OS-level live captioning, search-engine snippeting,
// and CSS :lang() selectors reflect actual content. Index.html ships
// `lang="en"`; without this sync a user with persisted lang=ru / uz
// has English semantics announced over Cyrillic / Latin-Uzbek text.
const _applyHtmlLang = (lang) => {
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = lang;
    // P445 — Skip-to-content link is rendered as static HTML in
    // index.html (so keyboard users get it before JS hydrates).
    // Once we're past hydration, swap its text to the active locale.
    const skip = document.querySelector("a.skip-link");
    if (skip) {
      const localized = TRANSLATIONS[lang]?.["a11y.skipToContent"]
        ?? TRANSLATIONS.en?.["a11y.skipToContent"];
      if (localized) skip.textContent = localized;
    }
  }
};
_applyHtmlLang(__lang);                   // boot-time sync (persisted lang)
window.setLang = (lang) => {
  if (!TRANSLATIONS[lang]) return;
  __lang = lang;
  _lsSave("lang", __lang);
  _applyHtmlLang(__lang);
  __langListeners.forEach((fn) => fn(__lang));
};
window.getLang = () => __lang;

// P509/P510 — Shared onClick helper for `<a href="/x">` SPA-nav links.
// Pre-fix every call site inlined `(e) => { e.preventDefault();
// window.goTo("x"); }` — which hijacked Cmd/Ctrl/Shift-Click and
// middle-click (button:1), which users use to "open in new tab".
// This helper preserves the browser's native modifier-key behavior:
// if the user wants a new tab, the handler bails and lets the real
// `href` take over.
//
// Accepts either a screen-name string (for goTo) or a callback (for
// custom navigation like onBack closures).
window.spaNavOnClick = (screenOrFn) => (e) => {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
  e.preventDefault();
  if (typeof screenOrFn === "function") screenOrFn();
  else window.goTo?.(screenOrFn);
};

// P396 — Single-source-of-truth for the X-Forge-Lang header. The
// SPA's _apiHeaders() helper bakes it into authed fetches; raw
// fetches that bypass _apiHeaders (auth modal, pricing CTA, publish
// submit, account.js *) need it too. P388/P393/P394/P395 each
// inlined `{ "x-forge-lang": window.getLang?.() || "en" }`; this
// helper centralizes the header name + the fallback so a future
// rename (or per-locale value-mapping) lands in one place. Returns
// an object so call sites can spread it: { ...langHeader(), ... }.
window.langHeader = () => ({ "x-forge-lang": __lang || "en" });

// P401 — Public non-hook translation lookup for callers that aren't
// React components (event handlers in non-component callbacks, async
// continuations after a setState, etc). Same fallback chain as
// `useT`'s returned `t`: lang-specific → en → raw key. Multiple in-
// shared.jsx call sites already use this pattern inline (_authT,
// _timeAgoT, the budget-exhausted formatter); exposing as window.tt
// lets external scripts (builder.jsx, etc) avoid duplicating the
// 3-line lookup. Name `tt` is short and obviously distinct from the
// React-hook `t` so it doesn't get confused at call sites.
window.tt = (key) =>
  TRANSLATIONS[__lang]?.[key] ?? TRANSLATIONS.en?.[key] ?? key;

// P575 — Shared normalize-for-search helper. Three call sites
// (dashboard search P573, templates search P573, command palette
// search P574) ran the same NFD-strip + apostrophe-collapse +
// lowercase pipeline. DRY'd here so a future tweak (e.g. additional
// apostrophe variants, soft-hyphen stripping, locale-aware
// case-folding via Intl.Collator) lands in one place. Pure
// string-ops, ~zero allocation cost vs the inline copies.
//
// Strips combining marks via NFD (`café` → `cafe`), collapses U+2018
// `'`, U+2019 `'`, and U+02BC `ʼ` to ASCII `'` (Latin-Uzbek's `O'`,
// `g'` use these interchangeably depending on input source), then
// lowercases. Cyrillic isn't materially affected by NFD — `й` is
// `и + ̆` after decomposition, but the user's keyboard types it
// the same way, so both sides decompose identically.
window.searchNorm = (s) => (s || "")
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "")
  .replace(/[‘’ʼ]/g, "'")
  .toLowerCase();

// P536 — Pre-fix three sites (ShareModal preview/live URL, publish
// LiveStep, builder WorkPane URL bar) used `navigator.clipboard?.
// writeText(text); window.toast("Copied")` — fire-and-forget. If
// the promise rejects (Permissions-Policy disallowed, the user
// denied the prompt, the API is unavailable on iOS Safari < 13.4,
// or a transient failure), the success toast still fires — the
// user thinks they copied something they didn't and silently
// fails their next paste. Now: await the promise, surface a
// localized warn-toast on failure, no toast at all if the API
// itself is missing (rare; means we'd toast "copy failed" while
// the user is already in a degraded environment without a
// follow-on action). Returns a boolean so call sites can branch
// on success when needed.
window.copyText = async (text, { successKey, failKey } = {}) => {
  if (!navigator.clipboard?.writeText) {
    window.toast?.(
      TRANSLATIONS[__lang]?.[failKey || "copy.failToast"]
        ?? TRANSLATIONS.en?.[failKey || "copy.failToast"]
        ?? "Couldn't copy — please copy manually",
      { tone: "warn" }
    );
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    if (successKey) {
      window.toast?.(
        TRANSLATIONS[__lang]?.[successKey]
          ?? TRANSLATIONS.en?.[successKey]
          ?? successKey
      );
    }
    return true;
  } catch {
    window.toast?.(
      TRANSLATIONS[__lang]?.[failKey || "copy.failToast"]
        ?? TRANSLATIONS.en?.[failKey || "copy.failToast"]
        ?? "Couldn't copy — please copy manually",
      { tone: "warn" }
    );
    return false;
  }
};
window.useT = function useT() {
  const [lang, setLang] = useStateS(__lang);
  useEffectS(() => {
    __langListeners.add(setLang);
    return () => __langListeners.delete(setLang);
  }, []);
  const t = (key) => TRANSLATIONS[lang]?.[key] ?? TRANSLATIONS.en[key] ?? key;
  return { t, lang };
};

function LangSwitcher({ compact = false }) {
  // P431 — also pull `t` so the radiogroup aria-label can localize.
  const { lang, t } = window.useT();
  // P179 — full names + native names (in their own language) for the
  // accessible name. The visible "EN"/"RU"/"UZ" is too cryptic for AT
  // users; aria-label exposes "English", "Русский", "Oʻzbekcha". Each
  // button is a radio in a single-select language radiogroup.
  const opts = [
    { v: "en", label: "EN", name: "English" },
    { v: "ru", label: "RU", name: "Русский" },
    { v: "uz", label: "UZ", name: "Oʻzbekcha" },
  ];
  // P332 — same WAI-ARIA radiogroup keyboard pattern as P330 (MTabs)
  // and P331 (Dashboard Filter): only the selected radio is tabbable;
  // arrow keys + Home/End move both selection and focus within the
  // group. The Language switcher sits in the global header on every
  // marketing page, so this lifts the keyboard floor for visitors who
  // toggle locale before the rest of the radiogroup sweep lands.
  const groupRef = useRefS(null);
  const onKeyDown = (e) => {
    const idx = opts.findIndex((o) => o.v === lang);
    if (idx < 0) return;
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (idx + 1) % opts.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (idx - 1 + opts.length) % opts.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = opts.length - 1;
    else return;
    e.preventDefault();
    window.setLang(opts[next].v);
    queueMicrotask(() => {
      const btns = groupRef.current?.querySelectorAll('[role="radio"]');
      btns?.[next]?.focus();
    });
  };
  return (
    <div
      style={langStyles.wrap}
      role="radiogroup"
      aria-label={t("lang.label")}
      ref={groupRef}
      onKeyDown={onKeyDown}
    >
      {opts.map((o) => (
        <button
          type="button"
          key={o.v}
          style={{
            ...langStyles.btn,
            ...(lang === o.v ? langStyles.btnOn : {}),
            ...(compact ? langStyles.btnCompact : {}),
          }}
          onClick={() => window.setLang(o.v)}
          role="radio"
          aria-checked={lang === o.v}
          aria-label={o.name}
          lang={o.v}
          tabIndex={lang === o.v ? 0 : -1}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
const langStyles = {
  wrap: {
    display: "inline-flex",
    background: "var(--paper-2)",
    border: "1px solid var(--rule)",
    borderRadius: 999,
    padding: 2,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
  },
  btn: {
    padding: "5px 10px",
    background: "transparent",
    color: "var(--ink-3)",
    borderRadius: 999,
    letterSpacing: "0.06em",
  },
  btnCompact: { padding: "3px 8px", fontSize: 10 },
  btnOn: {
    background: "var(--paper)",
    color: "var(--ink)",
    boxShadow: "0 0 0 1px var(--rule)",
  },
};

// ── Empty-state illustrations ───────────────────────────────────────────────
// Three small inline SVGs used wherever a screen has nothing to show.
// All use stroke-based line art with currentColor (carries from the parent
// text color) and var(--accent) for the highlight glyph.

function NoProjectsArt({ size = 96 }) {
  return (
    <svg viewBox="0 0 96 96" width={size} height={size} aria-hidden="true">
      {/* Tilted document */}
      <g transform="rotate(-4 48 48)">
        <path
          d="M28 18 L60 18 L72 30 L72 78 L28 78 Z"
          fill="var(--paper-2)"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M60 18 L60 30 L72 30"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Faint baseline copy */}
        <path d="M36 44 L62 44" stroke="currentColor" strokeWidth="1" opacity="0.3" strokeLinecap="round" />
        <path d="M36 52 L56 52" stroke="currentColor" strokeWidth="1" opacity="0.3" strokeLinecap="round" />
        <path d="M36 60 L52 60" stroke="currentColor" strokeWidth="1" opacity="0.3" strokeLinecap="round" />
      </g>
      {/* Accent sparkle, top-right */}
      <g stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round">
        <path d="M76 12 L76 22" />
        <path d="M71 17 L81 17" />
      </g>
      <circle cx="86" cy="30" r="1.6" fill="var(--accent)" />
      <circle cx="14" cy="72" r="1.6" fill="var(--accent)" opacity="0.5" />
    </svg>
  );
}

function NoMatchArt({ size = 96 }) {
  return (
    <svg viewBox="0 0 96 96" width={size} height={size} aria-hidden="true">
      {/* Loupe */}
      <circle cx="38" cy="38" r="20" fill="var(--paper-2)" stroke="currentColor" strokeWidth="1.5" />
      <path d="M52 52 L72 72" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* "No result" wavy null-set line through the loupe */}
      <path
        d="M24 38 Q31 30, 38 38 T54 38"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* A couple of search "particles" floating around */}
      <circle cx="80" cy="22" r="1.6" fill="var(--accent)" />
      <circle cx="14" cy="78" r="1.6" fill="var(--accent)" opacity="0.6" />
      <path d="M82 70 L86 74" stroke="var(--accent)" strokeWidth="1.4" strokeLinecap="round" opacity="0.6" />
    </svg>
  );
}

function AllClearArt({ size = 96 }) {
  return (
    <svg viewBox="0 0 96 96" width={size} height={size} aria-hidden="true">
      {/* Calm circle */}
      <circle cx="48" cy="48" r="26" fill="var(--paper-2)" stroke="currentColor" strokeWidth="1.5" />
      {/* Checkmark */}
      <path
        d="M34 49 L43 58 L62 36"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Confetti dots */}
      <circle cx="78" cy="20" r="1.8" fill="var(--accent)" />
      <circle cx="18" cy="22" r="1.4" fill="var(--accent)" opacity="0.6" />
      <circle cx="80" cy="74" r="1.4" fill="var(--accent)" opacity="0.5" />
      <path d="M14 70 L18 74" stroke="var(--accent)" strokeWidth="1.4" strokeLinecap="round" opacity="0.6" />
      <path d="M84 50 L88 50" stroke="var(--accent)" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

// ── api helper — mirrors mutations to D1 in background ─────────────────────
// Cookie-only auth (P123): the HttpOnly mint_session cookie is sent
// automatically with every same-origin fetch, so this helper just gates
// on the local authed state to avoid 401-spamming when signed out, and
// returns the JSON content-type for write methods.
function _apiHeaders() {
  const auth = __authState;
  if (!auth.isAuthed || !auth.email) return null;
  // P388 — surface the SPA's picked locale to the server so
  // server-rendered copy (chat stub, future error pages) matches the
  // user's in-app pick, not the browser's Accept-Language. The server
  // helper (functions/_lib/i18n.js) reads this header first, then
  // falls back to Accept-Language. P396 — use the shared
  // `window.langHeader()` for the same key + fallback as raw fetches.
  return {
    "content-type": "application/json",
    ...window.langHeader(),
  };
}
// Pull the full server-side project list for the current authed user.
// Server-truth quota snapshot from the last /api/projects sync. Updated
// on every _apiListProjects call. The dashboard reads it via
// window.useProjectQuota() to show "X / Y projects" + an upgrade hint.
let __projectQuota = null;
const __quotaListeners = new Set();
const __notifyQuota = () => __quotaListeners.forEach((fn) => fn(__projectQuota));
window.useProjectQuota = function useProjectQuota() {
  const [q, setQ] = useStateS(__projectQuota);
  useEffectS(() => {
    __quotaListeners.add(setQ);
    return () => __quotaListeners.delete(setQ);
  }, []);
  return q;
};

// Per-plan AI turn budget snapshot from /api/auth/me (P111). Updated
// whenever refreshAuth() runs — boot, sign-in success, post-checkout.
// Drives the dashboard's turn-budget pill alongside the project quota.
let __turnBudget = null;
const __turnBudgetListeners = new Set();
window.useTurnBudget = function useTurnBudget() {
  const [b, setB] = useStateS(__turnBudget);
  useEffectS(() => {
    __turnBudgetListeners.add(setB);
    return () => __turnBudgetListeners.delete(setB);
  }, []);
  return b;
};
// Public refresh for callers that just consumed a turn (e.g. chat.js
// SSE done handler). Re-fetches /api/auth/me so the pill catches up.
window.refreshTurnBudget = async () => {
  try {
    const r = await fetch("/api/auth/me");
    if (!r.ok) return;
    const j = await r.json();
    if (j.turnBudget) {
      __turnBudget = j.turnBudget;
      __turnBudgetListeners.forEach((fn) => fn(__turnBudget));
    }
  } catch { /* tolerate transient */ }
};

async function _apiListProjects() {
  const headers = _apiHeaders();
  if (!headers) return [];
  try {
    const r = await fetch("/api/projects", { headers });
    // 401 here means our cookie is dead server-side (TTL'd, revoked,
    // or session table wiped). Trigger the centralized expiry handler
    // (P92) — clears local stores, flips auth, prompts re-sign-in.
    if (r.status === 401) { window.__sessionExpiredMint?.(); return []; }
    if (!r.ok) throw new Error(`status ${r.status}`);
    const j = await r.json();
    if (j.quota) { __projectQuota = j.quota; __notifyQuota(); }
    return j.projects || [];
  } catch (e) {
    console.warn("[mint] listProjects failed:", e.message);
    return [];
  }
}

// Reconcile local + server project lists. Newer updatedAt wins; local-only
// rows get pushed up, server-only rows appear locally. Seeded "demo" projects
// stay client-only (no createdAt/updatedAt) so they don't get pushed.
async function _syncProjectsFromServer() {
  const headers = _apiHeaders();
  if (!headers) return;
  const serverList = await _apiListProjects();
  if (!serverList) return;

  // Drop seed entries from the local view before merging — once an
  // authed session has weighed in, demo data shouldn't show up next
  // to the user's real projects (P77, mirrors P75/P76).
  const localReal = __projects.filter((p) => !_SEED_PROJECT_IDS.has(p.id));
  const byIdLocal = new Map(localReal.map((p) => [p.id, p]));
  const byIdServer = new Map(serverList.map((p) => [p.id, p]));
  const merged = [];
  const allIds = new Set([...byIdLocal.keys(), ...byIdServer.keys()]);
  const pushQueue = [];

  for (const id of allIds) {
    const local = byIdLocal.get(id);
    const server = byIdServer.get(id);
    if (local && !server) {
      // Local-only — push to server (we already filtered seeds above,
      // so anything left is genuinely user-touched).
      pushQueue.push(local);
      merged.push(local);
    } else if (server && !local) {
      // Server-only — appear locally.
      merged.push({
        ...server,
        version: server.version || "v0.1",
        edits: server.edits ?? 0,
      });
    } else {
      // Both — newer updatedAt wins.
      const localTs = local.updatedAt || 0;
      const serverTs = server.updatedAt || 0;
      if (localTs > serverTs) {
        pushQueue.push(local);
        merged.push(local);
      } else {
        merged.push({ ...local, ...server });
      }
    }
  }
  // Sort by updatedAt desc to match the dashboard's default sort.
  merged.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  __projects = merged;
  __notifyProjects();

  // Push catch-up upserts in the background so the server matches local for
  // anything that drifted while signed out.
  //
  // P246 — Pre-fix this loop was fire-and-forget. Concrete bug:
  // device A trashes project X server-side; device B's local cache
  // still has X; on next sync the merge sees server-list missing X
  // and falls into the "local && !server" branch above (pushQueue +
  // keep locally), then upserts to the server. Server returns 409
  // (P228 — id matches a soft-deleted row), which the fire-and-forget
  // loop silently drops. Local state keeps the phantom project; user
  // clicks it; backend 404s on every chat/messages/PATCH call.
  // Now we await the upsert and, on 409, prune from local state +
  // re-notify so the dashboard catches up. 200/other failures keep
  // the existing keep-locally behaviour.
  for (const p of pushQueue) {
    const res = await window.api?.upsertProject(p);
    if (res && res.status === 409) {
      __projects = __projects.filter((x) => x.id !== p.id);
      __notifyProjects();
    }
  }
}

// Operator queue sync — same pattern as projects but only fires for users
// in the operator allow-list. Server already enforces operator gating
// (returns 403 to anyone else); we still gate the call client-side to
// avoid a guaranteed-failure request on every sign-in.
async function _apiListQueue() {
  const headers = _apiHeaders();
  if (!headers) return null;
  try {
    const r = await fetch("/api/operator/queue", { headers });
    if (r.status === 401) { window.__sessionExpiredMint?.(); return null; }
    if (r.status === 403) return null; // not an operator
    if (!r.ok) throw new Error(`status ${r.status}`);
    const j = await r.json();
    return j.items || [];
  } catch (e) {
    console.warn("[mint] listQueue failed:", e.message);
    return null;
  }
}
async function _syncQueueFromServer() {
  if (__authState.role !== "operator") return;
  const items = await _apiListQueue();
  if (!items) return;
  // Drop seeds from local before merging — same hygiene as
  // _syncProjectsFromServer / _syncNotificationsFromServer.
  const localReal = __operatorQueue.filter((q) => !_SEED_QUEUE_IDS.has(q.id));
  const byIdLocal = new Map(localReal.map((q) => [q.id, q]));
  const byIdServer = new Map(items.map((q) => [q.id, q]));
  const merged = [];
  const allIds = new Set([...byIdLocal.keys(), ...byIdServer.keys()]);
  const pushQueue = [];

  for (const id of allIds) {
    const local = byIdLocal.get(id);
    const server = byIdServer.get(id);
    if (local && !server) {
      // Already filtered seeds above; anything left is genuinely
      // operator-touched.
      pushQueue.push(local);
      merged.push(local);
    } else if (server && !local) {
      merged.push(server);
    } else {
      const localTs = local.updatedAt || local.requestedAt || 0;
      const serverTs = server.updatedAt || server.requestedAt || 0;
      if (localTs > serverTs) {
        pushQueue.push(local);
        merged.push(local);
      } else {
        merged.push({ ...local, ...server });
      }
    }
  }
  __operatorQueue = merged;
  __notifyOp();
  for (const it of pushQueue) window.api?.upsertQueueItem(it);
}

// Hook all sync routines into auth transitions: every time we enter the
// authed state from outside, kick a sync. Doing it inside the notify
// callback rather than from React keeps the trigger close to the source.
//
// Tracks the email of the last successful sync so a cross-user sign-in
// (User A signed out, User B signs in same browser) wipes A's
// in-memory cache before B's sync merges — closes the P91 leak.
let _wasAuthedForSync = false;
let _lastSyncedEmail = null;
__authListeners.add(() => {
  const nowAuthed = __authState.isAuthed && !!__authState.email;
  const emailChanged = nowAuthed && _lastSyncedEmail && _lastSyncedEmail !== __authState.email;
  if (emailChanged && typeof window._resetUserStoresMint === "function") {
    window._resetUserStoresMint();
  }
  if (nowAuthed && (!_wasAuthedForSync || emailChanged)) {
    _syncProjectsFromServer();
    _syncQueueFromServer();
    _syncNotificationsFromServer();
    _lastSyncedEmail = __authState.email;
  }
  _wasAuthedForSync = nowAuthed;
});
// Run once on script load if we're already authed (persisted from P1).
if (__authState.isAuthed && __authState.email) {
  _wasAuthedForSync = true;
  _lastSyncedEmail = __authState.email;
  _syncProjectsFromServer();
  _syncQueueFromServer();
  _syncNotificationsFromServer();
}

// _streamError: build an Error tagged with a request id (when the
// server returned one) so the caller can include it in user-facing
// copy. Keeps a normal message + a `.reqId` field on the side.
// Lazy-load all the screen scripts that aren't needed for landing.
// Landing page boots from a 152KB critical path (tweaks-panel + shared
// + auth + landing + command-palette + app); the rest (~216KB) only
// streams in once the user navigates away from /. Promise is memoized
// so concurrent navigations don't trigger duplicate loads.
const _LAZY_SCRIPTS = [
  "/dist/dashboard.js",
  "/dist/pricing.js",
  "/dist/templates.js",
  "/dist/operator.js",
  "/dist/builder.js",
  "/dist/preview-templates.js",
  "/dist/publish.js",
  "/dist/modals.js",
  "/dist/account.js",
  "/dist/legal.js",
];
let _lazyLoadPromise = null;
window.loadLazyScreens = function () {
  if (_lazyLoadPromise) return _lazyLoadPromise;
  // P211 — a transient network failure on first lazy-load used to
  // cache a rejected promise here, so the user could never navigate
  // to any non-landing screen until they refreshed. Now: clear the
  // cache on rejection so the next nav attempt retries cleanly.
  _lazyLoadPromise = new Promise((resolve, reject) => {
    let remaining = _LAZY_SCRIPTS.length;
    let failed = false;
    for (const src of _LAZY_SCRIPTS) {
      const s = document.createElement("script");
      s.src = src;
      // async=false on a dynamically-inserted script preserves load
      // order with siblings that also have async=false. Important
      // because some screens reference globals defined by earlier ones.
      s.async = false;
      s.onload = () => { if (--remaining === 0 && !failed) { window.__lazyScriptsLoaded = true; resolve(); } };
      s.onerror = () => { failed = true; reject(new Error(`failed to load ${src}`)); };
      document.head.appendChild(s);
    }
  });
  // Reset the cache on rejection so a retry can mount the scripts
  // again. Successful loads stick (window.__lazyScriptsLoaded gates
  // the re-entry above).
  _lazyLoadPromise.catch(() => { _lazyLoadPromise = null; });
  return _lazyLoadPromise;
};

// P247 — pre-fix this destructure dropped the `kind` argument, so
// every `_streamError(..., { kind: "stream_aborted" })` (P132) and
// `_streamError(..., { kind: "budget_exhausted" })` callsite produced
// an Error WITHOUT a .kind property. P245's duplicate-save guard
// (`streamError?.kind === "stream_aborted"`) was therefore always
// falsy, and budget-exhausted handling that depends on `.kind` would
// have suffered the same fate. Now `kind` flows through too.
function _streamError(message, { reqId, status, kind } = {}) {
  const err = new Error(message || "stream error");
  if (reqId) err.reqId = reqId;
  if (status) err.status = status;
  if (kind) err.kind = kind;
  return err;
}

window.api = {
  async upsertProject(p) {
    const headers = _apiHeaders();
    if (!headers) return { ok: true, offline: true };
    try {
      const r = await fetch("/api/projects", {
        method: "POST",
        headers,
        body: JSON.stringify({
          id: p.id, name: p.name, prompt: p.prompt, swatch: p.swatch,
          ink: p.ink, tag: p.tag, status: p.status, liveUrl: p.liveUrl,
          version: typeof p.version === "string" ? 0 : p.version,
          createdAt: p.createdAt, updatedAt: p.updatedAt, lastOpenedAt: p.lastOpenedAt,
        }),
      });
      // P193 — 401 routes through the centralised expiry handler so a
      // dead-session create attempt prompts re-sign-in instead of
      // bubbling up as a generic "Couldn't create project: status 401"
      // toast. Mirrors the pattern P151–P156 applied to every other
      // mutation helper.
      if (r.status === 401) {
        window.__sessionExpiredMint?.();
        return { ok: false, status: 401, error: "session expired" };
      }
      if (r.status === 402) {
        const j = await r.json().catch(() => ({}));
        return { ok: false, status: 402, error: j.error, plan: j.plan, used: j.used, cap: j.cap, upgradeTo: j.upgradeTo };
      }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        return { ok: false, status: r.status, error: j.error || `status ${r.status}` };
      }
      return { ok: true };
    } catch (e) {
      console.warn("[mint] upsertProject failed:", e.message);
      return { ok: false, error: e.message };
    }
  },
  async deleteProject(id) {
    const headers = _apiHeaders();
    if (!headers) return;
    // P152 — bare .catch() only fires on network errors, so a 401 from
    // a dead session left the optimistic local delete diverged from
    // server (next sync would re-add the "deleted" project, looking
    // like it un-deleted itself). Route 401 through the centralised
    // expiry handler.
    try {
      const r = await fetch(`/api/projects/${encodeURIComponent(id)}`, { method: "DELETE", headers });
      if (r.status === 401) window.__sessionExpiredMint?.();
    } catch (e) {
      console.warn("[mint] deleteProject failed:", e.message);
    }
  },
  async patchProject(id, patch) {
    const headers = _apiHeaders();
    if (!headers) return;
    try {
      const r = await fetch(`/api/projects/${encodeURIComponent(id)}`, {
        method: "PATCH", headers, body: JSON.stringify(patch),
      });
      if (r.status === 401) window.__sessionExpiredMint?.();
    } catch (e) {
      console.warn("[mint] patchProject failed:", e.message);
    }
  },
  async restoreProject(id) {
    const headers = _apiHeaders();
    if (!headers) return { ok: false, offline: true };
    try {
      const r = await fetch(`/api/projects/${encodeURIComponent(id)}/restore`, { method: "POST", headers });
      if (r.status === 401) { window.__sessionExpiredMint?.(); return { ok: false, status: 401 }; }
      if (r.status === 402) {
        // Quota exceeded (P126). Body shape matches the create-quota
        // path: { error, plan, used, cap, upgradeTo }. Surface the
        // exact numbers so the user knows which row to free up — and
        // re-sync the project list so the optimistic restore in the
        // local store doesn't linger as a phantom card.
        const body = await r.json().catch(() => ({}));
        // P451 — non-React caller; tt() for the localized fallback.
        window.toast(body.error || window.tt("proj.restoreOverCap"), { tone: "warn" });
        _syncProjectsFromServer();
        return { ok: false, status: 402, error: body.error, plan: body.plan, used: body.used, cap: body.cap, upgradeTo: body.upgradeTo };
      }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `status ${r.status}`);
      }
      return { ok: true };
    } catch (e) {
      console.warn("[mint] restoreProject failed:", e.message);
      // P430 — non-React caller; use the tt() lookup helper (P401).
      window.toast(window.tt("proj.restoreError"), { tone: "warn" });
      return { ok: false, error: e.message };
    }
  },
  async listTrash() {
    const headers = _apiHeaders();
    if (!headers) return null;
    try {
      const r = await fetch("/api/projects/trash", { headers });
      if (r.status === 401) { window.__sessionExpiredMint?.(); return null; }
      if (!r.ok) return null;
      const j = await r.json();
      return j.projects || [];
    } catch (e) {
      console.warn("[mint] listTrash failed:", e.message);
      return null;
    }
  },
  async purgeProject(id) {
    const headers = _apiHeaders();
    if (!headers) return { ok: false, error: "not signed in" };
    try {
      const r = await fetch(`/api/projects/${encodeURIComponent(id)}/purge`, { method: "POST", headers });
      if (r.status === 401) {
        window.__sessionExpiredMint?.();
        return { ok: false, status: 401, error: "session expired" };
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return { ok: false, status: r.status, error: j.error || `status ${r.status}` };
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  },
  async upsertQueueItem(it) {
    const headers = _apiHeaders();
    if (!headers) return;
    try {
      const r = await fetch("/api/operator/queue", {
        method: "POST",
        headers,
        body: JSON.stringify(it),
      });
      if (r.status === 401) window.__sessionExpiredMint?.();
    } catch (e) {
      console.warn("[mint] upsertQueueItem failed:", e.message);
    }
  },
  async patchQueueItem(id, patch) {
    const headers = _apiHeaders();
    if (!headers) return;
    try {
      const r = await fetch(`/api/operator/queue/${encodeURIComponent(id)}`, {
        method: "PATCH", headers, body: JSON.stringify(patch),
      });
      if (r.status === 401) window.__sessionExpiredMint?.();
    } catch (e) {
      console.warn("[mint] patchQueueItem failed:", e.message);
    }
  },
  async listMessages(projectId) {
    const headers = _apiHeaders();
    if (!headers) return null;
    try {
      const r = await fetch(`/api/projects/${encodeURIComponent(projectId)}/messages`, { headers });
      if (r.status === 401) { window.__sessionExpiredMint?.(); return null; }
      if (r.status === 404) return null; // empty / not yours
      if (!r.ok) throw new Error(`status ${r.status}`);
      const j = await r.json();
      // Normalise back to the in-memory message shape used by builder.jsx.
      return (j.messages || []).map((m) => {
        if (m.kind === "files") return { role: "assistant", kind: "files", files: m.payload?.files || [], createdAt: m.createdAt };
        if (m.kind === "diff")  return { role: "assistant", kind: "diff",  edits: m.payload?.edits || [], createdAt: m.createdAt };
        if (m.kind === "summary") return { role: "assistant", kind: "summary", text: m.content, suggestions: m.payload?.suggestions, errored: m.payload?.errored, createdAt: m.createdAt };
        if (m.kind === "thinking") return { role: "assistant", kind: "thinking", text: m.content, createdAt: m.createdAt };
        return { role: m.role, text: m.content, createdAt: m.createdAt };
      });
    } catch (e) {
      console.warn("[mint] listMessages failed:", e.message);
      return null;
    }
  },
  async listNotifications() {
    const headers = _apiHeaders();
    if (!headers) return null;
    try {
      const r = await fetch("/api/notifications", { headers });
      if (r.status === 401) { window.__sessionExpiredMint?.(); return null; }
      if (!r.ok) return null;
      const j = await r.json();
      const items = (j.items || []).map((n) => ({
        id: n.id,
        kind: n.kind,
        title: n.title,
        body: n.body,
        projectId: n.projectId,
        read: !!n.read,
        at: n.at,
      }));
      // Stash the server-side TRUE unread count so the bell badge
      // doesn't undercount when the user has more unreads than the
      // 50-item page covers.
      items.__unread = typeof j.unread === "number" ? j.unread : items.filter((n) => !n.read).length;
      return items;
    } catch (e) {
      console.warn("[mint] listNotifications failed:", e.message);
      return null;
    }
  },
  async markAllNotificationsRead() {
    const headers = _apiHeaders();
    if (!headers) return;
    try {
      const r = await fetch("/api/notifications/mark-all-read", { method: "POST", headers });
      if (r.status === 401) window.__sessionExpiredMint?.();
    } catch (e) {
      console.warn("[mint] markAllNotificationsRead failed:", e.message);
    }
  },
  async markNotificationRead(id) {
    const headers = _apiHeaders();
    if (!headers) return;
    try {
      const r = await fetch(`/api/notifications/${encodeURIComponent(id)}`, {
        method: "PATCH", headers, body: JSON.stringify({ read: true }),
      });
      if (r.status === 401) window.__sessionExpiredMint?.();
    } catch (e) {
      console.warn("[mint] markNotificationRead failed:", e.message);
    }
  },
  async appendMessage(projectId, msg) {
    const headers = _apiHeaders();
    if (!headers) return;
    // Wire format:
    //   - assistant non-summary kinds (files/diff/thinking): JSON payload
    //   - assistant summary with suggestions: JSON {text, suggestions}
    //   - plain text otherwise
    let content;
    if (msg.kind && msg.kind !== "summary") {
      content = { files: msg.files, edits: msg.edits, suggestions: msg.suggestions };
    } else if (msg.kind === "summary" && msg.suggestions?.length) {
      content = { text: msg.text ?? "", suggestions: msg.suggestions };
    } else {
      content = msg.text ?? "";
    }
    try {
      const r = await fetch(`/api/projects/${encodeURIComponent(projectId)}/messages`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          role: msg.role,
          kind: msg.kind || null,
          content,
          createdAt: msg.createdAt || Date.now(),
        }),
      });
      if (r.status === 401) window.__sessionExpiredMint?.();
    } catch (e) {
      console.warn("[mint] appendMessage failed:", e.message);
    }
  },
  // streamReply: POST a user turn to /chat, parse SSE deltas, drive the
  // caller via callbacks. Returns { ok, suggestions, id, createdAt } once
  // the stream closes; calls onError with an Error decorated with reqId
  // (when the response carried x-request-id) so the caller can show the
  // user a copy-pasteable id for support.
  async streamReply(projectId, text, { onDelta, onDone, onError } = {}) {
    const headers = _apiHeaders();
    if (!headers) {
      onError?.(_streamError("not signed in"));
      return null;
    }
    let r;
    try {
      r = await fetch(`/api/projects/${encodeURIComponent(projectId)}/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({ text }),
      });
    } catch (e) {
      onError?.(_streamError(e?.message || "transport error"));
      return null;
    }
    const reqId = r.headers.get("x-request-id") || null;
    if (r.status === 401) {
      // Cookie is dead server-side — fire the centralized expiry
      // handler (P92) so the user gets a clean prompt instead of a
      // generic "couldn't reach the AI" toast.
      window.__sessionExpiredMint?.();
      onError?.(_streamError("session expired", { reqId, status: 401 }));
      return null;
    }
    if (!r.ok || !r.body) {
      // 429 with structured body = per-plan turn budget exhausted (P105).
      // Surface the cap + reset hint so the user sees something actionable
      // instead of a generic "couldn't reach the AI."
      if (r.status === 429) {
        let body = null;
        try { body = await r.json(); } catch { /* ignore */ }
        if (body?.error === "budget_exhausted") {
          window.refreshTurnBudget?.();           // sync the pill to server truth
          const mins = Math.max(1, Math.round((body.retryAfterSeconds || 0) / 60));
          const planLabel = body.plan === "pro" ? "Pro" : "Free";
          // P398 — localized message via direct TRANSLATIONS lookup
          // (we're inside window.api, not a React component, so
          // useT() isn't available; same pattern as _authT etc).
          const tpl = TRANSLATIONS[__lang]?.["chat.budgetExhausted"]
            ?? TRANSLATIONS.en?.["chat.budgetExhausted"]
            ?? "You've used all {cap} {plan} chat turns for today — resets in about {mins}m.";
          const msg = tpl
            .replace("{cap}", String(body.cap))
            .replace("{plan}", planLabel)
            .replace("{mins}", String(mins));
          onError?.(_streamError(msg, { reqId, status: 429, kind: "budget_exhausted" }));
          return null;
        }
      }
      let detail = "";
      try { detail = (await r.json()).error || ""; } catch { /* ignore */ }
      onError?.(_streamError(detail || `chat ${r.status}`, { reqId, status: r.status }));
      return null;
    }
    const reader = r.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let final = null;
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const block = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          for (const line of block.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const data = line.slice(5).trim();
            if (!data) continue;
            let evt;
            try { evt = JSON.parse(data); } catch { continue; }
            if (evt.type === "delta") onDelta?.(evt.text || "");
            else if (evt.type === "done") final = evt;
          }
        }
      }
    } catch (e) {
      // Mid-stream network drop / abort. Without this catch the throw
      // escapes streamReply uncaught and the caller never sees onError,
      // so the UI is stuck on a half-written assistant turn forever.
      onError?.(_streamError(e?.message || "stream interrupted", { reqId, status: r.status, kind: "stream_aborted" }));
      try { reader.cancel(); } catch { /* tolerate */ }
      return null;
    }
    onDone?.(final);
    // Fire-and-forget: refresh the dashboard turn-budget pill so it
    // reflects the just-consumed turn. Cheap (single SQL row) and
    // decoupled from the SSE stream itself.
    window.refreshTurnBudget?.();
    return final;
  },
};

window.resetMintState = () => {
  if (typeof localStorage === "undefined") return;
  ["auth", "projects", "operatorQueue", "notifications", "lang", "tweaks"]
    .forEach((k) => localStorage.removeItem(_LS_KEY(k)));
  location.reload();
};

window.Toaster = Toaster;
window.ModalShell = ModalShell;
window.UserPill = UserPill;
window.LangSwitcher = LangSwitcher;

// ── MarketingNav — single nav shared across landing / pricing / templates ───
// `active` keys: "home" | "how" | "showcase" | "templates" | "pricing" | "faq"
// (active="home" means the brand is the active spot — no link is highlighted).
function MarketingNav({ active = "home" }) {
  const auth = window.useAuth();
  const { t } = window.useT();
  const scrolled = window.useScrolledPast(40);

  // For hash links (sections that exist on landing only), if we're already
  // on landing the browser handles the scroll natively. From other screens,
  // navigate to landing first; the screen-in animation does the visual.
  const onSection = (hash) => (e) => {
    e.preventDefault();
    if (active === "home" || active === "how" || active === "showcase" || active === "faq") {
      // We're on landing — do native scroll. P173 — honour OS-level
      // prefers-reduced-motion explicitly; scrollIntoView ignores the
      // CSS scroll-behavior override otherwise.
      const el = document.querySelector(hash);
      if (el) {
        const reduce = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
        el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      }
      return;
    }
    window.goTo("landing");
  };

  const links = [
    { id: "how", label: t("nav.how"), kind: "section", hash: "#how" },
    { id: "showcase", label: t("nav.showcase"), kind: "section", hash: "#showcase" },
    { id: "templates", label: t("nav.templates"), kind: "screen", screen: "templates" },
    { id: "pricing", label: t("nav.pricing"), kind: "screen", screen: "pricing" },
    { id: "faq", label: t("nav.faq"), kind: "section", hash: "#faq" },
  ];

  return (
    <nav
      aria-label={t("nav.mainAria")}
      style={{
        ...navStyles.bar,
        boxShadow: scrolled ? "0 8px 24px -16px rgba(0,0,0,0.18)" : "none",
        transition: "box-shadow 0.2s ease",
      }}
    >
      <div
        style={{
          ...navStyles.inner,
          padding: scrolled ? "10px var(--mkt-pad)" : "16px var(--mkt-pad)",
          transition: "padding 0.2s ease",
        }}
      >
        <button type="button" style={navStyles.brand} onClick={() => window.goTo("landing")}>
          <span data-brand-mark style={navStyles.brandMark} aria-hidden="true">◆</span>
          <span style={navStyles.brandWord}>Forge</span>
        </button>
        <div style={navStyles.links} data-mobile-hide>
          {links.map((l) => {
            const isActive = active === l.id;
            const baseStyle = {
              ...navStyles.link,
              ...(isActive ? navStyles.linkActive : {}),
            };
            const onClick = l.kind === "section"
              ? onSection(l.hash)
              : () => window.goTo(l.screen);
            return (
              <button
                type="button"
                key={l.id}
                style={baseStyle}
                {...(isActive ? {} : { "data-link": true })}
                onClick={onClick}
                aria-current={isActive && l.kind !== "section" ? "page" : undefined}
              >
                {l.label}
              </button>
            );
          })}
        </div>
        <div style={navStyles.actions}>
          <LangSwitcher />
          {auth.isAuthed ? (
            <>
              <UserPill onDashboard={() => window.goTo("dashboard")} />
              <button type="button" style={navStyles.btnSolid} onClick={() => window.goTo("dashboard")}>
                {t("nav.dashboard").replace(/\s*→\s*$/, "")} <span data-arrow aria-hidden="true">→</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" style={navStyles.btnGhost} onClick={() => window.openAuth("sign-in")}>
                {t("nav.signIn")}
              </button>
              <button type="button" style={navStyles.btnSolid} onClick={() => window.openAuth("sign-up")}>
                {t("nav.startFree")}
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

const navStyles = {
  bar: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "color-mix(in oklab, var(--paper) 92%, transparent)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderBottom: "1px solid var(--rule)",
  },
  inner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
  },
  brand: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontFamily: "var(--font-display)",
    fontSize: 22,
    color: "var(--ink)",
    background: "transparent",
    padding: 0,
  },
  brandMark: { color: "var(--accent)", fontSize: 14 },
  brandWord: { letterSpacing: "-0.01em" },
  links: { display: "flex", gap: 28, fontSize: 14 },
  link: {
    color: "var(--ink-2)",
    background: "transparent",
    padding: 0,
    fontFamily: "inherit",
    fontSize: 14,
    cursor: "pointer",
  },
  linkActive: {
    color: "var(--ink)",
    fontWeight: 500,
  },
  actions: { display: "flex", gap: 8, alignItems: "center" },
  btnGhost: {
    padding: "8px 14px",
    borderRadius: 999,
    fontSize: 14,
    color: "var(--ink-2)",
  },
  btnSolid: {
    padding: "8px 16px",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 500,
    background: "var(--ink)",
    color: "var(--paper)",
  },
};

window.MarketingNav = MarketingNav;

// ── Icon — single source of truth for line glyphs ───────────────────────────
// Stroke-only, currentColor, 1.6px stroke, 16px viewBox. Pass `size` to
// scale; pass `style` to override color/spacing.
function Icon({ name, size = 16, style }) {
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    style,
  };
  switch (name) {
    case "paperclip":
      return <svg {...props}><path d="M11 5 L6.5 9.5 a1.8 1.8 0 0 0 2.5 2.5 L13 7.5 a3.3 3.3 0 0 0-4.6-4.6 L4 7.4 a4.7 4.7 0 0 0 6.6 6.6 L14 10.6" /></svg>;
    case "image":
      return <svg {...props}><rect x="2" y="3" width="12" height="10" rx="1.5" /><circle cx="6" cy="6.5" r="1" /><path d="M2.5 12 L6 8.5 L9 11.5 L11.5 9 L13.5 11" /></svg>;
    case "at":
      return <svg {...props}><circle cx="8" cy="8" r="2.4" /><path d="M10.4 8 V9.4 a1.6 1.6 0 0 0 3.2 0 V8 a5.6 5.6 0 1 0-2.4 4.6" /></svg>;
    case "refresh":
      return <svg {...props}><path d="M13.5 7 a5.5 5.5 0 1 0 0.7 4.5" /><path d="M13.5 3 V7 H9.5" /></svg>;
    case "open-new":
      return <svg {...props}><path d="M7 3 H3 V13 H13 V9" /><path d="M9.5 2.5 H13.5 V6.5" /><path d="M13.5 2.5 L8 8" /></svg>;
    case "search":
      return <svg {...props}><circle cx="7.2" cy="7.2" r="4" /><path d="M10.2 10.2 L13.5 13.5" /></svg>;
    case "kebab":
      return <svg {...props} stroke="none" fill="currentColor"><circle cx="8" cy="3.5" r="1.2" /><circle cx="8" cy="8" r="1.2" /><circle cx="8" cy="12.5" r="1.2" /></svg>;
    case "x":
      return <svg {...props}><path d="M3.5 3.5 L12.5 12.5 M12.5 3.5 L3.5 12.5" /></svg>;
    case "check":
      return <svg {...props}><path d="M3.5 8.5 L6.5 11.5 L12.5 4.5" /></svg>;
    case "envelope":
      return <svg {...props}><rect x="2" y="4" width="12" height="9" rx="1" /><path d="M2.5 4.5 L8 9 L13.5 4.5" /></svg>;
    case "alert":
      return <svg {...props}><circle cx="8" cy="8" r="6" /><path d="M8 5 V8.5" /><path d="M8 10.5 V11" /></svg>;
    case "grid":
      return <svg {...props}><rect x="2.5" y="2.5" width="4.5" height="4.5" rx="0.5" /><rect x="9" y="2.5" width="4.5" height="4.5" rx="0.5" /><rect x="2.5" y="9" width="4.5" height="4.5" rx="0.5" /><rect x="9" y="9" width="4.5" height="4.5" rx="0.5" /></svg>;
    case "live":
      return <svg {...props} stroke="none" fill="currentColor"><circle cx="8" cy="8" r="3" /></svg>;
    case "dollar":
      return <svg {...props}><path d="M8 2.5 V13.5" /><path d="M11 5 H6.5 a1.5 1.5 0 0 0 0 3 H9.5 a1.5 1.5 0 0 1 0 3 H4.5" /></svg>;
    case "pencil":
      return <svg {...props}><path d="M11.5 2.5 L13.5 4.5 L5 13 H3 V11 Z" /></svg>;
    case "branch":
      return <svg {...props}><circle cx="4" cy="3.5" r="1.2" /><circle cx="4" cy="12.5" r="1.2" /><circle cx="12" cy="6" r="1.2" /><path d="M4 4.7 V11.3" /><path d="M4 8 a4 4 0 0 0 4-4 H10.8" /></svg>;
    case "phone":
      return <svg {...props}><path d="M3.5 3 H6 L7.2 6 L5.6 7.2 a8 8 0 0 0 3.2 3.2 L10 8.8 L13 10 V12.5 a1 1 0 0 1-1.1 1 A11 11 0 0 1 2.5 4.1 a1 1 0 0 1 1-1.1 Z" /></svg>;
    default:
      return null;
  }
}
window.Icon = Icon;
window.NoProjectsArt = NoProjectsArt;
window.NoMatchArt = NoMatchArt;
window.AllClearArt = AllClearArt;
window.NotificationsBell = NotificationsBell;

// ── ScrollProgress — thin accent bar pinned to top of long pages ────────────
function ScrollProgress() {
  const [pct, setPct] = useStateS(0);
  useEffectS(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      // P584 — Pre-fix `(scrollY / max) * 100` returned a float; on
      // a 5000px-tall page each 1px of scroll changed pct by 0.02%
      // and React's setState bailout (which compares with ===)
      // didn't catch sub-integer diffs, so every scroll-pixel
      // re-rendered the progress bar (which then transitioned to a
      // visually identical width). Rounding to integer keeps the
      // visual identical while letting React skip 99% of the
      // re-renders during smooth scrolling. The 80ms width
      // transition smooths the integer jumps so the bar still looks
      // continuous.
      setPct(max > 0 ? Math.round(Math.min(100, (window.scrollY / max) * 100)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        height: 2,
        width: `${pct}%`,
        background: "var(--accent)",
        zIndex: 100,
        transition: "width 80ms linear",
        pointerEvents: "none",
      }}
    />
  );
}
window.ScrollProgress = ScrollProgress;

// Returns true once the user has scrolled past `threshold` px. Cheap
// hook — drops a single passive listener and reuses across screens.
window.useScrolledPast = function useScrolledPast(threshold = 40) {
  const [past, setPast] = useStateS(false);
  useEffectS(() => {
    const onScroll = () => setPast(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return past;
};
